// LR1 — THE LIVING ROOM, AS LAW.
//
// **THE ONE IMPORT THIS FILE CARRIES, AND WHY IT COSTS NOTHING (T141-1):** `src/houseSeat.js`
// imports NOTHING itself, so this file is still loadable by a `.mjs` gate with no bundler and
// R146's reach is unchanged. It is imported rather than re-spelled because *who the house is* is
// read by the identity, the block rule, the message gate, the session doors and the migration —
// five spellings would be five answer keys on the one seat a member cannot tell from a person.
import { SUPPORT_SEAT_NAME, houseReach } from "./houseSeat.js";
//
// The community room: a feed of member posts, threads, comments, likes, member reporting, and
// member-to-member inboxes. Everything about WHO MAY SEE WHAT, WHAT A MEMBER IS CALLED, WHAT MAY BE
// POSTED and WHAT HAPPENS WHEN IT MAY NOT lives HERE, in one file, imported by BOTH the server and
// the client — because this is the first surface on this site where one member's words reach
// another member's eyes, and a law with two copies is a law with two answer keys.
//
// THE OWNER'S RULINGS THIS FILE ENCODES, each one load-bearing:
//
//   1. TIER: **EVERY PAID MEMBER — NEWBIE AND UP (her word, L2.7, 2026-08-05, replacing APPRENTICE
//      of 2026-07-31).** The room, the INBOX/MESSAGES and NOTIFICATIONS all cost `room:living` in
//      server/tiers.js and NOWHERE else, so her ruling was one line in that file, exactly like every
//      other room's move. **AND THE SECOND HALF OF THAT RULING IS A LAW HERE:** below the tier — and
//      that now means a FREE account only — a tap raises her box. It NEVER does nothing.
//      `communityDoorAnswer` is that law and it has no silent branch (see it below).
//
//   2. IDENTITY: **THE ONLY NAME SHOWN ANYWHERE IS THE MEMBER'S ROOM FIRST-NAME.** No surname, no
//      email, no link to her room, her chart, or any other member surface. `publicIdentity` below is
//      the ONLY function that turns a member row into something renderable, and it is deliberately
//      lossy: it cannot return a field it was never given, so a careless `...member` spread at a call
//      site cannot leak a surname into a feed. The server builds identity with this and sends the
//      RESULT; the raw row never crosses the wire.
//
//   3. SHADOW-HIDE: failing content is **POSTED TO ITS AUTHOR ONLY, invisible to everyone else, with
//      NO REJECTION MESSAGE.** The author's own view is byte-identical to a clean post. This is why
//      visibility is a per-VIEWER question (`canSee`) and never a column the client is trusted to
//      read: the moment the client knows an item is hidden, the ruling is broken.
//
//   4. BLOCKING IS ABSOLUTE. A blocked member cannot message the blocker and cannot see the
//      blocker's posts' comment box. It cuts BOTH directions regardless of who blocked whom, because
//      a block that only works one way is a block that hands the blocker's words to the blocked.
//
//   5. THE FAMILY-GATE CLASS EXTENDS TO COMMUNITY CONTENT, and Conjure shipping/order complaints are
//      BANNED OUTRIGHT. Those are two of the eight reason codes below.
//
// This module imports NOTHING (the entry requirement for `scripts/lr1-living-room.mjs` to LOAD and
// RUN it rather than read it as text).

// ---------------------------------------------------------------------------
// THE FEATURE KEY. One string, and it is the only thing that knows this room costs money.
// ---------------------------------------------------------------------------
export const LIVING_ROOM_FEATURE = "room:living";

// ---------------------------------------------------------------------------
// L2.7 — **WHAT A TAP ON A COMMUNITY DOOR DOES, AND THERE IS NO ANSWER THAT IS "NOTHING".**
//
// HER LIVE FINDING, as a paid Newbie: the mail icon did nothing and the bell did nothing. No panel,
// no refusal, no box — dead clicks on two controls that are on every page of this site.
//
// **THE CAUSE WAS A NO-OP HANDED TO A GUARD THAT RUNS IT.** All three community doors (the header
// pair, the inbox screen, the room itself) answered a remembered 402 with
// `onRequireMembership(() => {})`, and `requireMembership` reads *"is she a member"* — so for a
// member below the room's tier it did exactly what it was told: it called an empty function. Free
// visitors saw the box (they are not members), which is why every capture of this looked right.
//
// So the decision is a LAW rather than a line at three call sites, and it is written as the
// property her ruling actually asks for: **`kind` is always one of `COMMUNITY_DOOR_ANSWERS`, and
// neither of them is silence.** A gate DRIVES it over the whole cross-product; there is no input
// for which this function can answer "do nothing", which is a thing a check can prove and a
// comment cannot.
//
// The LOCK is the server's own 402 payload wherever the caller has one (T8-2: *the client never
// decides this — it is told*), so the box names the door she touched and the tier that opens it. A
// caller that only remembers THAT it was refused still gets a box, about this room, by feature.
// ---------------------------------------------------------------------------
export const COMMUNITY_DOOR_ANSWERS = ["open", "box"];

export function communityDoorAnswer({ isMember = false, lock = null } = {}) {
  if (lock && typeof lock === "object") return { kind: "box", lock };
  if (lock) return { kind: "box", lock: { feature: LIVING_ROOM_FEATURE } };
  if (!isMember) return { kind: "box", lock: { feature: LIVING_ROOM_FEATURE } };
  return { kind: "open" };
}

// ---------------------------------------------------------------------------
// IDENTITY — RULING 2. THE NARROWEST FUNCTION IN THE FILE, ON PURPOSE.
// ---------------------------------------------------------------------------

// The room first-name, cleaned. A member's room name is her own; it is never re-capitalized or
// spell-corrected (the N7-4 lesson: do not reformat a member's own words). Only the FIRST token
// survives, so a room name of "Alexandra Reyes" shows as "Alexandra" and the surname never exists
// on this surface in any form.
export const MAX_ROOM_NAME = 24;
export function roomFirstName(name) {
  const first = String(name ?? "").replace(/[\r\n\t]+/g, " ").trim().split(/\s+/)[0] || "";
  return first.slice(0, MAX_ROOM_NAME);
}

// LR1-12 ROUND FOUR — **THE IDENTITY SOURCE, AND THE BUG THIS FIXES WAS LIVE AND HERS.**
//
// Her room says "Lala's room". The Living Room showed her as **@Lashonda**, because the resolver
// reached for `profiles.first_name` and never looked at the name she had actually chosen. The room
// name is the name she picked for herself in this house; her legal first name is a field she filled
// in at signup for her CHART. Those are not the same thing, and this room must use the first.
//
// THE ORDER, and every step is deliberate:
//   1. an explicit Living Room name, if she ever sets one here;
//   2. **HER MY ROOM NAME** ("Lala's room" -> @Lala) — the fix;
//   3. her first name, ONLY until she has named her room (her stated edge case, and the remedy is
//      in her own hands: naming the room changes it);
//   4. the account name, as a last resort.
//
// RESOLVED AT DISPLAY TIME, NEVER STORED. Her ruling says a rename must update everywhere, and the
// only way that is true of a post written last month is if nothing ever copied the name into it.
export const ROOM_TITLE_SUFFIX = /['’]s\s+room\s*$/i;
export function nameFromRoomTitle(title) {
  const clean = String(title ?? "").replace(/[\r\n\t]+/g, " ").trim();
  if (!clean) return "";
  // "Lala's room" -> "Lala". A room she named something else entirely ("The Quiet House") keeps its
  // first word, which is the same rule every other name on this surface follows.
  return roomFirstName(clean.replace(ROOM_TITLE_SUFFIX, "").trim());
}

// ---------------------------------------------------------------------------
// LR1-12 ROUND EIGHT — **ROOM NAMES ARE UNIQUE SITE-WIDE, CASE-INSENSITIVELY.** Her law.
//
// "Lala" = "lala" = "LALA". The reason is the @ system: a tag resolves to a PERSON, and two members
// answering to the same @name makes every tag in the room ambiguous — including tags already
// written and notifications already delivered.
//
// **ENFORCED WHERE THE NAME IS SET**, which is the My Room name editor and the Living Room's own
// name route, and **enforced on the SERVER**: the check lives in the write path, so a client that
// lies about availability changes nothing. The handle below is what the database holds unique.
// ---------------------------------------------------------------------------

// The comparable form of a name. Everything about uniqueness is decided on THIS string, so the
// question "is this taken" has exactly one answer however the name was typed.
export function roomHandle(name) {
  return roomFirstName(name).trim().toLowerCase();
}

// HERS, AND RESERVED. These resolve to her accounts only; anybody else choosing one is told it is
// taken, in the same warm sentence as any other collision — a member who tries "Lala" does not need
// a lecture about why.
export const RESERVED_HANDLES = new Set([
  "lala", "lalainutiahari", "inutiahari", "12thhouse", "twelfthhouse",
  "theconjure", "conjure", "admin", "owner", "support", "house",
]);
export function isReservedHandle(name) {
  return RESERVED_HANDLES.has(roomHandle(name));
}

// A name has to be a name before it can be unique. Shape only; the taken-check is a database read.
export const MIN_ROOM_NAME = 2;
export function roomNameShapeViolations(name) {
  const clean = roomFirstName(name);
  const out = [];
  if (!clean) out.push("Pick a name for the room to know you by.");
  else if (clean.length < MIN_ROOM_NAME) out.push("That is a little short. Two letters or more.");
  if (clean && !/^[A-Za-z][A-Za-z0-9'’_-]*$/.test(clean)) {
    out.push("Letters, numbers, and the odd dash or apostrophe. It has to work as a tag.");
  }
  return out;
}

// THE SENTENCE A MEMBER READS when the name is gone. In her register: plain, warm, no machinery,
// and it never says WHO holds it — that would be a directory lookup wearing an error message.
export function nameTakenMessage(name) {
  return `${roomFirstName(name)} is taken. Pick another and it is yours.`;
}
export const NAME_FREE_MESSAGE = "That one is free.";

// ---------------------------------------------------------------------------
// R202b — **THE DOOR ROUND EIGHT'S OWN COMMENT NAMES, WHICH WAS ENFORCING NOTHING.**
//
// Round eight is written thirty lines above: *"ENFORCED WHERE THE NAME IS SET, which is the MY ROOM
// NAME EDITOR and the Living Room's own name route, and enforced on the SERVER: the check lives in
// the write path, so a client that lies about availability changes nothing."* Measured (R180's
// method, and R202's census is what pointed at it): **the check lived in NEITHER of the two doors it
// names.** `/api/living-room/name` really does claim the handle, run the shape rules and consult the
// reserved list — and is called by no surface on this site. **The My Room editor writes the title
// through `/api/room/state`, which validates the THEME and the LANGUAGE and nothing else**, and
// `room_state->>'roomName'` is the SECOND source in every identity COALESCE in the Living Room,
// above `first_name` and above the account name.
//
// So a member who called her room *"Lala's room"* was rendered **@Lala** in the community room —
// this house's own reserved handle, past a list that was never consulted on the one path anybody
// uses. That is not a uniqueness inconvenience, it is an identity claim on somebody else's name.
//
// **WHAT IS REFUSED IS THE RESERVED HANDLE AND NOTHING ELSE, AND THE NARROWNESS IS THE RULING.**
// A My Room title is FREE-FORM — *"The Quiet House"* is a legal title whose displayed name is *"The"*
// — so putting `roomNameShapeViolations` on this door would refuse titles members already have, which
// is R19's forbidden direction on a save. **And UNIQUENESS is deliberately NOT closed here**: it is a
// database read whose answer today is always *free*, because nothing claims a handle at all, and the
// claim path is `/api/living-room/name`, whose wiring is a surface decision and hers (R202-1). Named
// rather than implied (R17/R160), so nobody reads this guard as the whole of round eight.
//
// The sentence is `nameTakenMessage`, already declared — its own comment says a member who tries
// "Lala" does not need a lecture — so there is no second refusal for one refusal (R28/R29, ITEM 9's
// `MEMBER_NOT_HERE`). An EMPTY title is legal and is the revert to the default.
// ---------------------------------------------------------------------------
export function reservedRoomTitleRefusal(title, viewerEmail = "") {
  const shown = nameFromRoomTitle(title);
  if (!shown) return "";
  if (!isReservedHandle(shown)) return "";
  // **HER OWN SEAT HOLDS THE RESERVED NAMES.** `roomNameVerdict` below has always said this — *"Her
  // own accounts hold the reserved names; for anybody else they read as taken"* — and this door,
  // added later, never asked. The result was that the guard protecting her name refused HER: she
  // typed Lala, the save was refused, nothing was stored, and every surface fell through to her
  // LEGAL name. Identified by ADDRESS, the same way her owner seat is identified everywhere else.
  return RESERVED_NAME_HOLDERS.has(String(viewerEmail || "").trim().toLowerCase())
    ? ""
    : nameTakenMessage(shown);
}

/**
 * **WHOSE NAMES THE RESERVATION IS FOR.** Declared beside the reservation itself so the two can
 * never drift: a handle on `RESERVED_HANDLES` with nobody on this list is a name NOBODY can use,
 * including her, which is exactly the state this round found.
 */
export const RESERVED_NAME_HOLDERS = new Set([
  "lala@ivertical.tv",
  "conjuredhoodoo@gmail.com",
]);

// The whole decision, given the shape check and what the database found. One function, so the
// editor's live check and the server's write check cannot disagree about the answer.
export function roomNameVerdict({ name, takenBy = null, viewerId = null } = {}) {
  const shape = roomNameShapeViolations(name);
  if (shape.length) return { ok: false, message: shape[0] };
  // Her own accounts hold the reserved names; for anybody else they read as taken, like any other.
  if (takenBy && String(takenBy) === String(viewerId)) return { ok: true, message: NAME_FREE_MESSAGE };
  if (takenBy) return { ok: false, message: nameTakenMessage(name) };
  return { ok: true, message: NAME_FREE_MESSAGE };
}

// THE ONE RESOLVER. Every surface in this room — posts, comments, DMs, threads, the @ picker,
// notifications, shared-spell credits — gets its name from here and nowhere else.
export function resolveRoomName({ livingRoomName = "", roomTitle = "", firstName = "", accountName = "" } = {}) {
  return (
    roomFirstName(livingRoomName)
    || nameFromRoomTitle(roomTitle)
    || roomFirstName(firstName)
    || roomFirstName(accountName)
    || ""
  );
}

// The house avatar set. House-provided (her ruling: no uploaded avatars — an uploaded avatar is an
// unmoderated image on every post that member ever makes, and a face is an identity leak the room
// name law exists to prevent). Assigned deterministically from the member id so it is stable
// forever without a column, and so two sessions of the same member always agree.
export const AVATARS = ["moon", "sun", "star", "flame", "leaf", "key", "eye", "shell", "crown", "vessel", "thread", "bell"];
export function avatarFor(userId) {
  const s = String(userId ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATARS[h % AVATARS.length];
}

// THE ONLY renderable shape of a member on this surface. Note what it CANNOT carry: there is no
// email field, no surname field, no room link, no tier, no id-as-handle.
//
// LR1-12 ROUND EIGHT — **THE BADGE IS GONE.** The gold "LALA" tag on names is removed entirely:
// @name stands alone, for everyone. `owner` STAYS, because it is what her seat's permissions read
// (delete anything, see the queue) — it is a boolean, not a decoration, and it never renders.
// If she wants an owner marker back it returns by her word, redesigned.
export function publicIdentity({ userId, roomName, owner = false, crowned = false, photoVersion = null, following = false, house = false } = {}) {
  // >>> **T141-1 — THE HOUSE'S OWN SEAT RENDERS ITS DECLARED NAME AND NOT A FIRST TOKEN.** <<<
  //
  // Measured before a line was written: `roomFirstName` takes the FIRST WORD, so a seat named
  // *12th House Support* renders **@12th**. Ruling 3 takes the first token precisely so a SURNAME
  // can never reach a screen — and this seat has no surname to protect, because
  // `SUPPORT_SEAT_NAME` is a CONSTANT IN SOURCE and this branch reads no row value at all.
  // **Every real member is untouched, and no member-supplied string can reach a full-name render.**
  const isHouse = Boolean(house);
  return {
    ref: String(userId ?? ""),      // opaque handle for like/report/block plumbing; never displayed
    name: isHouse ? SUPPORT_SEAT_NAME : (roomFirstName(roomName) || "A member"),
    avatar: avatarFor(userId),
    owner: Boolean(owner),
    // S-2 — **THE CROWN IS DISPLAY; `owner` IS POWER, AND THEY ARE NOW TWO FACTS.** ITEM 11 derived
    // the crown FROM `owner` on purpose, so that a member calling herself Lala could never earn one
    // (room names are not unique here). Her ruling splits them: her three daughters wear the crown
    // and NONE of them gains a permission by wearing it. So the crown reads THIS field and never
    // `owner` again — and because the two rosters are different sets, a check that cannot tell them
    // apart is a check that would pass on the old code (the fixture asserts they DIFFER).
    // Like `owner`, it is a boolean with no field a surname or an email could ride in.
    crowned: Boolean(crowned),
    // ITEM 4(c) — HER OWN FACE, WHEN SHE HAS PUT ONE THERE. Still no field a surname or an email
    // could ride in: the URL is built from the ref that is already in this object and a version
    // number, and NOTHING here is a caller-supplied string. `null` is the house glyph above, which
    // is the default and the fallback and the thing a hidden photo goes back to.
    photo: photoVersion ? avatarPhotoUrl(userId, photoVersion) : null,
    // ITEM 7 — **DOES THE VIEWER FOLLOW THIS PERSON.** It rides on the identity for the same reason
    // the photo does: every surface in this room renders a member through one component, so a fact
    // carried here arrives everywhere by existing rather than by a sweep. Still nothing a surname or
    // an email could ride in — it is a boolean about the VIEWER, not a field about the member.
    following: Boolean(following),
    // T141-1 — **THE HOUSE SEAT, AS A FACT ABOUT THE ROW AND NOT A POWER.** It is here beside
    // `owner` and `crowned` for the same reason they are: every surface in this room renders a
    // member through one component, so a fact carried here arrives everywhere by EXISTING. It
    // confers no dashboard, no refund door and no delete-anything — `houseReach` is the only law
    // that reads it, and it reads it for exactly the two reaches the desk cannot work without.
    house: isHouse,
  };
}

// ---------------------------------------------------------------------------
// ITEM 4(c) — THE PROFILE PHOTO. **ONE DECLARATION OF WHERE A FACE LIVES.**
//
// The URL is a law rather than a string typed at a call site (R8's attachments.ts, R24's eighteen
// literals): the server composes it inside `publicIdentity` and the client only ever renders what
// it was handed, so there is no second spelling of this route anywhere and a change to it cannot
// leave one surface pointing at nothing.
//
// **THE VERSION IS IN THE URL AND IT IS LOAD-BEARING.** The bytes are served under the member's ref,
// which never changes, so a replaced photo behind an unversioned URL is her OLD face in every
// browser and every service-worker cache that already has it. The version bumps on every upload.
// ---------------------------------------------------------------------------
export const AVATAR_PHOTO_ROUTE = "/api/living-room/avatar";
export function avatarPhotoUrl(ref, version) {
  const id = encodeURIComponent(String(ref ?? ""));
  const v = encodeURIComponent(String(version ?? ""));
  return `${AVATAR_PHOTO_ROUTE}/${id}?v=${v}`;
}

// WHAT A PROFILE PHOTO MAY BE. Square by construction (the crop happens before the upload, in
// `src/photoUpload.js`), small on purpose — it is drawn at 40px and 28px, so 512 is already generous
// for a retina screen and anything past it is bytes on somebody's phone plan for no visible pixel.
export const AVATAR_PHOTO_EDGE = 512;
export const AVATAR_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
// **HEIC IS WHAT AN iPHONE ACTUALLY SENDS**, and it was missing — so a member choosing a photo
// from her camera roll was refused a file she had every right to use. The browser cannot decode
// HEIC on a canvas, so those arrive as RAW BYTES and the SERVER converts them (sharp reads HEIF
// here — measured, not assumed). Everything else is still cropped before it leaves the device.
export const AVATAR_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

/** What the browser can crop for us. Anything else travels whole and is converted server-side. */
export const CLIENT_CROPPABLE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const canCropInBrowser = (contentType) =>
  CLIENT_CROPPABLE_TYPES.includes(String(contentType || "").toLowerCase());

// **THE BROWSER DOES NOT ALWAYS KNOW WHAT AN iPHONE PHOTO IS, AND AN UNKNOWN IS NOT A REFUSAL.**
// Measured through a real picker: Chrome hands back `file.type === ""` for a `.HEIC` off a phone,
// because the type comes from the OS registry and Windows has no entry for it. An allow-list read
// straight off `file.type` therefore refuses the file with *"that is not a picture I can read"* —
// about a photograph — which is R18's collapse (*no type stated* and *a type we refuse* are
// different facts) landing as R19's forbidden direction (a guard refusing correct input).
//
// So the NAME answers when the browser will not. This is a promise either way, never a verdict:
// R15's law stands unchanged — the picker promises and **the server's sniffer decides** — so the
// worst a wrong extension buys is an honest refusal from the route that read the actual bytes.
const PHOTO_TYPE_BY_EXTENSION = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp",
  heic: "image/heic", heif: "image/heif",
};

// **WHAT SHE IS TOLD WHEN THE BROWSER CANNOT OPEN HER PHOTO.** Warm, plain, and it names the way
// out rather than the machinery — her honesty law: a failed upload SAYS it failed, never a broken
// icon over a dead submit. It is DECLARED here because two surfaces say it and a third will.
export const UNREADABLE_PICTURE =
  "This browser could not open that photo. From your phone it will work — or save it as a JPEG and try again.";

/**
 * What this file most likely is: the browser's word if it is one we take, otherwise the extension.
 *
 * @returns {string} a rostered content type, or "" when neither signal names one.
 */
export function photoTypeFor(fileName = "", declaredType = "") {
  const declared = String(declaredType || "").toLowerCase();
  if (AVATAR_PHOTO_TYPES.includes(declared)) return declared;
  const dot = String(fileName || "").lastIndexOf(".");
  if (dot < 0) return "";
  return PHOTO_TYPE_BY_EXTENSION[String(fileName).slice(dot + 1).toLowerCase()] || "";
}

// A refusal, in the shape the room already uses (`attachmentRefusal`): honest, immediate, and never
// a hide. A photo that is too big or is not a photograph is not a rule she broke.
// **`maxBytes` IS A PARAMETER BECAUSE THE TWO PICTURES HAVE DIFFERENT LIMITS AND ONE LAW.** A group
// picture rides the post-picture cap by her ruling and a profile photo keeps its own; the question
// *why is this upload refused* stays a single function, and the number it measures against is the
// caller's (R31). It was hard-wired here, so the group's own 8MB clause passed an iPhone photo and
// this one refused it two lines later, printing a limit the group does not have.
export function avatarRefusal({ bytes = 0, contentType = "", width = 0, height = 0, maxBytes = AVATAR_PHOTO_MAX_BYTES } = {}) {
  if (!bytes) return "Choose a picture first.";
  if (!AVATAR_PHOTO_TYPES.includes(String(contentType || "").toLowerCase())) {
    // The sentence NAMES the camera roll first, because that is where a member's picture actually
    // comes from and "a JPEG, a PNG or a WebP" reads as a refusal of the photo she just took.
    return "That file is not a picture I can read. A photo from your camera roll, a JPEG, a PNG or a WebP all work.";
  }
  if (bytes > maxBytes) {
    return `That picture is ${Math.round(bytes / (1024 * 1024))}MB. A profile photo goes up to ${Math.round(maxBytes / (1024 * 1024))}MB.`;
  }
  // The crop is the client's job and the server checks its homework — the S2 law, one room over: a
  // gate that trusts the caller's word about the shape of what it sent is not a gate.
  if (width && height && width !== height) return "A profile photo is square. Crop it and try again.";
  return "";
}

// THE COPY, in one place so the control, the pointer and the refusal cannot drift into three
// different accounts of the same rule.
export const AVATAR_UPLOAD_LABEL = "Add a photo";
export const AVATAR_REPLACE_LABEL = "Change photo";
export const AVATAR_REMOVE_LABEL = "Use the house glyph";
// R72 — THE THIRD LABEL, AND IT EXISTS BECAUSE THE OTHER TWO ARE BOTH CLAIMS. "Add a photo" says she
// has none and "Change photo" says she has one, so **before the read lands there is no honest word
// between them** — and the control used to say the first one, which is how a member with a photo was
// invited to add the thing she already added. This one claims nothing and is what she reads while
// the answer is still coming and after one that never did.
export const AVATAR_CHOOSE_LABEL = "Choose a photo";
export const AVATAR_SAVING = "Saving your photo...";
export const AVATAR_ALT = "Your profile photo";
export const AVATAR_HELP =
  "Your photo rides with your name everywhere in the Living Room. Square, and it is cropped for you.";
export const AVATAR_POINTER = "Add a photo. It rides with your name in the Living Room.";

// R72 — THE THREE SENTENCES THIS CONTROL SAYS WHEN SOMETHING GOES WRONG, DECLARED HERE FOR THE
// REASON 14-B4 ALREADY WROTE DOWN. The `photo.` prefix is ENGLISH_BY_DESIGN and its stated reason is
// that *every word this control says comes from `src/livingRoom.js` or from `avatarRefusal`* — and
// that reason was FALSE: `AVATAR_SAVE_FAILED`'s exact sentence was hand-typed TWICE in the component
// and a THIRD time in the route's own 503, which is R47's count-the-copies with a declaration
// resting on it. It is one string now, imported by both sides.
//
// **`AVATAR_LOAD_FAILED` IS THE ONE THAT DID NOT EXIST AT ALL, AND IT IS THE ROUND'S DEFECT.** A read
// that never landed left the control drawing the house glyph over a member who HAS a photo, with the
// Remove button gone and the Add button offering to add the thing she already added (W5-2/R39's
// third state, destroyed the way R59's saved shelf destroyed it).
// The name is the ROOM's, not the photo's, and that is the point: this exact sentence was typed
// FIVE times — twice in the control, once in the avatar 503, and once in each of the two post
// handlers' own 503s. One act, one account of it failing (R47's count-the-copies).
export const SAVE_FAILED = "That did not save. Try again.";
export const AVATAR_LOAD_FAILED = "Your photo could not be loaded just now. It has not been changed.";
// **THE LAST-RESORT SENTENCE, AND IT IS A LEAK BEING CLOSED RATHER THAN A COURTESY.** An unhandled
// throw in any Living Room route fell through to index.js's last-resort handler, which answers
// `500 { message: error.message }` — the RAW DATABASE ERROR, with no `ok` on it — and this control
// prints `d.message` straight onto her screen (R65 found the same handler doing the same thing to
// the owner's dashboard). The room answers in its own words now, above all 29 routes.
export const ROOM_UNAVAILABLE = "The room could not do that just now. Try again in a moment.";

// ---------------------------------------------------------------------------
// D-4 — **HER RULING: A MESSAGE MUST NOT PROMISE THAT RETRYING HELPS.**
//
// `ROOM_UNAVAILABLE` ends *"Try again in a moment."* and the DISPATCHER'S CATCH was handing it to
// her for a THROWN HANDLER — a handler that threw on her delete will throw on the next one too, so
// the room was inviting her to re-tap a control that cannot work and then blaming the moment. She
// tapped delete four times and read the same encouraging sentence four times.
//
// **THE FIX IS A SECOND SENTENCE, NOT AN EDIT TO THE SHARED ONE, AND THAT IS HER WORD RATHER THAN
// a preference of ours.** `ROOM_UNAVAILABLE` is emitted by routes where retrying really is the right
// advice (a busy pool, a lost connection — R18's transport noise); rewriting it would take honest
// advice away from every one of them to fix the one place it is a lie. Two facts, two sentences.
//
// **AND IT CARRIES A REFERENCE, WHICH IS THE HALF SHE HAS BEEN ASKING FOR.** The real error went to
// `console.error` alone — Railway's runtime console, which this repo already records as the thing
// nobody can read after the fact (it is the whole reason `gate_log` lives in Postgres). So a member
// reporting *"it will not delete"* left NOTHING behind to look up, and no cause has ever been named
// for her defect. The sentence now ends in a short code that is also on the stored row, so what she
// reports and what the owner reads are the same incident.
export const ROOM_FAILED_PREFIX = "The room hit a fault doing that, and it has been recorded.";
export const ROOM_FAILED_NOTE = "Telling Lala this reference is what lets it be traced";

// A reference is SIX characters from an unambiguous alphabet — no O/0, no I/1/L — because its whole
// job is to survive being read off a phone and typed into a message by hand. `random` is a
// PARAMETER (this house's pattern, R142/R145/R63) so a gate RUNS it rather than reading it, and so
// the same reference can be produced twice in a test without a clock or a global.
export const ROOM_REF_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export function roomFailureRef(random = Math.random) {
  let ref = "";
  for (let i = 0; i < 6; i += 1) {
    // `Math.floor` of a random in [0,1) cannot reach `length`, but a SUPPLIED random is somebody
    // else's function and a fixture is entitled to hand this a 1. Clamping is what stops a stray
    // `undefined` landing in a reference she is then asked to read out (R19: an instrument's gaps,
    // and a caller's, must never read as facts).
    const at = Math.min(Math.floor(random() * ROOM_REF_ALPHABET.length), ROOM_REF_ALPHABET.length - 1);
    ref += ROOM_REF_ALPHABET[Math.max(at, 0)];
  }
  return ref;
}

// The sentence a member reads. **THE REFERENCE IS NEVER INVENTED HERE** — an absent one means the
// recording did not happen, and promising a traceable code for an incident with no row behind it is
// the exact class of false receipt this house has spent twenty rounds abolishing (R141). With no
// ref the fault is still reported honestly, just without a code to quote.
export function roomFailedMessage(ref = "") {
  const code = typeof ref === "string" ? ref.trim() : "";
  return code ? `${ROOM_FAILED_PREFIX} ${ROOM_FAILED_NOTE}: ${code}` : ROOM_FAILED_PREFIX;
}

// ---------------------------------------------------------------------------
// R83 — **WHAT `json()` ANSWERED, AND WHAT THE ROOM DID WITH IT, WHICH WAS NOTHING.**
//
// R72 gave the whole room its own sentence above all 29 routes, and every act route beneath it
// composes real ones of its own (*"That is not yours to delete."*, *"That is not here."*,
// *"Nothing to flag."*). **NOT ONE OF THEM HAD EVER REACHED A MEMBER** — R51/R60/R66's finding a
// fourth time: the server was already honest and the client dropped the answer on the floor.
//
// `json()` swallows the status code and hands back the parsed body or `null`, and **that answer is
// already enough to tell R18's two things apart, which is why this is a reader and not a second
// transport**: `null` is TRANSPORT NOISE (a thrown request, an unparseable body — nothing was
// decided), and a parsed `{ ok: false }` is a DECISION the server deliberately sent. Collapsing
// them is what turned every one of those sentences into an empty room.
//
// The two are kept apart in `roomAnswerKind` even though they take the same sentence today (R18's
// own ruling, and R70's), because they are different facts about a request.
// ---------------------------------------------------------------------------
export const ROOM_READ_FAILED = "The room could not be read just now. Try again in a moment.";

// FREE-1 (her order, 2026-08-11) — **WHAT A FREE EXPLORER IS TOLD WHEN SHE WALKS INTO THIS ROOM.**
// Her clause 1 is that every room opens with no popup and she sees what the house IS; her clause 2
// puts the price at the moment of generation. The community feed is neither: it is somebody else's
// words, and a member who has not joined may not read them. So the room OPENS, dressed, and says
// this — one warm line where the feed would be, instead of the box that used to meet her here.
//
// **IT IS A DIFFERENT FACT FROM `ROOM_READ_FAILED` AND IS KEPT APART FOR THE REASON THAT LAW IS
// KEPT APART FROM ITS OWN NEIGHBOUR (R18):** *"the room could not be read"* invites her to try
// again, which is a lie for a room that will answer exactly the same way on the next tap.
// >>> RETIRED AT ITS DECLARATION BY HER BROWSE AMENDMENT (2026-08-19) — DL-2/DT-2b/R181. <<<
// Her words: *"THE LIVING ROOM opens to its velvet-rope face for free accounts... never a hard wall
// from the menu, the room itself makes its case."* This sentence WAS that wall: it dressed the room
// and put one line where the feed would be. What replaced it is the rope, which is the room's own
// face and its own whole render, so the sentence has nowhere left to be drawn. It is deleted rather
// than kept warm (R165: a member-facing line nothing draws is a capability the house says it has
// and does not), and every instrument that read it is migrated at its own declaration.

// >>> TRAP-1b's COPY, AND THE SECOND LINE IS HERS VERBATIM. <<< Her order quotes it: *"The Living
// Room is a secret place where people from all over the world gather to share, post, and give
// their spiritual experiences."* It is kept word for word rather than paraphrased into house
// voice, because a paraphrase of a sentence she wrote herself is a second answer to what this room
// IS (R28/R29) — and it is the whole of the EXPLANATION half of *desire, then explanation, then
// price*.
//
// **NOT ONE OF THESE THREE NAMES A PRICE, A TIER OR A PAYMENT**, which is her ordering held as a
// property of the words rather than as a promise about the layout: the money is what the ENTER
// tap opens, and it cannot arrive before it.
export const VELVET_ROPE_HEADING = "You have been tagged in the Living Room.";
// >>> HER BROWSE AMENDMENT (2026-08-19): THE ROPE IS THE ROOM'S FACE FOR EVERY FREE ARRIVAL, AND
// THE TAGGED SENTENCE IS A CLAIM THAT MAY NOT RIDE WITH IT. <<<
//
// Her words: *"THE LIVING ROOM opens to its velvet-rope face for free accounts: they enter and meet
// the standing message... never a hard wall from the menu, the room itself makes its case."* That
// widens TRAP-1b, whose rope fired ONLY on a tagged arrival — and that round was right on the day it
// shipped, for the reason it wrote down: a locked member who was only looking around must not meet a
// sales panel. **What makes the widening safe is that this is the ROOM'S OWN FACE rather than a
// popup over a page she was standing on**, which is the exact distinction FREE-1 draws.
//
// **BUT THE HEADING IS A FACT ABOUT HER AND THE BODY IS A FACT ABOUT THE ROOM (R18).** *"You have
// been tagged"* is TRUE only where somebody really tagged her; shown to an untagged member it is a
// receipt for something nobody witnessed (R141), on the first surface she meets. So the heading is
// RESOLVED and the body — her own sentence, the whole of the explanation half — is unconditional.
export const VELVET_ROPE_HEADING_OPEN = "The Living Room is waiting for you.";
export function ropeHeading(taggedArrival = false) {
  return taggedArrival === true ? VELVET_ROPE_HEADING : VELVET_ROPE_HEADING_OPEN;
}
export const VELVET_ROPE_BODY = "The Living Room is a secret place where people from all over the world gather to share, post, and give their spiritual experiences.";
export const VELVET_ROPE_ACTION = "Enter the room";

export function roomAnswerKind(d) {
  if (d === null || d === undefined) return "offline";
  return d.ok === true ? "ok" : "refused";
}

// The sentence for an answer that is not ok, and "" for one that is. **THE SERVER'S OWN WORDS COME
// FIRST** — it is the only side that knows whether this was a 403 about ownership or a 404 about a
// row that is gone — and the house's sentence is the floor beneath it, never a replacement for it.
export function roomNote(d, fallback = ROOM_UNAVAILABLE) {
  if (roomAnswerKind(d) === "ok") return "";
  const said = d && typeof d.message === "string" ? d.message.trim() : "";
  return said || fallback;
}

// A collection, or `null` when the read did not land — W5-2/R39's THIRD STATE, and the whole reason
// the room's own empty-state invitation stopped being printed over a feed that never arrived.
// **PRESENCE, NOT TRUTHINESS** (R53): a 200 that carries no list at all is not a list.
export function roomItems(d, key) {
  if (roomAnswerKind(d) !== "ok") return null;
  return Array.isArray(d[key]) ? d[key] : null;
}

// ---------------------------------------------------------------------------
// B2 (her order, 2026-08-15) — **WHAT A SIGNED-OUT VISITOR MEETS AT THIS DOOR.**
//
// Her report: the room's signed-out face is a blank page with the background and a sign-in line,
// reading as broken emptiness. **MEASURED ON PRODUCTION RATHER THAN READ:** `/api/living-room/feed`
// and `/me` both answer `401 {"message":"Sign in first."}` with **no `locked` flag on either** — so
// `gate(d, "arrival")` never fires for her, `roomItems` answers `null`, and `roomNote`'s own law
// (the server's words first) puts **"Sign in first."** where the feed would be. The sentence she is
// looking at is `requireFeature`'s GENERIC SITE-WIDE 401, not this room's own.
//
// **R18 — THREE FACTS, AND THE FIRST ONE HAD NO FACE.** *She is not signed in*, *she is signed in
// and below tier* (FREE-1's `ROOM_BROWSE_ONLY`), and *the read failed* (`ROOM_READ_FAILED`). The
// signed-out case was falling into the THIRD one's slot wearing the SERVER'S words — and
// `ROOM_READ_FAILED` ends *"Try again in a moment"*, which is a lie for a room that will answer
// exactly the same way on the next tap. That is the same distinction `ROOM_BROWSE_ONLY` was split
// out for, one fact over.
//
// **THE DIRECTION IS THE RULING (R19/R8-4): THE INVITATION NEEDS A POSITIVE *NO SESSION*.** Showing
// a sign-in invitation to a member who IS signed in reads as the house not knowing who she is, and
// it renders perfectly. So `isLoggedIn === false` and never a truthiness read (R53/R136/R137) —
// **and `sessionKnown === true` beside it**, which is L5.1's law and is not belt-and-braces here:
// this component's own comment records paying for exactly this shape (*"on a fresh load the session
// has not come back yet, so `isMember` is false for a beat, and a paying Apprentice landing on her
// own bookmark got the membership box thrown in her face"*). *We have not looked yet* and *she is
// not signed in* are two facts and only the second one may draw this.
//
// **`locked` OUTRANKS EVERYTHING AND IS ASKED FIRST**, because it is a positive fact from the SERVER
// that depends on the client knowing nothing at all: production sends it only to a session, so a
// below-tier member on a deep link keeps FREE-1's browse-only face through the beat before her
// session answers.
//
// **AND AN OFFLINE READ IS *failed* AND NEVER *invite*** — `roomAnswerKind`'s own ruling that a
// thrown request decided NOTHING (R18/R70). *Try again in a moment* is the honest sentence there,
// and it is the wrong one for a refusal that will repeat forever.
// ---------------------------------------------------------------------------
export const ROOM_INVITE_HEADING = "Where the spiritual community gathers";
export const ROOM_INVITE_BODY =
  "Sign in to join the community and see what members are sharing, posting and building together.";
export const ROOM_INVITE_CTA = "Sign in";

// HER BROWSE AMENDMENT (2026-08-19): `browse-only` leaves this roster with the face it named. The
// rope is declared below and joins here, so every face this law can answer is on the list and no
// face on the list is unreachable.
export const ROOM_ENTRY_FACES = ["invite", "rope", "failed", ""];

// =================================================================================================
// TRAP-1b — **THE VELVET ROPE.** Her order: *"ONLY signed-up-never-paid accounts meet THE VELVET
// ROPE: a popup in her exact aesthetic that INTRODUCES the room BEFORE ever asking for money … with
// ONE gold ENTER control, and THAT tap opens the membership popup. **DESIRE, THEN EXPLANATION, THEN
// PRICE. NEVER PRICE FIRST.**"*
//
// >>> HER CORRECTION OF 2026-08-17 IS WHY THIS IS A LOCK TEST AND NOT A TIER LIST. <<< She struck
// her own earlier note that Newbie has no Living Room: `FEATURES["room:living"]` is **newbie**, so
// **ALL PAID TIERS — Newbie, Apprentice, Vet and grants — land directly ON the post**, and the rope
// is for the one cohort that has never paid. Read as a tier roster this would need editing every
// time she moves a room between tiers; read as `locked`, it is the SERVER's own answer about this
// member and it can never disagree with the gate (R28/R29).
//
// >>> AND IT IS A DOOR RATHER THAN A WALL, WHICH IS FREE-1's RULING HELD RATHER THAN BENT. <<<
// TRAP-1b fired this ONLY on a tag arrival, and wrote its reason down: a free member who walks in
// from the menu must not meet a sales panel. **HER 2026-08-19 AMENDMENT WIDENS IT TO EVERY LOCKED
// ARRIVAL** — *"the room itself makes its case"* — and what makes that safe is the distinction
// FREE-1 really draws: this is the ROOM'S OWN FACE rather than a popup over a page she was standing
// on. The tag still decides what the rope SAYS (`ropeHeading`), because being tagged is a claim
// about HER rather than about the room, and telling somebody she was tagged when nobody tagged her
// is a receipt for something that did not happen (R141).
// =================================================================================================
export const VELVET_ROPE_FACE = "rope";

export function roomEntryFace(d, { isLoggedIn = null, sessionKnown = false } = {}) {
  const kind = roomAnswerKind(d);
  if (kind === "ok") return "";
  // The rope is asked ABOVE browse-only and BELOW the ok test: a member who can READ the room was
  // never behind the rope, and one who cannot must meet the introduction rather than the sentence.
  // HER BROWSE AMENDMENT (2026-08-19): a LOCKED arrival meets the rope whether or not she was
  // tagged — *the room itself makes its case*. TRAP-1b's marker still decides what the rope SAYS
  // (see `ropeHeading`), because being tagged is a claim about HER rather than about the room.
  // FREE-1's `browse-only` sentence is retired at this declaration rather than left standing beside
  // its replacement (DL-2/DT-2b/R181): it dressed the room and put one line where the feed would be,
  // which is a wall wearing a room, and it is the thing her amendment is naming.
  if (d && d.locked === true) return VELVET_ROPE_FACE;
  if (kind === "refused" && sessionKnown === true && isLoggedIn === false) return "invite";
  return "failed";
}

// ---------------------------------------------------------------------------
// WHAT MAY BE POSTED — the honest limits, stated IN-UI (LR1-2's explicit requirement).
//
// The numbers are here rather than at the upload site so the sentence the member reads and the
// number the server enforces are the same fact. A cap the UI does not state is a cap that reads as
// a bug the first time a member hits it.
// ---------------------------------------------------------------------------
// HER ORDER, 2026-09-30: "Increase the limit for uploads in the living room post to 25mb so people
// can share pdf and books." There was no file kind at all, so `document` is a NEW post kind (a PDF or
// an EPUB book), not a bigger picture; pictures stay 8MB and video 80MB on her word ("Photos can stay
// at 8mb", HOUSE-Q3).
export const POST_KINDS = ["text", "image", "video", "document"];
export const LIMITS = {
  titleChars: 120,
  postChars: 5000,
  commentChars: 2000,
  // **HER ORDER (2026-08-07): THE MESSAGE CAP IS 2,000, THE SAME NUMBER A COMMENT ALREADY HAD.** It
  // moved here rather than into the composer, because the SERVER enforces this table and a client
  // that stopped typing at 2,000 while the route accepted 5,000 would be two answers to one question
  // (R28/R29) — and the other direction is worse: a member typing 2,001 characters into a box that
  // let her, then losing the message at the door. `limitCopy.message` is derived from this line, so
  // what she is TOLD moves with it. Existing longer messages are untouched; a cap governs a SEND.
  messageChars: 2000,
  images: 4,              // per post
  imageBytes: 8 * 1024 * 1024,
  // **HER ORDER (2026-08-15, C6): 50MB -> 80MB.** The number is still a REQUEST-BODY decision rather
  // than a guess at what a video should weigh — an upload arrives base64-encoded inside the post's
  // JSON, which costs a THIRD on top. **AND THAT IS WHY THIS LINE COULD NOT MOVE ON ITS OWN:** the
  // route's ceiling was a SECOND hand-typed number (80MB) in another file, and 80MB of video is a
  // ~107MB body. Raised here alone, every upload she was promised would have died at the door —
  // which is the exact failure the retired note on this line warned about in its own words (*"a cap
  // the server cannot actually accept is the worst kind: the member is told 100MB and the request
  // dies at the door"*). **The ceiling is DERIVED from this table now** (`postBodyCeiling` below),
  // so the cap she is shown and the body the route will read can never again be two numbers.
  videoBytes: 80 * 1024 * 1024,
  videoSeconds: 180,
  // **HER ORDER (2026-09-30): A PDF OR A BOOK, UP TO 25MB.** One per post, and it travels ALONE (no
  // pictures or video beside it), which is what keeps `postBodyCeiling` a `max` of two shapes rather
  // than a sum of three, and the server enforces exactly that (`postDocumentRefusal`).
  documents: 1,
  documentBytes: 25 * 1024 * 1024,
};

// **THE BODY THE POST ROUTE MUST BE ABLE TO READ, DERIVED FROM THE CAPS RATHER THAN TYPED BESIDE
// THEM (R28/R29/R31).** Two numbers that must agree are two answers to one question, and this pair
// fails silently in the direction that costs a member her upload: she is told 80MB, the composer
// lets her pick it, the encode runs, and the request is refused 413 at the door with the video
// already read off her phone.
//
// **IT IS THE WORST POST THE *SERVER* REALLY ACCEPTS, NOT THE ONE THE COMPOSER OFFERS.** The picker
// swaps a video in for images and back (`addVideo` replaces the whole tray), but the ROUTE takes
// `media.slice(0, LIMITS.images + 1)` and has no clause forbidding a video beside four pictures — so
// a body carrying both is legal at the door and the ceiling has to be able to read one. Measured and
// left alone rather than tightened here (DECISIONS C6-3, scope rule 4).
export const BASE64_EXPANSION = 4 / 3;
// Everything in the JSON that is not the media itself: a 120-character title, a 5,000-character body
// at up to 4 bytes a character, five `data:video/mp4;base64,` prefixes, the keys, and JSON's own
// escaping. Measured at well under 100KB; declared at 1MB so nothing here turns on a tight estimate.
export const POST_ENVELOPE_BYTES = 1024 * 1024;
export function postBodyCeiling() {
  // The worst legal body is the larger of the two shapes a post may take: pictures and a video
  // together (the route allows both), or ONE document travelling alone. Derived from LIMITS, so her
  // 25MB and this ceiling can never be two numbers (the C6 lesson written above).
  const rawMedia = Math.max(
    LIMITS.videoBytes + (LIMITS.images * LIMITS.imageBytes),
    LIMITS.documents * LIMITS.documentBytes,
  );
  return Math.ceil(rawMedia * BASE64_EXPANSION) + POST_ENVELOPE_BYTES;
}

// The exact sentences the composer shows. Composed from LIMITS so a cap change moves the copy too.
const mb = (bytes) => `${Math.round(bytes / (1024 * 1024))}MB`;
export function limitCopy() {
  return {
    title: `A title, up to ${LIMITS.titleChars} characters.`,
    post: `Up to ${LIMITS.postChars.toLocaleString("en-US")} characters.`,
    images: `Up to ${LIMITS.images} pictures, ${mb(LIMITS.imageBytes)} each.`,
    video: `One video, up to ${mb(LIMITS.videoBytes)} and ${LIMITS.videoSeconds / 60} minutes.`,
    document: `One PDF or book (EPUB), up to ${mb(LIMITS.documentBytes)}.`,
    comment: `Up to ${LIMITS.commentChars.toLocaleString("en-US")} characters.`,
    message: `Up to ${LIMITS.messageChars.toLocaleString("en-US")} characters.`,
  };
}

// A too-big or too-long upload is refused AT THE COMPOSER with the honest reason — this is not
// moderation and never shadow-hides. A member whose video is too long has done nothing wrong and
// must be told plainly, or she will re-record it four times into a silent void.
export function attachmentRefusal({ kind, bytes = 0, seconds = 0, count = 0 } = {}) {
  const c = limitCopy();
  if (kind === "image") {
    if (count > LIMITS.images) return `That is ${count} pictures. ${c.images}`;
    if (bytes > LIMITS.imageBytes) return `That picture is ${mb(bytes)}. ${c.images}`;
    return "";
  }
  if (kind === "video") {
    if (bytes > LIMITS.videoBytes) return `That video is ${mb(bytes)}. ${c.video}`;
    if (seconds > LIMITS.videoSeconds) return `That video runs ${Math.round(seconds / 60)} minutes. ${c.video}`;
    return "";
  }
  if (kind === "document") {
    // One decimal place, ROUNDED UP, because a file one byte over would otherwise be told "25.0MB ...
    // up to 25MB": refused in words that say it fits.
    if (count > LIMITS.documents) return `That is ${count} files. ${c.document}`;
    if (bytes > LIMITS.documentBytes) return `That file is ${(Math.ceil((bytes / (1024 * 1024)) * 10) / 10).toFixed(1)}MB. ${c.document}`;
    return "";
  }
  return "";
}

// LR16 — **WHAT A CONTENT TYPE MAY SAY, AND WHY THE ANSWER IS A ROSTER RATHER THAN THE CLIENT'S
// WORD.** The bytes of every picture and clip in this room are served back by one route with the
// stored `content_type` on the header, and until this round that string was whatever the browser
// handed us: `String(m.contentType || "image/jpeg")`. A client is a thing a member controls, so a
// posted `text/html` came back FROM OUR OWN ORIGIN wearing that header, behind a member's session.
//
// **IT REFUSES NOTHING SHE CAN ACTUALLY DO, WHICH IS WHAT MAKES IT SAFE ON A LIVE PATH.** Every real
// photograph and every real phone video already names one of these; anything else falls back to the
// kind's own default and is still stored and still served. So this is not a narrower upload gate —
// R15's disease would be a picker that offers what the server refuses — it is a rule about what the
// SERVER is allowed to repeat back. The sniff (does this file really contain what it claims) is a
// separate law and is deliberately NOT invented here: the post path has never had one either, and
// closing that is its own round rather than a clause smuggled into this one (R25/R126).
export const MEDIA_CONTENT_TYPES = {
  image: ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/avif"],
  video: ["video/mp4", "video/quicktime", "video/webm"],
};
export const MEDIA_DEFAULT_TYPE = { image: "image/jpeg", video: "video/mp4" };

export function mediaContentType(kind, claimed) {
  // A content type may carry parameters (`image/jpeg; charset=…`), and the part before the semicolon
  // is the type. Casing is not significant in a MIME type and a browser is entitled to send either.
  const named = String(claimed || "").split(";")[0].trim().toLowerCase();
  // HER ORDER 2026-09-30: a DOCUMENT asks this same roster function (so every write path still has
  // ONE answer to "what type is stored"), but it has no default: the post route hands it the SNIFFED
  // type (`documentTypeOf`), and anything off the document roster answers "" rather than a guess.
  if (kind === "document") return DOCUMENT_CONTENT_TYPES.includes(named) ? named : "";
  const roster = MEDIA_CONTENT_TYPES[kind];
  if (!roster) return "";
  return roster.includes(named) ? named : MEDIA_DEFAULT_TYPE[kind];
}

// =================================================================================================
// >>> **HER ORDER, 2026-09-30: PDFs AND BOOKS IN A POST, UP TO 25MB.** <<<
//
// **THE DOCUMENT ROSTER, AND WHY IT IS A SEPARATE ONE.** `MEDIA_CONTENT_TYPES` is what a MESSAGE may
// carry (`messageMediaRefusal` refuses any kind it does not roster), and her order names the POST
// only, so a document lives on its own roster and the inbox keeps refusing it by construction.
//
// **AND UNLIKE A PICTURE, A DOCUMENT IS SNIFFED AND REFUSED, NEVER DEFAULTED.** A picture's roster
// falls back to `image/jpeg` because every real photo already names a rostered type. A file that
// claims to be a PDF and is not one has no honest default: serving it from this origin under a
// document header is exactly the `text/html`-from-our-own-origin risk LR16 closed for pictures. So
// the STORED type is what the bytes ARE (the sniff), the client's claim is never read for it, and a
// file that is neither is refused with words she can act on.
//
//   PDF  — "%PDF-" at byte 0 (ISO 32000's own header).
//   EPUB — a ZIP whose FIRST entry is the uncompressed file "mimetype" holding exactly
//          "application/epub+zip", which the EPUB OCF spec REQUIRES so readers can sniff it: local
//          header "PK\x03\x04" at 0, the name at byte 30, the content right after it.
// =================================================================================================
export const DOCUMENT_CONTENT_TYPES = ["application/pdf", "application/epub+zip"];
/** The post composer's PDF/book picker offer, derived from the roster above so what the picker
 *  offers and what the route accepts are one fact (R15-3d). The extensions help phones that
 *  report no type for an EPUB. */
export function postDocumentAccept() {
  return [...DOCUMENT_CONTENT_TYPES, ".pdf", ".epub"].join(",");
}

const asBytes = (buf) => (buf && typeof buf.length === "number" ? buf : []);
const bytesAt = (buf, offset, ascii) => {
  const b = asBytes(buf);
  if (b.length < offset + ascii.length) return false;
  for (let i = 0; i < ascii.length; i += 1) if (b[offset + i] !== ascii.charCodeAt(i)) return false;
  return true;
};

/** What the bytes REALLY are, from the roster, or "" when they are neither a PDF nor an EPUB. */
export function documentTypeOf(buf) {
  if (bytesAt(buf, 0, "%PDF-")) return "application/pdf";
  if (bytesAt(buf, 0, "PK\u0003\u0004") && bytesAt(buf, 30, "mimetypeapplication/epub+zip")) return "application/epub+zip";
  return "";
}

/** The words a refused file gets: what it was, what the room takes. */
const DOCUMENT_NOT_A_BOOK = "That file is not a PDF or an EPUB book.";

/**
 * >>> **WHAT A POST'S ATTACHMENTS MAY BE, ASKED ONCE FOR THE WHOLE TRAY.** <<<
 *
 * `media` items carry `kind`, `bytes` (a COUNT) and `head` (the first bytes, for the sniff).
 * Answers "" when the tray is legal, otherwise the sentence she is shown. It closes the path's old
 * gap too: before this an UNROSTERED kind on a post was stored with an empty content type, because
 * `attachmentRefusal` and `mediaContentType` both answer "" for a kind they do not know.
 */
export function postDocumentRefusal(media = []) {
  const items = Array.isArray(media) ? media : [];
  const known = (k) => k === "document" || Boolean(MEDIA_CONTENT_TYPES[k]);
  if (items.some((m) => !known(m?.kind))) return "That kind of file cannot go in a post.";
  const docs = items.filter((m) => m?.kind === "document");
  if (!docs.length) return "";
  if (docs.length > LIMITS.documents) return attachmentRefusal({ kind: "document", count: docs.length });
  if (items.length > docs.length) return `A PDF or book travels on its own. ${limitCopy().document}`;
  const size = attachmentRefusal({ kind: "document", bytes: Number(docs[0].bytes) || 0 });
  if (size) return size;
  if (!documentTypeOf(docs[0].head)) return `${DOCUMENT_NOT_A_BOOK} ${limitCopy().document}`;
  return "";
}

/**
 * The name the card shows and the download keeps: hers, made safe. Paths, control characters and
 * header-breaking quotes come out; it is capped; and the extension matches what the bytes ARE, so a
 * book saved off the room opens in a reader rather than as a mystery file.
 */
export function documentFileName(name, contentType) {
  const ext = contentType === "application/epub+zip" ? ".epub" : ".pdf";
  let base = String(name || "").split(/[\\/]/).pop() || "";
  base = base.replace(/[\u0000-\u001f\u007f"<>|*?:]/g, "").replace(/\s+/g, " ").trim();
  base = base.replace(/\.(pdf|epub)$/i, "").slice(0, 120).trim();
  return `${base || (ext === ".epub" ? "Book" : "Document")}${ext}`;
}

/** The card's second line: what it is and how big, e.g. "PDF · 24.3MB" or "EPUB book · 3.1MB". */
export function documentCardLabel({ contentType = "", sizeBytes = 0 } = {}) {
  const what = contentType === "application/epub+zip" ? "EPUB book" : "PDF";
  const n = Number(sizeBytes) || 0;
  return n > 0 ? `${what} · ${(n / (1024 * 1024)).toFixed(1)}MB` : what;
}

/** Whether the card offers Open (a PDF reads in the browser) as well as Download (both do). */
export const documentOpensInPlace = (contentType) => contentType === "application/pdf";

/**
 * The header the bytes route sends for a document. A PDF OPENS in the browser's own viewer (her
 * "share pdf" is read in place); an EPUB has no viewer in a browser, so it downloads. The ASCII
 * fallback keeps old clients working and `filename*` carries her real title in UTF-8 (RFC 6266/5987).
 */
export function documentDisposition(contentType, fileName) {
  const name = documentFileName(fileName, contentType);
  const ascii = name.replace(/[^\x20-\x7e]/g, "_").replace(/[\\]/g, "_");
  const mode = contentType === "application/pdf" ? "inline" : "attachment";
  return `${mode}; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(name)}`;
}

// LR16 — **HER ORDER SAYS DM MEDIA RIDES THE STANDING CAPS, SO IT ASKS `attachmentRefusal`** — the
// room's own function, the room's own sentences — rather than a second table of numbers for the
// inbox. The one thing that differs is the SUBJECT of the count: a post allows four pictures and one
// video, and a message is held to the same, because a member who learned the room's limits should
// not have to learn a second set to send the same photograph to one person.
// ---------------------------------------------------------------------------
// IT #124 (2026-08-21) — **NINE VIDEOS THAT ARE NOWHERE NEAR HER SCREEN FETCH THE MOMENT SHE OPENS
// THE BUSIEST ROOM IN THIS HOUSE.**
//
// ITEM 7's ruling is that an inline clip is a THUMBNAIL WITH A PLAY BADGE and that *"the frame she
// sees is the file's own first frame"* — which a `<video preload="metadata">` paints with no poster
// stored anywhere. Right, and it is UNCONDITIONAL, so every video row in a thread starts fetching on
// mount whether or not it is within five hundred messages of the fold.
//
// **MEASURED ON ELITE 2026 BEFORE A LINE WAS WRITTEN (R25/R180):** 693 messages visible to a member,
// **NINE of them carrying a video, at rows 196, 301, 392, 393, 394, 398, 465, 546 and 655 of 693** —
// **263.6MB of video, the biggest 62.5MB** — and the thread opens at the NEWEST message, so the
// nearest of the nine is 38 rows above the fold and the furthest is 497. **All nine fetch anyway**,
// against the FIVE-CONNECTION POOL 13-F1 warned about and PERF-1 measured saturating on exactly this
// class. That round repaired the attachment DOOR (a SQL slice and `Accept-Ranges`) and the FEED's
// cards; the inbox thread is a different render and it was never touched.
//
// **THE PICTURES AND THE AVATARS ARE ALREADY LAZY** (`loading="lazy"`, 53 images and 148 distinct
// authors in that thread), so the video is the ONLY eager media left in a message list — which is
// why it is the one thing this round moves.
//
// **THE COST IS NAMED RATHER THAN HIDDEN (R17/R160), BECAUSE IT IS REAL:** a video further back than
// the window draws its play badge over an EMPTY frame until she taps it, and a tap still opens the
// full-screen viewer, which carries `preload="metadata"` and `controls` and plays exactly as it does
// today. So what is lost is the poster frame on a clip she has scrolled hundreds of messages to
// reach, and what is bought is that a member opening the room fetches nothing she cannot see.
// **The honest upgrade is an IntersectionObserver that promotes a row as it nears the viewport** —
// declared rather than built, because it costs `MessageMedia` the hookless property that lets two
// fixtures CALL it (R7/R8/R10-3), and that is a trade nobody has authorised.
//
// It lives here for the same reason every other decision in this file does: a `.mjs` gate LOADS and
// RUNS it, where a ternary inside a memoized component could only ever be grepped.

/**
 * HOW MANY ROWS BACK FROM THE NEWEST MESSAGE STILL FETCH THEIR OWN FIRST FRAME.
 *
 * A DECLARED number, so moving it is a deliberate act in the open. It is comfortably more than a
 * screenful at any viewport — PERF-1 measured a rendered `.lr-msg` at 166px, so forty rows is
 * roughly six phone screens of scrolling — and it is what makes the ordinary case (a short DM, a
 * quiet group) BYTE-IDENTICAL to what shipped: every row in a thread of forty or fewer is eager.
 */
export const MEDIA_EAGER_ROWS = 40;

/**
 * **IS THIS ROW NEAR ENOUGH TO THE NEWEST MESSAGE TO BE WORTH FETCHING?**
 *
 * The thread opens at the newest, so distance from the END is the question, and it is the same shape
 * `receiptMessageId` already answers from — the THREAD decides and the row obeys, rather than every
 * row holding an opinion about where it is.
 *
 * Presence, never coercion (R53/R136/R137): these arrive from a `.map` and `Number(true)` is 1, so
 * an index that is not a real index would otherwise silently make a far row eager. **Anything it
 * cannot place is EAGER**, which is exactly today's behaviour — the failure mode of this law is the
 * status quo and never something worse (R19).
 */
export function rowMediaIsEager(index, total, window = MEDIA_EAGER_ROWS) {
  if (!Number.isInteger(index) || !Number.isInteger(total)) return true;
  if (index < 0 || total <= 0) return true;
  return total - index <= window;
}

/**
 * **WHAT A MESSAGE'S OWN VIDEO ELEMENT MAY ASK FOR.**
 *
 * `metadata` is ITEM 7's ruling and is what paints her first frame; `none` is the same element with
 * nothing fetched. There is no third value in the spec that would give a poster without a request,
 * which is why this is a two-way answer rather than a tuning knob.
 *
 * `eager` is PRESENCE and never truthiness, and it DEFAULTS TO EAGER so a caller that says nothing
 * gets exactly what shipped (ITEM 12's neutrality argument).
 */
export function mediaPreloadFor(eager) {
  return eager === false ? "none" : "metadata";
}

export function messageMediaRefusal(media = []) {
  const items = Array.isArray(media) ? media : [];
  if (!items.length) return "";
  const unknown = items.find((m) => !MEDIA_CONTENT_TYPES[m?.kind]);
  // NOT a plausible default: a kind nobody rosters is refused rather than filed as a picture, because
  // "we did not recognise this" and "this is an image" are different facts (R18) and only one of them
  // is true.
  if (unknown) return "That kind of file cannot go in a message.";
  const images = items.filter((m) => m.kind === "image");
  const videos = items.filter((m) => m.kind === "video");
  const count = attachmentRefusal({ kind: "image", count: images.length });
  if (count) return count;
  if (videos.length > 1) return limitCopy().video;
  for (const m of items) {
    const refusal = attachmentRefusal({ kind: m.kind, bytes: Number(m.bytes) || 0, seconds: Number(m.durationSeconds) || 0 });
    if (refusal) return refusal;
  }
  return "";
}

// LR16(d) — **WHAT A NOTIFICATION SAYS ABOUT A MESSAGE THAT IS A PICTURE.** Her order: the bell and
// the push say *sent you a photo* / *sent you a video*, WITHOUT leaking content.
//
// **THE WORDS SHE WROTE ARE STILL THE EXCERPT WHEN THERE ARE ANY**, because a caption is what she
// chose to say and a notice that replaced it would be less informative, not safer. The notice is for
// the case a text excerpt cannot reach: a message that is media alone, where the old code excerpted
// the empty string and the bell said nothing at all about a photograph that had just arrived.
//
// It NAMES no filename, no dimensions and no duration — a filename is content (`divorce-papers.pdf`
// is a sentence), and a lock screen is read by whoever is holding the phone.
export function messageExcerpt(text, media = []) {
  const words = String(text || "").trim();
  if (words) return words;
  const items = Array.isArray(media) ? media : [];
  if (!items.length) return "";
  const videos = items.filter((m) => m?.kind === "video").length;
  const images = items.filter((m) => m?.kind === "image").length;
  // A mixed message leads with the PICTURES, because that is what a thumbnail will show her.
  if (images > 1) return `sent you ${images} photos`;
  if (images === 1) return "sent you a photo";
  if (videos) return "sent you a video";
  // A kind nobody rosters is not given a plausible name (R18/R25): silence is honest, a wrong noun
  // is not.
  return "";
}

// ---------------------------------------------------------------------------
// THE COMMUNITY GUIDELINES — HER RULES, DRAFTED FOR HER WORD.
//
// DRAFTED means exactly that: this is my drafting of the rules she dictated in LR1-3, written out so
// she has something to strike through rather than a blank page. Every `rule` below maps to a reason
// code the machine gate actually enforces, so the page cannot promise a rule that nothing checks —
// `scripts/lr1-living-room.mjs` holds the two lists to each other.
// ---------------------------------------------------------------------------
export const GUIDELINES_TITLE = "The Living Room — house rules";
export const GUIDELINES_INTRO =
  "This room is for spirituality and metaphysical intelligence. That is what it is for, and that is what keeps it worth being in. Read these once. They are enforced.";

export const GUIDELINES = [
  {
    code: "purpose",
    rule: "Keep it to the work.",
    body: "Spirituality and metaphysical intelligence is this room's purpose. Questions, spells-talk, readings, your own work, reviews of what you have tried — that is the room.",
  },
  {
    code: "harassment",
    rule: "No bullying, harassment, or hate.",
    body: "Not at another member, not at a group of people. No slurs, no pile-ons, no threats. This one has no version of itself that is a joke.",
  },
  {
    code: "family",
    rule: "Nothing derogatory about the Conjure, about Lala, or about her children.",
    body: "Her family is not a topic in this room. Neither is running down the practice you came here to learn.",
  },
  {
    code: "shipping",
    rule: "Order and shipping problems do not belong here.",
    body: "This room cannot fix an order. Anything about a package, a tracking number, a refund or a delivery goes to support, where somebody can actually act on it.",
  },
  {
    code: "doxxing",
    rule: "No doxxing.",
    body: "No addresses, phone numbers, emails, government names, workplaces, or screenshots that carry them — yours or anybody else's.",
  },
  {
    code: "soliciting",
    rule: "No soliciting.",
    body: "No selling, no promoting your own services or readings, no recruiting, no affiliate links.",
  },
  {
    code: "nsfw",
    rule: "Nothing NSFW.",
    body: "No sexual content, no graphic violence, no gore.",
  },
  {
    code: "identity",
    rule: "You are your room name here.",
    body: "Everyone in this room is known by their room first name and nothing else. Do not ask another member for more, and do not post your own.",
  },
];

export const GUIDELINE_CODES = GUIDELINES.map((g) => g.code);

// K4 (her J3-Q4 ruling, 2026-10-19): sign-up says "By signing up, you agree to our Terms and house rules", and the house
// rules link opens THESE rules, the same list the room shows and the gate enforces, as a document.
export function guidelinesMarkdown() {
  return [GUIDELINES_INTRO, ...GUIDELINES.map((g, i) => `${i + 1}. **${g.rule}** ${g.body}`)].join("\n\n");
}

// ---------------------------------------------------------------------------
// THE MACHINE GATE — MACHINE-FIRST, RUN ON EVERYTHING BEFORE PUBLIC VISIBILITY (LR1-3).
//
// Deterministic patterns first, at zero cost, on every post, comment AND DM. What this catches is
// hidden immediately with no engine call at all; what it is unsure about is what the engine reads.
// That ordering is the cost control AND the latency control, and it is why the room can gate every
// keystroke of community content without a bill that scales with chatter.
//
// Every entry pairs a reason code (which the moderation queue shows the owner) with the guideline it
// enforces. There is no rule here that the guidelines page does not state.
// ---------------------------------------------------------------------------

const SLURS_AND_HATE = [
  /\b(?:n[i1]gg(?:er|a)s?|f[a4]gg?[o0]ts?|k[i1]kes?|sp[i1]cs?|ch[i1]nks?|tr[a4]nn(?:y|ies)|ret[a4]rds?)\b/i,
  /\b(?:kill|gas|lynch|exterminate)\s+(?:all\s+)?(?:the\s+)?(?:jews|blacks|whites|muslims|gays|women|men|immigrants)\b/i,
];

const HARASSMENT = [
  // Directed abuse at a person: "you are a <slur/insult>", "kill yourself", "nobody likes you".
  /\b(?:kill|k[i1]ll)\s*(?:your\s*self|yourself|urself)\b/i,
  /\bk\s*y\s*s\b/i,
  /\byou(?:'?re| are)\s+(?:a\s+)?(?:stupid|worthless|pathetic|disgusting|trash|garbage|ugly|fat|dumb)\b/i,
  /\b(?:shut up|nobody (?:likes|wants) you|go die|drop dead)\b/i,
  /\b(?:i(?:'|’)?ll|i will|gonna|going to)\s+(?:find|hurt|beat|kill|end)\s+you\b/i,
];

// RULING 5 — THE FAMILY-GATE CLASS, EXTENDED TO COMMUNITY CONTENT. This is a COMMUNITY-CONTENT
// gate, not the Ask Lala probe ladder: a member here is not asking Lala about her daughters, she is
// SAYING something about them to a room full of people. So the shape is different — a reference to
// the family or the practice CARRYING a derogatory charge — and the charge is what is banned, not
// the mention. "Lala's teaching changed my life" must pass; nothing below matches it.
const FAMILY_SUBJECT =
  /\b(?:lala|the\s+conjure|conjure\s+(?:academy|family|body|hoodoo)|soul\s+purge|ahari\s+water|her\s+(?:daughters?|kids?|children|girls?|family)|lala'?s?\s+(?:daughters?|kids?|children|girls?|family|man|husband))\b/i;
const DEROGATORY_CHARGE =
  /\b(?:scam(?:mer|ming)?|fraud(?:ulent)?|fake|phon(?:y|ey)|liar|lying|grift(?:er|ing)?|thief|steal(?:ing)?|stole|crook|con\s*artist|hoe|whore|slut|bitch(?:es)?|ratchet|ghetto|trash(?:y)?|nasty|ugly|stupid|dumb|clown|joke|washed|busted|snake|demonic|evil|witch\s*craft\s*is\s*evil)\b/i;

// RULING 5, SECOND HALF — CONJURE SHIPPING / ORDER COMPLAINTS, BANNED OUTRIGHT. Note this is the one
// rule where a member with a completely legitimate grievance is hidden: she is not wrong, she is in
// the wrong room, and the room cannot solve it. The moderation queue's reason line says exactly that
// so the owner can route her to support rather than read it as misconduct.
const COMMERCE_SUBJECT =
  /\b(?:order(?:ed|s)?|package|parcel|shipment|shipping|shipped|tracking|deliver(?:y|ed|ies)?|refund(?:ed)?|charge[ds]?|invoice|receipt|payment|paid|purchase[ds]?|bought|checkout|customer\s*service|support\s*ticket)\b/i;
const COMPLAINT_CHARGE =
  /\b(?:never\s+(?:came|arrived|shipped|received|got)|still\s+(?:waiting|haven'?t|has\s*not|hasn'?t)|hasn'?t\s+(?:come|arrived|shipped)|didn'?t\s+(?:come|arrive|get)|no\s+(?:tracking|response|reply|answer|update)|weeks?\s+ago|months?\s+ago|wrong\s+item|missing|lost|damaged|broken|late|delayed|where\s+is\s+my|when\s+will\s+(?:i|my)|want\s+my\s+money|charged\s+me\s+twice|double\s+charged)\b/i;

const DOXX = [
  // Street address, phone number, email address, and "her real name is".
  /\b\d{1,5}\s+[A-Za-z][A-Za-z.'-]*(?:\s+[A-Za-z][A-Za-z.'-]*)*\s+(?:st|street|ave|avenue|rd|road|blvd|boulevard|ln|lane|dr|drive|ct|court|way|pkwy|parkway)\b\.?/i,
  /(?:^|[^\d])(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}(?!\d)/,
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/,
  /\b(?:her|his|their)\s+(?:real|government|legal|@)\s*name\s+is\b/i,
  /\b(?:she|he|they)\s+works?\s+at\b[^.?!]{0,40}\b(?:in|on)\s+[A-Z][a-z]+/,
];

const SOLICITING = [
  /\b(?:dm|pm|message|text|hit)\s+me\s+(?:for|to)\b[^.?!]{0,40}\b(?:readings?|prices?|rates?|services?|bookings?|appointments?|deals?|specials?)\b/i,
  /\b(?:book|booking)\s+(?:me|with\s+me)\b|\bmy\s+(?:cash\s*app|cashapp|venmo|paypal|zelle)\b/i,
  /\b(?:cash\s*app|venmo|paypal|zelle)\s*[:$]?\s*[$@]?[A-Za-z0-9_-]{2,}/i,
  /\b(?:i\s+(?:do|offer|sell)|selling|offering)\b[^.?!]{0,30}\b(?:readings?|spells?|candles?|oils?|kits?|courses?|mentorships?)\b[^.?!]{0,30}\b(?:\$|\d+\s*(?:dollars|bucks)|dm|inbox|link\s+in)\b/i,
  /\b(?:affiliate|referral)\s+link\b|\blink\s+in\s+(?:my\s+)?bio\b|\bpromo\s*code\b/i,
  /\b(?:follow|subscribe\s+to)\s+(?:me|my)\b[^.?!]{0,20}\b(?:page|channel|shop|store|ig|instagram|tiktok|youtube|onlyfans)\b/i,
];

const NSFW = [
  /\b(?:porn(?:o|hub)?|onlyfans|nudes?|n[u4]d3s|sex\s*tape|xxx)\b/i,
  /\b(?:suck|fuck|eat)\s+(?:my|your|his|her)\s+(?:dick|cock|pussy|ass)\b/i,
  /\b(?:dick|cock|pussy|titties|cum)\s+pic(?:s|ture)?s?\b/i,
  /\b(?:decapitat|dismember|mutilat|behead)(?:e|ed|ing)?\b/i,
];

const IDENTITY_PROBE = [
  /\bwhat(?:'?s| is)\s+your\s+(?:real|government|legal|last|full)\s*name\b/i,
  /\b(?:send|give|drop|what(?:'?s| is))\s+(?:me\s+)?your\s+(?:number|digits|address|email|@|insta(?:gram)?|snap(?:chat)?|cash\s*app)\b/i,
  /\bmy\s+(?:number|address|email)\s+is\b/i,
];

// The roster. ORDER IS THE PRECEDENCE ORDER: the first code that matches is the reason shown to the
// owner, so the gravest classes sit at the top and a post that is both hateful and soliciting is
// filed as hate.
const RULES = [
  { code: "harassment", label: "Hate speech or a slur", test: (s) => SLURS_AND_HATE.some((re) => re.test(s)) },
  { code: "harassment", label: "Bullying or harassment", test: (s) => HARASSMENT.some((re) => re.test(s)) },
  { code: "family", label: "Derogatory about the Conjure, Lala, or her family", test: (s) => FAMILY_SUBJECT.test(s) && DEROGATORY_CHARGE.test(s) },
  { code: "nsfw", label: "NSFW or graphic content", test: (s) => NSFW.some((re) => re.test(s)) },
  { code: "doxxing", label: "Doxxing or personal contact details", test: (s) => DOXX.some((re) => re.test(s)) },
  { code: "identity", label: "Asking for or posting identity beyond a room name", test: (s) => IDENTITY_PROBE.some((re) => re.test(s)) },
  { code: "shipping", label: "An order or shipping complaint — belongs in support, not the room", test: (s) => COMMERCE_SUBJECT.test(s) && COMPLAINT_CHARGE.test(s) },
  { code: "soliciting", label: "Soliciting, selling, or promoting", test: (s) => SOLICITING.some((re) => re.test(s)) },
];

// THE MACHINE PASS. Returns [] for clean content, or the violations in precedence order.
// `kind` is "post" | "comment" | "message" and rides along to the queue so the owner sees where it
// happened; no rule differs by kind, because her ruling was that DMs run THE SAME gates. A shared
// SPELL (LR1-14) is a post and runs exactly this, which is the point of making it one.
//
// LR1-15 — `allowances` IS HER TUNING KNOB, and it is checked per rule rather than globally. Each
// entry says "this phrase is fine FOR THIS CLASS": clearing "fake" for the family class must not
// also clear it for harassment. A rule whose trip is fully explained by an allowed phrase does not
// fire. This is the voice-ban mechanism in reverse — she adds bans there, she removes catches here.
// =================================================================================================
// >>> **THE PROTECTED CLASS — HER RULING, 2026-08-31: A MEMBER REPORTING A FAULT IS COMMUNITY, NEVER
// A VIOLATION.** <<<
//
// Her words: *"members reporting a house outage are community, never violations... outage/fault
// reports are never held by the filter."*
//
// **THIS IS NOT HYPOTHETICAL AND IT IS NOT SMALL.** On 2026-08-29 the Anthropic credit balance ran
// dry and Ask Lala went down for 71 minutes. Members did what members do: they told each other.
// When the review engine came back and those messages were re-read, it held two of them —
//
//     "Everything says not available right now"   -> held as `shipping`
//     "Not working for me either"                 -> held as `purpose`
//
// — which means the house's own outage produced members reporting it, and the house's own filter
// then treated them as rule-breakers. **A woman is punished for noticing that we were broken.** She
// is never told (shadow-hide is silent by design), so the only thing she learns is that saying it
// out loud made her invisible.
//
// **THE EXEMPTION IS FROM THE TOPIC RULES AND FROM NOTHING ELSE, AND THAT LINE IS THE WHOLE SAFETY
// OF IT.** `purpose` and `shipping` are about what the room is FOR; a fault report is off-topic only
// in the sense that a fire alarm is off-topic. Harassment, hate, doxxing, NSFW, soliciting and
// anything about her family are UNTOUCHED — a report wrapped around a slur is still a slur, and an
// exemption that reached those would be a hole anybody could walk through by adding "the site is
// broken" to the end of a sentence.
// >>> **IT IS THE SYMPTOM THAT MAKES A FAULT REPORT, NOT THE SUBJECT — AND THE FIRST CUT OF THIS GOT
// IT BACKWARDS.** <<< Requiring a named subject looked careful and failed on **the exact two messages
// her ruling is about**: *"Everything says not available right now"* names no subject at all, and
// *"Not working for me either"* names nothing but the trouble. That is how members actually write in
// a room — the subject is whatever everyone was already talking about.
//
// **THE ORDER COMPLAINT IS EXCLUDED BY NAME instead**, which is the real thing the subject test was
// reaching for. A package, a tracking number or an order is a commerce problem her own 2026-08-31
// ruling routes to The Conjure, and it stays held here exactly as it did.
const ORDER_SUBJECT = /\b(?:package|parcel|tracking|shipment|shipp(?:ed|ing)|deliver(?:y|ed)|my\s+order|the\s+order|order\s+(?:number|status|#)|refund|usps|fedex|dhl)\b/i;
const FAULT_SYMPTOM = new RegExp(
  "\\b(?:"
  + "not\\s+working|isn.?t\\s+working|won.?t\\s+work|doesn.?t\\s+work|"
  + "is\\s+down|are\\s+down|went\\s+down|"
  + "not\\s+available|unavailable|"
  + "not\\s+loading|won.?t\\s+load|wont\\s+load|will\\s+not\\s+load|"
  + "error\\s+message|error\\s+messages|getting\\s+an?\\s+error|keeps?\\s+erroring|"
  // "kicks our messages out", "kicked me out", "keeps kicking me out" — all one complaint, and the
  // words between the verb and "out" are whatever she was in the middle of losing.
  + "kick(?:s|ed|ing)?\\s+(?:\\w+\\s+){0,3}out|logged\\s+me\\s+out|lose\\s+all\\s+my|"
  + "blank\\s+(?:page|screen)|white\\s+screen|"
  + "glitch(?:ed|ing|y)?|frozen|freez(?:es|ing)|stuck\\s+(?:on|at)|spinning|"
  // **`anyone else` ALONE WAS TOO LOOSE AND THE BACKLOG SCAN CAUGHT IT.** *"Has anyone else..."* is
  // how half this room opens a sentence, and on its own it made an order complaint about a coin that
  // never arrived look like a house fault — which would have restored it into the room against her
  // OTHER ruling. The phrase only counts when it is asking about a problem.
  + "same\\s+(?:problem|issue|thing)|not\\s+working\\s+for\\s+me\\s+either|"
  + "anyone\\s+else\\s+(?:having|getting|seeing|unable)|is\\s+it\\s+just\\s+me|"
  + "having\\s+(?:the\\s+same|trouble|issues?)"
  + ")\\b", "i",
);

/**
 * **IS THIS A MEMBER REPORTING THAT SOMETHING IS BROKEN?**
 *
 * Deliberately generous on the SUBJECT (including a bare "it", because *"it keeps kicking me out"*
 * is how a real member writes it) and strict on the SYMPTOM. Being wrong here publishes a message
 * that was going to be published anyway in every case but one — the topic rules — so the generous
 * direction costs nothing and the strict direction costs a member her voice.
 */
export function isFaultReport(text) {
  const s = String(text ?? "");
  if (!s.trim()) return false;
  // An order complaint is not a house fault, however it is phrased. Her 2026-08-31 ruling sends
  // those to The Conjure, and this exemption must not become the back door into the room for them.
  if (ORDER_SUBJECT.test(s)) return false;
  return FAULT_SYMPTOM.test(s);
}

/** The codes a fault report is exempt from. NEVER the safety ones, and the list is declared here so
 *  nobody has to infer it from a call site (R17/R160). */
export const FAULT_EXEMPT_CODES = ["purpose", "shipping"];

export function communityViolations(text, { kind = "post", allowances = [] } = {}) {
  const s = String(text ?? "");
  if (!s.trim()) return [];
  const out = [];
  for (const rule of RULES) {
    if (!rule.test(s)) continue;
    if (allowedAway(s, rule.code, allowances)) continue;
    out.push({ code: rule.code, label: rule.label, kind, by: "machine" });
  }
  return out;
}

// Does an allowance for this class cover what tripped? The test is deliberately strict: the allowed
// phrase must actually APPEAR in the text. An allowance is a pardon for a specific phrase she has
// seen and cleared, never a blanket switch that turns a whole class off.
export function allowedAway(text, code, allowances = []) {
  const s = String(text ?? "").toLowerCase();
  return (allowances || []).some(
    (a) => String(a?.reason || "") === code && s.includes(String(a?.phrase || "").trim().toLowerCase()) && String(a?.phrase || "").trim().length > 0,
  );
}

// LR1-15 — WHICH CLASSES SHE KEEPS OVERTURNING. Pure arithmetic over the overturn log, so the queue
// can say "you have reversed `shipping` eleven times" instead of making her notice it herself. A
// class over the threshold is FLAGGED, never auto-relaxed: relaxing a gate because it fired a lot is
// how a gate gets talked out of the thing it was built for. The decision stays hers.
export const OVERTURN_FLAG_AT = 5;
export function overturnPressure(overturns = []) {
  const counts = {};
  for (const o of overturns) {
    const code = String(o?.reason || "");
    if (!code) continue;
    counts[code] = (counts[code] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([code, count]) => ({
      code,
      count,
      rule: GUIDELINES.find((g) => g.code === code)?.rule || code,
      flagged: count >= OVERTURN_FLAG_AT,
    }))
    .sort((a, b) => b.count - a.count);
}

// Does the machine pass alone settle it? A clean machine pass is NOT a clean post — it is a post the
// engine should read. This function names that fact so no call site can quietly treat "no regex
// matched" as "approved", which is how a machine-first gate silently becomes a machine-only gate.
export function needsEngineReview(text, violations = []) {
  if (violations.length) return false;             // already decided, and hidden — do not spend
  return String(text ?? "").trim().length > 0;
}

// The engine's verdict, folded into the same shape. `allow` is the only thing that publishes.
export function engineVerdictToViolations(verdict, { kind = "post" } = {}) {
  if (!verdict || verdict.allow !== false) return [];
  const code = GUIDELINE_CODES.includes(verdict.code) ? verdict.code : "purpose";
  const g = GUIDELINES.find((x) => x.code === code);
  return [{ code, label: g?.rule || "Against the house rules", kind, by: "engine", note: String(verdict.reason || "").slice(0, 300) }];
}

// ---------------------------------------------------------------------------
// SHADOW-HIDE — RULING 3. THE VISIBILITY QUESTION IS ALWAYS "TO WHOM".
// ---------------------------------------------------------------------------
export const VISIBILITY = { PUBLIC: "public", HIDDEN: "hidden", REMOVED: "removed" };

// Can THIS viewer see THIS item? The whole ruling, in one function.
//   • public  → everyone, unless a block sits between them.
//   • hidden  → the AUTHOR sees it exactly as she posted it, and the OWNER sees it in her queue.
//               Nobody else knows it exists. No rejection message is ever generated anywhere.
//   • removed → the owner deleted it; it is gone for everyone including the author.
export function canSee(item, viewer = {}) {
  if (!item) return false;
  const authorRef = String(item.authorRef ?? item.author?.ref ?? "");
  const viewerRef = String(viewer.ref ?? "");
  const isAuthor = Boolean(authorRef) && authorRef === viewerRef;
  if (item.visibility === VISIBILITY.REMOVED) return false;
  if (item.visibility === VISIBILITY.HIDDEN) return isAuthor || Boolean(viewer.owner);
  if (isAuthor || viewer.owner) return true;
  // RULING 4: a block hides both directions on the public feed.
  return !isBlockedBetween(viewer, authorRef);
}

// RULING 4 — ABSOLUTE, AND BOTH WAYS. `viewer.blocked` is the set of refs this viewer blocked;
// `viewer.blockedBy` is the set who blocked this viewer. Either one is a wall.
export function isBlockedBetween(viewer = {}, otherRef = "") {
  const ref = String(otherRef ?? "");
  if (!ref) return false;
  const blocked = viewer.blocked instanceof Set ? viewer.blocked : new Set(viewer.blocked || []);
  const blockedBy = viewer.blockedBy instanceof Set ? viewer.blockedBy : new Set(viewer.blockedBy || []);
  return blocked.has(ref) || blockedBy.has(ref);
}

// May this viewer comment on this item? Ruling 4 names the comment box specifically: a blocked
// member cannot see the blocker's comment box. Hidden items take no comments from anyone but their
// author — a shadow-hidden post that collected replies would tell its author it was hidden the
// moment the replies were invisible to her.
//
// **LR11-2 — THE CODE CONTRADICTED THE SENTENCE DIRECTLY ABOVE IT, AND IT LEAKED THE HOLD.** The
// line read `if (item.visibility === HIDDEN) return false;` with no author exception, so a
// shadow-held post took no comments from ANYONE — *including the member who wrote it*. `canSee`
// hands that same author her post back "exactly as she posted it" (line 458), so what she actually
// got was her own post, rendered normally, with the reply box silently missing from it and from
// nothing else in the room. **That absence IS the rejection message ruling 1 says is never
// generated anywhere.** A member cannot fail to notice that her post alone cannot be answered.
//
// The author exception is the whole of the fix and it is what the law already said. The OWNER is
// deliberately NOT given it: she sees held content in her queue to judge it, and a reply from her
// seat that the author could read would disclose the hold from the other end. Everything else is
// untouched — a silenced member still does not write, and a block is still absolute.
export function canComment(item, viewer = {}) {
  if (!canSee(item, viewer)) return false;
  const authorRef = String(item.authorRef ?? item.author?.ref ?? "");
  const isAuthor = Boolean(authorRef) && authorRef === String(viewer.ref ?? "");
  if (item.visibility === VISIBILITY.HIDDEN && !isAuthor) return false;
  if (viewer.silenced) return false;              // a struck member reads but does not write
  return !isBlockedBetween(viewer, authorRef);
}

// ---------------------------------------------------------------------------
// LR19 — **A HELD ITEM LOOKS HELD ON HER SEAT, AND ON NO OTHER SEAT IN THE WORLD.** (2026-08-07, hers)
// ---------------------------------------------------------------------------
//
// **HER REPORT, AND IT IS A GAP IN RULING 1 RATHER THAN A BREACH OF IT.** `canSee` has always let the
// owner read held content in the room — that is the line above, and it is deliberate. What nothing
// ever did was TELL her. A held comment renders on her seat in the ordinary body type, in the
// ordinary place, so she reads it believing the room read it too, and the hold is discoverable only
// by leaving the room for the dashboard. **A moderator who cannot tell judged content from published
// content is not moderating; she is reading.**
//
// **RULING 1 IS UNTOUCHED AND THIS IS WHY.** Shadow-hide is a promise to the MEMBER: no rejection
// message, no marker, no absence she can notice. The owner is not a member of that promise — she is
// the one the promise is kept BY. So the treatment is owner-eyes-only, and "members see zero change"
// is not a hope about a render site, it is this function: **`viewer.owner` is asked FIRST and a
// non-owner gets `null` no matter what the payload says.** The server already strips `heldReason` and
// forces `visibility: public` for everybody else (`shapePost`/`shapeComment`), so this is the SECOND
// wall rather than the only one — and it is the one a gate can RUN over every combination, which the
// payload law cannot be without a database.
//
// **THE AUTHOR OF A HELD ITEM GETS NOTHING, INCLUDING WHEN THE AUTHOR IS ALSO A MEMBER OF THIS
// HOUSE'S OWNER SET.** Owner outranks author, because the yellow is a fact about the ITEM and her
// seat is the seat that acts on it; an owner reading her own held post is still the person who has
// to decide about it. The case that must never invert is the other one: a member author who is not an
// owner sees her own words exactly as she wrote them, forever, which is ruling 1 in one clause.
//
// Three outcomes rather than two (R18): `null` is *nothing to say here*, and a treatment names WHICH
// state — `held` (the gate caught it, she can still let it through) versus `removed` (already gone
// for everyone, and there is nothing left to approve). Collapsing those two would offer her an
// Approve button on a row that no longer exists for its own author.
export const HELD_TREATMENT = { HELD: "held", REMOVED: "removed" };
// What the strip says. Her seat reads a SENTENCE, not a status word: "held" alone does not say who
// can see it, and who can see it is the entire question she is being asked to rule on.
export const HELD_NOTICE = "Held by the gate — only you and its author can see this.";
export const REMOVED_NOTICE = "Removed — gone for everyone, including its author.";
export function heldTreatment(item, viewer = {}) {
  // OWNER FIRST, AND NOT AS AN OPTIMISATION. Every clause below this line is unreachable for a
  // member, which is what makes the member half of her ruling a property of this function rather
  // than a property of whoever writes the next render site.
  if (!viewer || viewer.owner !== true) return null;
  if (!item) return null;
  if (item.visibility === VISIBILITY.REMOVED) {
    return { state: HELD_TREATMENT.REMOVED, notice: REMOVED_NOTICE, reason: item.heldReason || null, canApprove: false };
  }
  if (item.visibility === VISIBILITY.HIDDEN) {
    return { state: HELD_TREATMENT.HELD, notice: HELD_NOTICE, reason: item.heldReason || null, canApprove: true };
  }
  return null;
}
// `owner !== true` rather than a truthy read, because `owner` arrives off a JSON payload and
// `Boolean("false")` is `true` (R136/R137, paid for on a coerced flag in this same room).

// WHAT THE STRIP MAY OFFER, WHICH IS A DIFFERENT QUESTION FROM WHAT HER SEAT MAY DO. A REMOVED item
// is already gone for everyone including its author, so there is nothing to approve and nothing to
// keep holding — offering either would be a control that cannot do what its label says. It is
// DERIVED from the one roster and from the treatment's own `canApprove`, never a second list, so an
// action added to `FEED_MOD_ACTIONS` arrives here by existing (R31).
export function heldActionsFor(treatment) {
  if (!treatment) return [];
  if (!treatment.canApprove) return [];
  return FEED_MOD_ACTIONS;
}

// May this viewer post or send at all? The strike ladder's silence lands here.
export function canPost(viewer = {}) {
  return !viewer.silenced;
}

// May A message B? Ruling 4, on the inbox side. Absolute and symmetric.
export function canMessage(viewer = {}, otherRef = "") {
  if (viewer.silenced) return false;
  return !isBlockedBetween(viewer, otherRef);
}

// CRYSTAL (her emergency, 2026-08-12) — **THE "IS THIS CONVERSATION CLOSED TO HER" LAW IS NOT HERE,
// AND ITS ABSENCE IS THE RULING.** It lives in `server/thread-access.js`, read by the door she opens
// and by the door she speaks through. **A PARTICIPANT-SET BLOCK QUESTION MAY NOT BE ANSWERED IN THIS
// FILE** (R181): a viewer context carries `viewer.blocked` and `viewer.blockedBy` and no third
// party's pairs, so a client that looks as though it can answer one will be asked one. The first
// draft of that repair was written here and R181 went red about it, correctly — the law moved to the
// server rather than R181 being weakened to fit the round in front of it.

// ---------------------------------------------------------------------------
// ITEM 3 — OWNER REACH (her order, 2026-08-07). **THE HOUSE DOES NOT GET BLOCKED, AND THE HOUSE CAN
// REACH ANYBODY.** Two halves of one order, and they are written here together because they are the
// same fact seen from the two ends of a wall.
//
// **WHY THIS IS NOT A SOFTENING OF RULING 4.** Ruling 4 is about two MEMBERS: a block that only works
// one way hands the blocker's words to the blocked, so between members it stays absolute, symmetric
// and untouched by every line below. Her seat is not the other side of a member relationship — it is
// the room's moderation, its support desk and the only account that can answer a report. **A block on
// it is a member removing herself from the one place she can be helped from**, and it is silent: she
// gets no notice, the house gets no notice, and the first anybody learns of it is a support ticket
// that cannot be answered. Two of them were live on her seat when this shipped.
//
// **AND IT IS ASKED OF `owner`, NEVER OF THE CROWN, WHICH IS THE HALF THAT NEEDED WRITING DOWN.**
// S-2 split those rosters precisely so a crown could say WHO SOMEBODY IS without handing her the
// refund door; a reach law that read `crowned` would hand four accounts an unblockable seat and a
// reach into every stranger's inbox, which is that split undone from the other side. `publicIdentity`
// carries both booleans and they are different sets on purpose. **Crowns keep normal member powers:
// a crowned member is blockable like anybody, and she follows before she messages like anybody.**
// ---------------------------------------------------------------------------

// The warm refusal, in the house's own voice: it says what the rule IS and what to do instead, rather
// than apologising or explaining the moderation architecture. It is a DECLARATION and not a literal at
// the call site for the same reason `MEMBER_NOT_HERE` is — the copy has one home so a copy edit
// somewhere cannot quietly turn it into something colder.
//
// **IT NAMES ITSELF, AND THAT IS DELIBERATELY THE OPPOSITE OF RULING 2's SILENCE.** A block between
// members refuses without saying so, because *that a block exists* is the thing a block withholds.
// This refusal has nothing to withhold: she already knows who she just tried to block, and the honest
// sentence is the only one that does not read as the tap having failed.
export const OWNER_BLOCK_REFUSAL =
  "The house does not get blocked. If something here needs looking at, report it and it comes straight to Lala.";

// **IS THIS SOMEBODY A MEMBER MAY BLOCK?** One predicate, asked by the surfaces that DRAW the control
// and by the route that honours it, so a control that is offered and a request that is accepted can
// never be two different answers (R15: a picker that offers what the gate refuses costs her the whole
// action before she is told no). The group participant list already hand-typed `!p.owner` and the
// one-to-one list did not, which is how a member came to have a Block button on the house at all.
//
// It takes the IDENTITY the surface is already holding rather than a ref, because `owner` is a fact
// carried on every identity this room draws (`publicIdentity`) and a ref would need a second lookup.
// **T141-1 — AND THE HOUSE'S OWN SEAT IS UNBLOCKABLE FOR ITEM 3's OWN REASON, WORD FOR WORD:**
// a block on the support desk is a member removing herself from the one place she can be helped
// from, silently, with nobody told. It asks `houseReach` rather than a second `|| who?.house`,
// because the message gate asks the same question and two spellings is how one of them drifts.
export function canBlock(who = {}) {
  return !houseReach(who);
}

// The violations of a block WRITE, in the order they are asked, so the caller answers the first one
// and never has to know the precedence. Blocking yourself is refused silently (it is a client bug, not
// a member act); blocking the house gets the sentence above.
export function blockViolations({ viewer, target } = {}) {
  const out = [];
  const me = String(viewer?.ref ?? "");
  const them = String(target?.ref ?? "");
  if (!me) out.push("no-viewer");
  if (!them) out.push("no-target");
  if (me && them && me === them) out.push("self");
  if (!canBlock(target)) out.push("owner");
  return out;
}

// What she is TOLD. Only the owner rule speaks; everything else collapses to the room's one sentence,
// which is `messageGateRefusal`'s shape exactly and for the same reason.
export function blockRefusal(codes = []) {
  const list = Array.isArray(codes) ? codes : [codes];
  if (!list.length) return null;
  if (list.includes("owner")) return OWNER_BLOCK_REFUSAL;
  return MEMBER_NOT_HERE;
}

// ---------------------------------------------------------------------------
// LR1-9 — THE @ SYSTEM. Identity in this room is @Name, where Name is the room first-name.
//
// THE SCRAPING RULE IS PART OF THE LAW, NOT AN IMPLEMENTATION DETAIL. Her order: the picker matches
// members "who have posted or are in the thread" — **no directory-scraping of the whole membership.**
// That is a privacy boundary wearing an autocomplete: a picker backed by the member table would let
// anybody type "a" and read a list of everyone who has ever paid, which is precisely the identity
// leak the room-first-name law exists to prevent. The candidate set is therefore always derived from
// PARTICIPATION, and the server query that builds it says so.
// ---------------------------------------------------------------------------

// A room name is one token, so a mention is one token. Deliberately not "@First Last" — there is no
// last name in this room, and allowing a space would make "@Dee and I talked" tag a member called
// "and". The trailing boundary is anything that is not a name character.
//
// THE LEADING LOOKBEHIND IS LOAD-BEARING AND WAS ADDED AFTER IT BIT: without it, "email me at
// dee@example.com" parses as a mention of @example, so pasting an address into the room would tag a
// stranger and notify them. An @ preceded by a letter or digit is part of an address or a handle,
// never a tag. `activeMentionQuery` already guarded this at the caret; the extractor did not, and
// the extractor is the one that writes the rows and fires the notifications.
// =================================================================================================
// >>> **IPAD-BLANK-1 (2026-09-06) — THE LOOKBEHIND IS GONE FROM BOTH PATTERNS. THE RULE IT ENFORCED
// IS NOT.** <<<
//
// **HER OWN IPAD WENT BLANK ON 12thhouse.ai, AND THIS WAS WHY.** Safari did not support regex
// lookbehind until 16.4. These two are MODULE-LEVEL CONSTANTS in a file the client imports, so on an
// older iPad they threw the instant the module was evaluated: React never mounted, and what she saw
// was a flash and then nothing.
//
// >>> **esbuild IS WHAT MAKES IT FATAL RATHER THAN OBVIOUS.** <<< It rewrites a regex literal it
// cannot target into `new RegExp("…")` so the bundle still PARSES — which converts a build-time
// error anybody would have caught into a runtime throw on the load path of every single page.
//
// >>> **THE BOUNDARY RULE IS LOAD-BEARING AND IS KEPT EXACTLY.** <<< It exists because
// `dee@example.com` was extracted as a mention of `@example` and notified a stranger. The pattern no
// longer asserts it; the LOOP asks it instead, which is the same question one character to the left:
//
//     /(?<![A-Za-z0-9])@name/   ->   /@name/ + precededBy(s, m.index, MENTION_BOUNDARY) === false
//
// **AND `m.index`, `m[0]` AND `m[1]` STAY BYTE-IDENTICAL**, which is the whole reason it is done this
// way rather than with a leading capture group: a captured prefix would shift every group number and
// widen every match, and four call sites below do position arithmetic on exactly those values to
// slice a member's own words apart.
// =================================================================================================

/**
 * Is the character before `at` one this pattern refuses to start after?
 * **The start of the string is always allowed**, which is what a lookbehind does at index 0.
 */
export function precededBy(text, at, re) {
  const i = Number(at);
  if (!Number.isFinite(i) || i <= 0) return false;
  const ch = String(text ?? "").charAt(i - 1);
  return ch !== "" && re.test(ch);
}

/** The two boundary rules, declared once so a pattern and its guard can never drift apart. */
export const MENTION_BOUNDARY = /[A-Za-z0-9]/;
export const LINK_BOUNDARY = /[\w@.]/;

export const MENTION_RE = /@([A-Za-z][A-Za-z0-9'’_-]{0,23})/g;

// Every @token in a piece of text, in order, deduplicated case-insensitively. Pure text work: this
// does NOT decide who exists. Resolution against real members happens once, on the server, at write
// time (see migration 054's note on why a tag is a stored row and not a re-scan).
export function extractMentions(text) {
  const out = [];
  const seen = new Set();
  // C5, AND IT IS THE LOOKBEHIND ABOVE ONE CHARACTER CLASS OVER. That guard was added because
  // `dee@example.com` tagged a stranger — an `@` after a LETTER is part of an address. An `@` after a
  // `/` is part of a HANDLE IN A PATH, and it was never considered: measured, `https://x.com/@dee`
  // extracted `dee`, so pasting a link notified a member who was never mentioned. This half is what
  // writes the rows and fires the bells, so masking is done HERE and not only at the render — or the
  // round would leave a notification pointing at a tag its own post no longer draws.
  const masked = maskLinks(String(text ?? ""));
  for (const m of masked.matchAll(MENTION_RE)) {
    // The boundary the pattern used to assert with a lookbehind (IPAD-BLANK-1).
    if (precededBy(masked, m.index, MENTION_BOUNDARY)) continue;
    const key = m[1].toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(m[1]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// C5 — **A LINK IN A MEMBER'S OWN WORDS IS TAPPABLE, AND THE LAW THAT DECIDES IT IS THE ONE THAT
// ALREADY TOKENISES HER BODY.** (her order, ITEM 11 / C5)
//
// HER CLAUSE: *inbox messages, group threads, Living Room posts and comments — real tappable links,
// new tab, plain URLs auto-linked, no raw dead text. Moderation and leak laws untouched.*
//
// **>>> ALL FOUR OF HER SURFACES ALREADY FUNNEL THROUGH `renderMentionParts`, SO THE LINK IS ONE
// DECLARATION AND THE FOUR SURFACES JOIN BY EXISTING (R28/R29). <<<** Measured before a line was
// written (R25/R180): `MentionBody` is the only body renderer on this site, `MessageRow` draws DMs
// and groups from ONE line, and the post body and the comment body — comments and replies share a
// row — are the other two. A linkifier written per surface is four answers to *what a link is*, and
// the copy that gets missed is the one somebody adds next.
//
// **THE ORDER IS LOAD-BEARING: URLS ARE MATCHED FIRST, MENTIONS INSIDE WHAT IS LEFT.** `MENTION_RE`
// carries a lookbehind so `dee@example.com` is not a tag, and its boundary is anything that is not a
// name character — so a `/` is one. Run mentions first and `https://x.com/@dee` is FRAGMENTED: the
// handle is lifted out as a tag and the URL is torn into three parts, none of which is a link. A URL
// is therefore one token before anything else looks at the text.
//
// **A SCHEME IS REQUIRED AND EVERYTHING ELSE IS REFUSED RATHER THAN GUESSED AT.** `emailAbsoluteUrl`
// is this house's existing precedent one rail over and it takes the same shape: `http`/`https` only,
// so `javascript:` and `data:` cannot be reached from a member's post — held TWICE, because the
// pattern cannot start on one and the resolver refuses one. **A BARE DOMAIN IS NOT A LINK** (`node.js`,
// `3.30 p.m.`, `Ms. Clark`): over-linking turns ordinary prose blue on the most-read surfaces in the
// house, and R19's direction is that the visible failure is the cheaper one — a `www.` or a scheme is
// what a member types when she means an address.
// The same de-lookbehind as `MENTION_RE` above, for the same reason and with the same guarantee:
// the pattern loses the assertion, `maskLinks` and `renderMentionParts` ask `LINK_BOUNDARY` instead,
// and `m.index` and `m[0]` are untouched — which matters more here than anywhere, because both call
// sites slice the member's text on exactly those two numbers.
export const LINK_RE = /(https?:\/\/|www\.)[^\s<>"'`]+/gi;

// The sentence's punctuation is the SENTENCE'S, not the link's. `read https://x.com/a.` must not
// navigate to `…/a.` — and `(see https://x.com/a)` must not eat the closing paren, while
// `https://en.wikipedia.org/wiki/Foo_(bar)` must keep its own. So a closer is kept when the link
// opened it and dropped when the prose did.
const LINK_TAIL = new Set([".", ",", ";", ":", "!", "?", '"', "'", "’", "”"]);
const LINK_CLOSERS = { ")": "(", "]": "[", "}": "{" };
export function trimLinkTail(raw) {
  let text = String(raw ?? "");
  for (;;) {
    const last = text.slice(-1);
    if (!last) break;
    if (LINK_TAIL.has(last)) { text = text.slice(0, -1); continue; }
    const open = LINK_CLOSERS[last];
    if (open && (text.split(last).length - 1) > (text.split(open).length - 1)) { text = text.slice(0, -1); continue; }
    break;
  }
  return text;
}

// What the browser is told to open, or "" for a string this house will not navigate to. The DISPLAY
// is what she typed and the HREF is normalised, which is the only pair that is both honest on screen
// and safe on the wire. `new URL` is what refuses a malformed address rather than a regex deciding
// it, and the protocol is asked AFTER parsing, so a scheme smuggled through a redirect spelling is
// still refused.
export function linkHref(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  // **THE `www.` BRANCH IS THE ONE PLACE THIS FUNCTION INVENTS A SCHEME, SO IT IS THE ONE THAT HAS
  // TO ASK WHETHER THERE IS A HOST THERE AT ALL.** `new URL("https://www.")` parses perfectly and
  // hands back the hostname `www.`, so without this a bare `www.` becomes a live anchor to nothing.
  // Unreachable through `LINK_RE`, which needs a character after the prefix — but this is the law and
  // the pattern is only one of its two callers.
  const absolute = /^www\./i.test(text) ? (/^www\.[^.\s]/i.test(text) ? `https://${text}` : "") : text;
  if (!/^https?:\/\//i.test(absolute)) return "";
  let url;
  try { url = new URL(absolute); } catch { return ""; }
  // R216, PAID IN THIS ROUND'S OWN BATTERY: a `url.protocol !== "http:"` test stood here and its
  // mutation came back VACUOUS — correctly. Nothing that fails the prefix test above reaches this
  // line, and nothing that passes it can parse to another protocol, so it could not change an answer
  // and no reader could trust it. **The dead line came out of the LAW rather than the mutation being
  // aimed around it.** The two walls that really decide are the PATTERN and that prefix test.
  if (!url.hostname) return "";
  return url.href;
}

// The characters a link occupies, blanked out so another pass cannot read INTO one. Spaces rather
// than deletion, because every offset either side has to stay where it was. Only a span this house
// would really navigate to is masked — a refused match is ordinary text and is left to be read.
export function maskLinks(text) {
  const s = String(text ?? "");
  let out = s;
  for (const m of s.matchAll(LINK_RE)) {
    const at = m.index ?? 0;
    // The boundary the pattern used to assert with a lookbehind (IPAD-BLANK-1).
    if (precededBy(s, at, LINK_BOUNDARY)) continue;
    const raw = trimLinkTail(m[0]);
    if (!raw || !linkHref(raw)) continue;
    out = `${out.slice(0, at)}${" ".repeat(raw.length)}${out.slice(at + raw.length)}`;
  }
  return out;
}

// Split a body into plain runs, link runs and mention runs, for rendering. Returns a flat list the
// client maps straight to elements — the alternative (dangerouslySetInnerHTML with a highlighted
// span) would put member-authored text into an HTML sink, which is the one thing a community room
// must never do, and it is the reason a link here is an ELEMENT WITH AN href rather than markup.
// `resolved` maps a lowercased typed name to the member ref it was resolved to at write time; a
// mention with no entry renders as PLAIN TEXT, because an unresolved @word is just a word.
//
// **THIS CHANGES WHAT IS DRAWN AND NOTHING ABOUT WHAT IS STORED OR SCREENED.** The gate reads the
// body on the way IN and this law runs on the way OUT, so LR1's shadow-hide, the strike ladder and
// P2-4C's leak net are untouched by construction — `visibleBody` is still what feeds it, and a held
// body still reaches only its author.
export function renderMentionParts(text, resolved = {}) {
  const s = String(text ?? "");
  const parts = [];
  let last = 0;
  for (const m of s.matchAll(LINK_RE)) {
    const at = m.index ?? 0;
    // The boundary the pattern used to assert with a lookbehind (IPAD-BLANK-1).
    if (precededBy(s, at, LINK_BOUNDARY)) continue;
    const raw = trimLinkTail(m[0]);
    const href = linkHref(raw);
    // A refusal is not a deletion: the text stays exactly where she typed it and is read again by
    // the mention pass below, so nothing a member wrote can be swallowed by this law.
    if (!href || !raw) continue;
    if (at > last) parts.push(...mentionParts(s.slice(last, at), resolved));
    parts.push({ type: "link", text: raw, href });
    last = at + raw.length;
  }
  if (last < s.length) parts.push(...mentionParts(s.slice(last), resolved));
  return parts.length ? parts : [{ type: "text", text: s }];
}

// The mention half, unchanged in what it decides — it simply no longer sees the runs a link took.
function mentionParts(text, resolved = {}) {
  const s = String(text ?? "");
  const parts = [];
  let last = 0;
  for (const m of s.matchAll(MENTION_RE)) {
    const at = m.index ?? 0;
    // The boundary the pattern used to assert with a lookbehind (IPAD-BLANK-1).
    if (precededBy(s, at, MENTION_BOUNDARY)) continue;
    // **THE HOUSE-WIDE CALL RENDERS AS ITS OWN KIND**, so the room can draw it gold and a member
    // knows at a glance she is being called with everybody else. It resolves to no member, which is
    // why it has to be recognised HERE — the loop above skips any @word with nobody behind it, and
    // that is exactly right for a name and exactly wrong for this.
    if (m[1].toLowerCase() === EVERYONE_TAG) {
      if (at > last) parts.push({ type: "text", text: s.slice(last, at) });
      parts.push({ type: "everyone", text: `@${m[1]}` });
      last = at + m[0].length;
      continue;
    }
    const ref = resolved[m[1].toLowerCase()];
    if (!ref) continue;
    if (at > last) parts.push({ type: "text", text: s.slice(last, at) });
    parts.push({ type: "mention", text: `@${m[1]}`, ref, name: m[1] });
    last = at + m[0].length;
  }
  if (last < s.length) parts.push({ type: "text", text: s.slice(last) });
  // An EMPTY run contributes nothing rather than an empty span: this half is now called per gap
  // between links, and a body that begins or ends on one has gaps of zero length. The "always at
  // least one part" guarantee callers rely on lives in `renderMentionParts` above, where it always did.
  return parts;
}

// What the composer is typing right now: the @token under the caret, or null. Drives the picker.
//
// LR1-12 ROUND TWO, HER DEFECT (a): **THE PICKER MUST NOT FIRE ON A BARE @.** It was matching zero
// characters, so the moment anybody typed "@" the whole room's roster dropped open — which is both
// startling and, on a room of any size, a list of everybody who has ever posted. It now needs at
// least one LETTER, and the query narrows as she types.
export const MENTION_MIN_CHARS = 1;
export function activeMentionQuery(text, caret) {
  const s = String(text ?? "");
  const pos = Math.max(0, Math.min(Number(caret) || 0, s.length));
  const before = s.slice(0, pos);
  // A name starts with a LETTER (same rule as MENTION_RE), so "@1" is not a half-typed tag either.
  const m = before.match(/@([A-Za-z][A-Za-z0-9'’_-]{0,23})$/);
  if (!m) return null;
  if (m[1].length < MENTION_MIN_CHARS) return null;
  // An @ immediately after a word character is an email address or a handle, not a tag.
  const charBefore = before[before.length - m[0].length - 1];
  if (charBefore && /[A-Za-z0-9]/.test(charBefore)) return null;
  return { query: m[1], start: before.length - m[0].length, end: pos };
}

// Her defect (a), second half: the list is SHORT. A picker is a shortcut, not a directory.
export const MENTION_PICKER_MAX = 6;

// Replace the active @token with a chosen name. Returns the new text and where the caret lands.
export function applyMention(text, active, name) {
  const s = String(text ?? "");
  if (!active) return { text: s, caret: s.length };
  const tag = `@${roomFirstName(name)} `;
  const next = s.slice(0, active.start) + tag + s.slice(active.end);
  return { text: next, caret: active.start + tag.length };
}

// ---------------------------------------------------------------------------
// LR13 — COMMENT REPLIES. BACK-AND-FORTH UNDER A COMMENT, ONE LEVEL DEEP, NEVER AN INFINITE TREE.
//
// The whole shape of this feature is two decisions, and both live here rather than at a call site:
// WHICH THREAD a reply joins, and WHO it is answering. Everything else in the room — the gates,
// shadow-hide, the two-body edit, delete-own, likes, reports, @tags — already applies to a comment
// row and therefore applies to a reply row, because **A REPLY IS A COMMENT WITH A PARENT.** That is
// the load-bearing design choice: not a new table, not a new kind, not a second visibility law to
// keep in step. A second table would have been a second answer to every one of those questions.
// ---------------------------------------------------------------------------
export const REPLY_DEPTH = 1;

// WHICH THREAD A REPLY JOINS, and it is the ONE place the tree is flattened. Instagram's rule and
// hers: a reply to a reply is not a grandchild — it joins the SAME thread and is auto-tagged at
// whoever is being answered. So the parent a reply is STORED under is always the thread ROOT, and a
// reply can therefore never be stored under a row that itself has a parent. Written as arithmetic on
// the target rather than as a depth check at the route, because a depth check is a rule that can be
// forgotten by the next surface that writes a comment; this cannot return a depth-2 answer.
export function replyParentFor(target) {
  if (!target) return null;
  const parent = target.parentId ?? target.parent_id ?? null;
  if (parent) return String(parent);
  const id = target.id ?? null;
  return id ? String(id) : null;
}

// WHO A REPLY IS ANSWERING, in the box, before she types a word. Her order is Instagram-exact:
// tapping Reply opens the compose with @[that person] ALREADY IN IT and the cursor after it. The
// trailing space is not cosmetic — without it her first keystroke lands inside the tag and the
// mention stops resolving, which is a tag that renders as plain text and notifies nobody.
export function replyPreTag(name) {
  const n = roomFirstName(name);
  return n ? `@${n} ` : "";
}
export function replyComposeState(name) {
  const text = replyPreTag(name);
  return { text, caret: text.length };
}

// ---------------------------------------------------------------------------
// D-5 — **WHETHER A COMMENT MAY GO, AND THE IN-FLIGHT CLAUSE IS THE WHOLE ROUND.**
//
// The post composer has always refused a second submit while one is in flight (`if (busy) return;`
// beside `disabled={busy}`); the comment send control had **no such guard at all** — not on the
// button, not on the Enter key — and the box does not clear until the answer arrives, so the body
// is still sitting there for the next press to send again. **THAT IS WHY POSTS HAVE ZERO DUPLICATES
// AND COMMENTS HAVE SEVEN**, and it is why the only three live replies in this room are byte-
// identical: it is one control, missing one guard, and nothing about the delete path (D-1's matrix
// is 29/29 green under real thumbs).
//
// **THREE OUTCOMES RATHER THAN TWO, WHICH IS R18 ON A CONTROL:** *she has nothing to send* and *one
// is already going* are different facts about the same tap, and only the second one means the
// button should look busy. A boolean here would make an empty box and a live request the same
// state, which is the shape ITEM 7's `self` argument settled one control down.
//
// **A PICTURE WITH NO WORDS IS A COMMENT** — the Instagram reading and the server's — so `pictures`
// is asked beside the text rather than the text alone.
//
// The law lives here rather than in the component for the reason every law in this file does: a
// decision inside a TSX file that holds hooks cannot be RUN by anything (R8/R95), and this one is
// the difference between one reply and three.
// ---------------------------------------------------------------------------
export const COMMENT_SEND_STATES = ["send", "empty", "in-flight"];

export function commentSendState({ inFlight = false, text = "", pictures = 0 } = {}) {
  // THE IN-FLIGHT CLAUSE IS FIRST AND THAT ORDER IS LOAD-BEARING. Asked after the empty test, a
  // second press arriving while the first request is still out — with the box still holding her
  // words, because it is not cleared until the answer lands — reads as an ordinary send.
  if (inFlight) return "in-flight";
  if (!String(text ?? "").trim() && !(Number(pictures) || 0)) return "empty";
  return "send";
}

// ---------------------------------------------------------------------------
// THE COMPOSER GROWS (her order, 2026-08-07)
// ---------------------------------------------------------------------------
//
// **HER REPORT: the write-a-message box is ONE ROW and a long message scrolls out of sight as she
// types.** Her ruling: it grows, multi-line, tall enough to show everything written, up to roughly a
// third of the screen, past which the box itself scrolls; **Enter makes a new line and the button
// sends**; a 2,000-character cap where typing stops and a small red line appears in house language;
// and the warning shows only NEAR the limit, never always.
//
// **THE TWO LAWS ARE HERE, PURE, BECAUSE THE COMPONENT CANNOT BE.** Auto-growing needs a live
// element (`scrollHeight` is a rendered fact), so the COMPONENT holds a ref and an effect — but the
// two decisions worth guarding are arithmetic and belong where a gate can RUN them over sizes no
// browser in this repo will ever produce.

// The cap notice, in house language and in her own words. One string, so the box, the fixture and
// the walk cannot disagree about what she reads.
export const COMPOSER_MAX_NOTICE = "You have reached the max characters.";
// **THE WARNING APPEARS ONLY NEAR THE LIMIT, WHICH IS HER CLAUSE AND IS WHY THIS IS A FRACTION
// RATHER THAN A CONSTANT.** A counter that is always on screen is clutter on a phone in a room whose
// members write two lines; one that appears at nine-tenths is a warning. Named, so tuning is one
// number.
export const COMPOSER_WARN_FRACTION = 0.9;

// Three outcomes rather than two (R18): *nothing to say*, *getting close*, and *stopped*. The middle
// one is the whole of her "only near the limit" clause, and collapsing it into the last would mean
// she meets the red line with no warning at all.
export function composerLimitState({ text = "", max = LIMITS.messageChars } = {}) {
  const cap = Math.max(1, Number(max) || LIMITS.messageChars);
  // **CHARACTERS AS A PERSON COUNTS THEM, WHICH IS GRAPHEMES AND NOT CODE POINTS.** `String.length`
  // counts UTF-16 code units, so a plain emoji is 2 and a member typing five hearts would be told she
  // had used ten of her characters. Spreading fixes that and is STILL wrong for the ones built out of
  // several joined parts — a family emoji is SEVEN code points — so the count is taken with
  // `Intl.Segmenter`, which is what a person means by "one character". The spread is kept as the
  // fallback for any runtime without it, because being slightly strict is better than throwing.
  const raw = String(text ?? "");
  const length = typeof Intl !== "undefined" && typeof Intl.Segmenter === "function"
    ? [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(raw)].length
    : [...raw].length;
  const remaining = Math.max(0, cap - length);
  const atLimit = length >= cap;
  return {
    length,
    cap,
    remaining,
    atLimit,
    // At the limit the warning is the red line; near it, it is the count.
    nearLimit: length >= Math.floor(cap * COMPOSER_WARN_FRACTION),
    notice: atLimit ? COMPOSER_MAX_NOTICE : "",
  };
}

// **WHAT THE BOX MAY GROW TO IS *NOT* DECLARED HERE, AND THAT IS THE ROUND'S SHARPEST DECISION.**
// The first draft of this round wrote a `composerHeight` right here — clamp the content between a
// floor and a third of the viewport, turn the scroll on past it — and it was a SECOND ANSWER to a
// question this house had already answered. `src/lib/live-view.ts` is W4-7's module, built for her
// dictation ruling in almost the same words (*"the input doesn't scroll/grow with long dictation,
// past a point she couldn't see her words"*), and it already holds `clampGrow`, `overflows`,
// `latestScrollTop` and `applyLiveView` — including the `height = "auto"` reset that is the one
// non-obvious part, and a fixture that RUNS all of it.
//
// **Two grow laws would have drifted, and the drift would be invisible**: a box that grows correctly
// on one composer and not on another is exactly the report she just made. The message composer
// declares its own LIMITS there, beside Lala's, and the mechanism stays one.

// The flat list the store returns, as the two-level shape the room renders. Sorted oldest-first at
// both levels, which is how a conversation reads.
//
// **AN ORPHAN IS RENDERED, NEVER DROPPED, AND THAT IS A SHADOW-HIDE CONSEQUENCE.** A reply's parent
// can leave this viewer's list without the reply leaving it: the owner hides the parent after the
// replies exist, or a block hides the parent's author. Dropping the orphans would silently delete
// members' visible words from the page and would also tell nobody anything; hoisting them to the top
// level keeps every word that passed the gates on screen, which is the room's standing bias.
export function nestComments(rows = []) {
  const list = (rows || []).filter(Boolean);
  const ids = new Set(list.map((r) => String(r.id)));
  const tops = [];
  const byId = new Map();
  for (const r of list) {
    const parent = r.parentId ?? r.parent_id ?? null;
    if (!parent || !ids.has(String(parent))) {
      const node = { ...r, parentId: parent && ids.has(String(parent)) ? String(parent) : null, replies: [] };
      tops.push(node);
      byId.set(String(r.id), node);
    }
  }
  for (const r of list) {
    const parent = r.parentId ?? r.parent_id ?? null;
    if (!parent || !ids.has(String(parent))) continue;
    const node = byId.get(String(parent));
    // A parent that is itself a reply cannot exist by `replyParentFor`, but if a row ever arrives
    // that way it joins the nearest thread it can rather than vanishing.
    if (node) node.replies.push({ ...r, parentId: String(parent) });
    else tops.push({ ...r, parentId: null, replies: [] });
  }
  return tops;
}

// COLLAPSED SENSIBLY WHEN LONG, expanding IN PLACE. Two replies stay on screen so a thread reads as
// a conversation rather than as a button; the rest are one tap away.
export const REPLIES_SHOWN_COLLAPSED = 2;

export function repliesToggleLabel(total, expanded = false) {
  const n = Number(total) || 0;
  if (n <= REPLIES_SHOWN_COLLAPSED) return "";
  if (expanded) return "Hide replies";
  const hidden = n - REPLIES_SHOWN_COLLAPSED;
  return `View ${hidden} more ${hidden === 1 ? "reply" : "replies"}`;
}

// THE LANDING LAW COMPOSES WITH THE COLLAPSE, and this is the half that is easy to miss: a member
// who taps a notification about the seventh reply in a thread must not arrive at a "View 5 more
// replies" button with her reply behind it. A wanted reply OPENS its thread.
export function repliesOpen({ replies = [], expanded = false, wanted = "" } = {}) {
  if (expanded) return true;
  const want = String(wanted || "");
  if (!want) return false;
  return (replies || []).some((r) => String(r?.id) === want);
}

export function visibleReplies(replies = [], open = false) {
  const list = replies || [];
  return open ? list : list.slice(0, REPLIES_SHOWN_COLLAPSED);
}

// ---------------------------------------------------------------------------
// ITEM 6 — **PAST SIX, THE COMMENTS GET THEIR OWN SCROLL AND THE COMPOSER STAYS REACHABLE.**
//
// HER ORDER: "Six comments, then the comment area gets its own inner scroll; composer stays
// reachable; panels-stay-open persistence respected."
//
// **WHAT IS COUNTED IS THE ROWS ON SCREEN, NOT THE TOP-LEVEL COMMENTS**, and that is the load-bearing
// reading of her sentence. A post with two comments and thirty visible replies is thirty-two rows
// tall and has exactly the problem she is describing; counting only the top-level two would leave it
// unscrolled. It also composes with the collapse rather than fighting it: a thread expanded past the
// threshold turns the scroll ON, and collapsing it turns the scroll back OFF, because both are
// answers to the same question — how tall is this area right now.
export const COMMENTS_BEFORE_SCROLL = 6;

// The rows this list will really draw: each thread's own row, plus the replies `visibleReplies` will
// actually render for it. It takes the open test as a PARAMETER rather than reading component state,
// so a gate RUNS it — and so the count can never disagree with what the component drew, because both
// ask the same function.
export function visibleCommentRows(threads = [], isOpen = () => false) {
  let rows = 0;
  for (const t of threads || []) {
    if (!t) continue;
    rows += 1;
    const replies = t.replies || [];
    rows += visibleReplies(replies, Boolean(isOpen(t))).length;
  }
  return rows;
}

// **A COUNT THAT IS NOT A NUMBER IS NOT A COUNT (R136/R137).** `Number("7")` is 7 and `Number(true)`
// is 1, so a coerced reading could switch a scroll box on over a value nobody measured. The failure
// direction is deliberate: with no reading there is NO scroll box, which is exactly today's behaviour
// — everything visible, nothing capped — so a bad input costs a long page rather than hidden comments.
export function commentScrollState(rowCount, { threshold = COMMENTS_BEFORE_SCROLL } = {}) {
  const limit = typeof threshold === "number" && Number.isFinite(threshold) && threshold > 0
    ? Math.trunc(threshold) : COMMENTS_BEFORE_SCROLL;
  if (typeof rowCount !== "number" || !Number.isFinite(rowCount)) return { rows: 0, threshold: limit, scrolls: false };
  const rows = Math.max(0, Math.trunc(rowCount));
  // **STRICTLY MORE THAN SIX.** Her words are "six comments, THEN it scrolls" — six is the last
  // number that fits, and capping at six would hide the sixth behind a scrollbar she never asked for.
  return { rows, threshold: limit, scrolls: rows > limit };
}

// ---------------------------------------------------------------------------
// LR14 — THE PANELS STAY OPEN. HER RULING: **THE ONE-AT-A-TIME ACCORDION DIES.**
//
// Every title she opens stays open until SHE closes it, as many at once as she wants, because the
// reasons a member opens two posts at the same time are comparing, copying and reading her own way —
// and an accordion answers all three by closing the thing she was looking at.
//
// THE OPEN SET IS A SET OF POST IDS AND NOTHING ELSE, which is what makes the rest arithmetic: new
// posts arrive collapsed because they are not IN it, and a notification tap ADDS to it without
// touching anything, so LR12-2's exact-landing law composes rather than fighting it. There is no
// "which one is open" state left to be wrong.
//
// IT IS STORED ON HER ACCOUNT, NOT ON THE DEVICE (`profiles.room_state`, the standing precedent), so
// the room she left on her phone is the room she finds on her desktop.
// ---------------------------------------------------------------------------

// A cap, because a set that only ever grows is a payload that only ever grows. The NEWEST ids are
// kept: the ones she opened least recently are the ones she has finished with.
export const OPEN_SET_LIMIT = 60;
export const COLLAPSE_ALL_LABEL = "Collapse all";

// Whatever the database hands back — an array, a stray object, null from an account that has never
// opened anything — becomes a clean list of id strings. The store is jsonb and jsonb has no schema,
// so the SHAPE is checked here rather than trusted, once, for every reader.
export function normalizeOpenSet(value) {
  const list = Array.isArray(value) ? value : [];
  const out = [];
  for (const v of list) {
    const id = String(v ?? "").trim();
    if (!id || out.includes(id)) continue;
    out.push(id);
  }
  return out.slice(-OPEN_SET_LIMIT);
}

// TAPPING A TITLE. Opening never closes anything else — that is the whole ruling in one line.
export function toggleOpen(current, id) {
  const set = normalizeOpenSet(current);
  const key = String(id ?? "").trim();
  if (!key) return set;
  return set.includes(key) ? set.filter((x) => x !== key) : normalizeOpenSet([...set, key]);
}

// THE LANDING LAW COMPOSES: a notification opens ITS target and leaves every other panel exactly as
// she left it. Separate from `toggleOpen` on purpose — a tap that arrives from outside must never be
// able to CLOSE the thing it was sent to find, which is what a toggle would do on a second tap of
// the same notification.
export function openWith(current, id) {
  const set = normalizeOpenSet(current);
  const key = String(id ?? "").trim();
  if (!key || set.includes(key)) return set;
  return normalizeOpenSet([...set, key]);
}

export function isOpen(current, id) {
  return normalizeOpenSet(current).includes(String(id ?? ""));
}

// ---------------------------------------------------------------------------
// >>> **HER RULING (2026-08-14): POSTS RENDER OPEN IN THE FEED BY DEFAULT.** <<<
//
// **THE SET IS THE SAME SHAPE AND MEANS THE OPPOSITE, AND THAT IS WHY IT GOT A NEW KEY RATHER THAN A
// NEW READING.** `livingRoomOpen` holds, for every member who has ever tapped a title, the ids she
// had OPEN. Re-interpreting that column as *closed* would take the three posts she was reading and
// close exactly those while opening everything else — **her stored state inverted in place, silently,
// for every member at once.** So `livingRoomCollapsed` is minted, the old key is retired at its
// declaration (DL-2/DT-2b/R181) and never read again: every member arrives with the whole feed open,
// which IS her ruling, and no row is misread.
//
// **AND IT IS THE SAME FOUR FUNCTIONS A THIRD TIME.** LR14 declared them, COMMENTS COLLAPSE applied
// them one fold down, and this applies them inverted — `normalizeOpenSet` / `toggleOpen` / `isOpen`
// carry the collapsed set UNCHANGED, because a set of post ids on her account, capped and normalised
// off jsonb, is one idea however it is read (R28/R29). What is NOT the same is the LANDING law, and
// that difference is the whole round: see `expandWith`.
//
// **THE CAP FAILS THE OTHER WAY NOW, AND THAT IS THE SAFE DIRECTION (R19).** `normalizeOpenSet` keeps
// the newest 60, so a member past the cap finds her OLDEST collapsed posts open again — visible, and
// one tap from being closed. Under the old reading the same cap re-CLOSED the oldest post she had
// opened, which is the direction that hides something she asked to see.
// ---------------------------------------------------------------------------

/** Whether a post's body draws. Her ruling in one line: not collapsed means open. */
export function postExpanded(collapsed, id) {
  return !isOpen(collapsed, id);
}

/**
 * **THE LANDING LAW, INVERTED — AND IT IS THE ONE THING THAT COULD NOT BE CARRIED OVER.** LR12-2's
 * clause is that a notification, a shared link or a search hit OPENS its target and touches nothing
 * else. On an OPEN set that is `openWith`, which ADDS; on a COLLAPSED set, adding is CLOSING THE
 * VERY POST SHE WAS SENT TO READ. So the landing removes.
 *
 * It is DERIVED from `toggleOpen` rather than written out, which is what makes the relationship
 * provable: a landing is exactly the half of a toggle that can only ever open something. Landing on
 * a post she has NOT collapsed changes nothing, so a second tap of the same notification cannot
 * close it — the same property `openWith` was split out for.
 */
export function expandWith(collapsed, id) {
  const set = normalizeOpenSet(collapsed);
  const key = String(id ?? "").trim();
  if (!key) return set;
  return isOpen(set, key) ? toggleOpen(set, key) : set;
}

/**
 * **COLLAPSE ALL, WHICH IS NOW THE WORK RATHER THAN THE UNDO.** Under open-by-default the convenience
 * she needs is the one that clears the screen, and its reverse is the empty set — so the control has
 * TWO labels and is always actionable. Only the posts really ON HER SCREEN are collapsed: a set that
 * tried to name the whole room would be unbounded and would be trimmed by the cap the moment it grew.
 */
export const EXPAND_ALL_LABEL = "Open all";
export function collapseAll(collapsed, ids) {
  return (Array.isArray(ids) ? ids : []).reduce((acc, id) => {
    const key = String(id ?? "").trim();
    return !key || isOpen(acc, key) ? acc : normalizeOpenSet([...acc, key]);
  }, normalizeOpenSet(collapsed));
}

// ---------------------------------------------------------------------------
// COMMENTS COLLAPSE (her `5`, 2026-08-11) — **THE COMMENTS ARE FOLDED UNTIL SHE ASKS FOR THEM.**
//
// HER ORDER: comments hidden by default under every post; a "Show comments (count)" line that drops
// them in place on a tap; the composer only when open; per-member persistence; tap again collapses.
//
// **THIS IS A SECOND FOLD NESTED INSIDE LR14's, AND IT IS THE SAME LAW APPLIED TWICE RATHER THAN A
// SECOND ONE WRITTEN BESIDE IT.** The set of posts whose comments she has open is exactly the shape
// of the set of posts she has open — a set of ids, on her account, capped, normalized off jsonb —
// so it is carried by `normalizeOpenSet` / `toggleOpen` / `openWith` / `isOpen` UNCHANGED. A second
// copy of those four would be a second answer to what an open set is (R28/R29), and the copy that
// drifts is the one that loses her panels.
//
// **THE LABEL HAS THREE STATES, NOT TWO (R18), AND THE MIDDLE ONE IS THE WHOLE REASON THIS IS A LAW
// AND NOT A TERNARY.** A post with NO comments still needs the line, because her *"composer only
// when open"* clause means the fold is the only door to the compose box: with the line hidden on
// zero, **no member could ever write the FIRST comment on a post** — silent, total, and it looks
// exactly like a quiet room. And *"Show comments (0)"* is a control that promises to show nothing.
// So zero-and-closed names what the tap really offers.
//
// **THE COUNT IS THE ROW'S OWN COUNT AND IS NEVER RE-DERIVED HERE.** The collapsed feed row already
// renders `post.comments`; the fold line is handed the same number, so the two can never disagree
// about how many comments a post has (R28/R29). It is deliberately NOT the length of the loaded
// comment list — while the fold is shut nothing is loaded, which is the point of shutting it.
// ---------------------------------------------------------------------------
export const COMMENTS_HIDE_LABEL = "Hide comments";
export const COMMENTS_FIRST_LABEL = "Add a comment";

export function commentsFoldLabel(count, open = false) {
  if (open === true) return { kind: "hide", text: COMMENTS_HIDE_LABEL };
  const raw = Number(count);
  const n = Number.isFinite(raw) ? Math.max(0, Math.trunc(raw)) : 0;
  if (n <= 0) return { kind: "first", text: COMMENTS_FIRST_LABEL };
  return { kind: "show", text: `Show comments (${n})` };
}

// THE LANDING LAW COMPOSES WITH THIS FOLD TOO, AND IT IS THE HALF A NAIVE BUILD BREAKS SILENTLY.
// Three paths send a member AT a comment — the room's search (a post whose match was inside a
// comment), a notification or shared link naming a comment, and one naming a REPLY — and a fold
// that starts shut hides the exact row she was sent to look at, with no error and nothing to tap
// that says so. This is `repliesOpen`'s own `wanted` shape one level up: the fold OPENS ITSELF for
// a wanted row and leaves her stored set alone, so a landing can never CLOSE something either.
export function commentsFoldOpen({ open = false, wanted = "", matched = false } = {}) {
  if (open === true) return true;
  if (matched === true) return true;
  return Boolean(String(wanted ?? "").trim());
}

// ---------------------------------------------------------------------------
// LR1-10 — NOTIFICATIONS. The four kinds she named, and the one line each renders as.
//
// The sentence is composed HERE rather than on the client so the in-house list and PN1's future push
// payload say the same thing. When push ships it reads `notificationText` and sends it; there is no
// second copy of the wording to drift.
// ---------------------------------------------------------------------------
// ITEM 8 added `post`, and it is the widest notification this room has: one act by one member
// reaches everybody who follows her. **HER RULE IS PART OF THE ROSTER: A POST OR A SHARED SPELL, AND
// NEVER A COMMENT OR A REPLY** — there is deliberately no kind here that a comment could be filed
// under, so the rule is kept by the roster rather than by whoever writes the next fan-out.
//
// R183 — **THE ROSTER IS EXPORTED AND THE MEMBERSHIP PREDICATE IS NOT, AND THAT IS THE REPAIR.**
// This file used to carry `notificationValid`, a third spelling of a question `src/notifications.js`
// already answers twice (`isLivingRoomKind`, live, and `notificationKindValid`, which is what
// `server/db/living-room.js` really guards a write with). R165 filed it as R38's trap with the
// reason that *a kind this one accepts and the house one refuses is a real disagreement*.
// **Measured, that class is EMPTY BY CONSTRUCTION** — `LIVING_ROOM_KINDS` is spread FROM this very
// roster and the house roster is the union, so the house one can never refuse what this one accepts.
// The real disagreement runs the other way and is live with three kinds (`generation`, `grant`,
// `house`), which is a narrower predicate wearing a wider name. Deleted rather than left standing
// beside its replacement (DL-2, DT-1c, DT-2b, R181).
// >>> **`everyone` JOINED THIS ROSTER ON 2026-08-14, AFTER HER DAUGHTER RECEIVED A BLANK ONE.** <<<
//
// `notifyEveryone` has written `kind = 'everyone'` since migration 086 — and this roster has never
// contained it, so `notificationKindValid` answered FALSE, `houseNotificationText` fell through to
// its empty-string default, and **every member of this house who was tagged by an @everybody call got
// a notification with no words in it.** The default is deliberately empty (R183: a plausible sentence
// for an unrostered kind is exactly what defeats the guard), so nothing was wrong with the guard —
// what was missing is the kind, on the one roster every other law reads.
//
// **THE HOUSE ROSTER RE-EXPORTS THIS ONE RATHER THAN RE-TYPING IT** (`LIVING_ROOM_KINDS = [...LR_KINDS]`),
// so adding it here is what makes the sentence, the target, the validity test and the push preference
// all agree at once — which is the whole reason that re-export exists.
export const NOTIFICATION_KINDS = ["mention", "reply", "message", "like", "post", "everyone"];

// ---------------------------------------------------------------------------
// N1 (her queue item 5, 2026-08-11) — **A LIKE NOTIFICATION SAYS WHAT WAS LIKED, AND LIKES ON ONE
// THING ARE ONE NOTIFICATION.**
//
// **HER TWO CLAUSES, AND THE MEASUREMENT THAT SIZED THEM.** On the live table: 1,702 like rows, of
// which **1,546 sit in 222 groups that are already more than one row** — one member's single post
// carries **75 separate like notifications**. And `notificationText`'s `like` arm was the ONE arm of
// the five with no `${tail}` on it, so a like has never said what it was about even on the rows that
// carry an excerpt.
//
// **HER CLAUSE 3 IS WHY THE COUNT IS A PARAMETER RATHER THAN A NEW SENTENCE.** *"Messages and every
// other notification kind stay exactly as they are"* — so the four other arms below are byte-identical
// to what they were, the tail is the SAME shape they already use, and the only thing that reads
// `count` is the `like` arm.
// ---------------------------------------------------------------------------

/** The plural rule, so the sentence is composed once rather than at each caller. */
export const likedByLine = (count) => (Number(count) > 1 ? `${Number(count)} people liked what you said.` : null);

/**
 * **THE SNIPPET, CLEAN-TRUNCATED, ONE LINE.** Her example is *"Welcome home family..."*.
 *
 * "One line" is the load-bearing half and it is done FIRST: a post body carries newlines, and a bell
 * row is one line of a list — an excerpt with a newline in it either breaks the row's height or is
 * silently collapsed by the browser at a place nobody chose. Whitespace is collapsed before anything
 * is measured, so the length that is cut is the length that will be drawn.
 *
 * **IT CUTS AT A WORD BOUNDARY, and falls back to a hard cut rather than to the whole string** — a
 * body with no spaces in it (a URL, a wall of emoji) has no boundary to find, and returning it whole
 * because the search failed is the direction that puts an unbounded string in her bell.
 */
/**
 * **MG4 — `max` IS A CEILING ON WHAT COMES BACK, WHICH IS WHAT IT SAYS AND WHAT IT WAS NOT.** The
 * ellipsis was appended AFTER the budget was spent, so a string with no word boundary in it came
 * back at `max + 1` — and N1's own fixture wrote that overrun into its tolerance (`<= MAX + 1`,
 * twice) rather than refusing it. On a bell row one character over is invisible; MG4 reads this law
 * through `PUSH_BODY_MAX`, where the gate refuses a body longer than the cap, so **a morning whose
 * line had no space in its first 72 characters would have been refused and NOBODY would have got
 * it.** The tolerance is not widened, the law is fixed (R216: a cap that is not a cap is a line no
 * reader can trust), and it is `groupMessageTitle`'s own established pattern in this house.
 *
 * **AND DROPPING WHOLE CHARACTERS RATHER THAN SLICING CLOSES A SECOND HOLE FOR FREE.** `[...line]`
 * for the same reason the circle's medallion takes its initial that way — half a surrogate pair is
 * not a character — while the budget is still counted in the UNITS `pushShapeViolations` measures,
 * because an emoji costs two of those where `[...s]` counts one. Nothing without an astral character
 * moves by this.
 */
export const LIKE_SNIPPET_MAX = 60;
export function likeSnippet(text, { max = LIKE_SNIPPET_MAX } = {}) {
  const line = String(text || "").replace(/\s+/g, " ").trim();
  if (!line) return "";
  if (line.length <= max) return line;
  const chars = [...line];
  let kept = chars.slice(0, max - 1);
  while (kept.length && `${kept.join("")}…`.length > max) kept = kept.slice(0, -1);
  const cut = kept.join("");
  const space = cut.lastIndexOf(" ");
  // A boundary in the first third is not a boundary worth keeping — it would answer one word and an
  // ellipsis for a long first token, which tells her less than a hard cut does.
  const head = space > max / 3 ? cut.slice(0, space) : cut;
  return `${head.replace(/[\s.,;:!?-]+$/, "")}…`;
}

export function notificationText({ kind, actorName = "Someone", excerpt = "", count = 1 } = {}) {
  const tail = excerpt ? ` "${String(excerpt).slice(0, 80)}"` : "";
  if (kind === "mention") return `${actorName} tagged you.${tail}`;
  // **IN THE ROOM IT SAYS WHICH KIND OF TAG IT WAS, AND ON A COLD SCREEN IT DOES NOT.** This file's
  // own header states the split: LR1's lines are what the ROOM renders beside an avatar, where she is
  // already standing in the Living Room; `houseNotificationText` is what a phone shows cold, and her
  // law fixes THAT one word for word (*"Lala tagged you in the Living Room"*).
  //
  // **AND THE TWO LINES MUST DIFFER, WHICH IS THIS ROSTER'S OWN STANDING LAW** — every kind says
  // something different, so none can fall through to the catch-all unnoticed. A first draft made this
  // `mention`'s sentence exactly and LR1's own fixture went red about it, correctly.
  if (kind === "everyone") return `${actorName} tagged everyone.${tail}`;
  if (kind === "reply") return `${actorName} replied to you.${tail}`;
  if (kind === "message") return `${actorName} sent you a message.${tail}`;
  // N1 — **HER TWO SENTENCES, AND THE TAIL IS NOW ON BOTH.** One liker is named; more than one is a
  // number, because *"@Renee and 26 others"* is a sentence nobody asked for and her own wording is
  // *"27 people liked what you said"*. The count is READ rather than derived here: it is the item's
  // real `like_count` as of the last like, written onto the row by the store (R11/R31 — one place
  // keeps that number).
  if (kind === "like") return `${likedByLine(count) || `${actorName} liked what you said.`}${tail}`;
  // HER COPY, WORD FOR WORD. It names the room because a follow notification is the one kind that
  // does not arrive from a thing of hers — she is not being answered, she is being told somebody she
  // follows has spoken.
  if (kind === "post") return `${actorName} posted in ${ROOM_TITLE}.`;
  return `${actorName} did something.`;
}

// WHERE A TAP LANDS (her order: "the exact post/comment/thread"). One function, so the in-house list
// and the push payload cannot disagree about the destination.
export function notificationTarget(n = {}) {
  if (n.threadId) return { kind: "thread", id: String(n.threadId), messageId: n.messageId ? String(n.messageId) : null };
  if (n.postId) {
    const commentId = n.commentId ? String(n.commentId) : null;
    // LR13 — THE TARGET GROWS A REPLY ID, IN ONE PLACE, exactly the way it grew a comment id. A
    // reply's notification row names THE REPLY in `commentId`, because a reply IS a comment; the
    // thread it lives inside is its parent. A landing needs BOTH — the parent to expand the
    // collapsed thread, the reply itself to scroll to and mark — so the subject is split here and
    // nowhere else, and both carriers read the URL derived from it.
    const parent = n.parentCommentId ? String(n.parentCommentId) : null;
    if (commentId && parent) return { kind: "post", id: String(n.postId), commentId: parent, replyId: commentId };
    return { kind: "post", id: String(n.postId), commentId, replyId: null };
  }
  return { kind: "room", id: "", commentId: null, replyId: null };
}

// ---------------------------------------------------------------------------
// LR12-2 — WHERE THAT TARGET ACTUALLY GOES, AS A URL. HER LAW WITH TEETH: EVERY TAP LANDS DIRECTLY
// ON ITS SUBJECT, AND "THE ROOM, UNSCROLLED" IS A FAILING STATE.
//
// **MEASURED BEFORE THIS EXISTED, AND IT WAS WORSE THAN THE NOTE THAT ORDERED THE WORK.** The note
// said the URL did not yet carry the comment id. In fact `notificationTarget` returned no URL AT ALL
// for a Living Room kind, and `pushPayloadFor` reads `target.url || "/"` — so **every Living Room
// push landed on the HOME PAGE.** Not the comment, not the post, not even the room. Meanwhile the
// bell built its own URL by hand in `HouseInbox.tsx`, which is the second copy this house keeps
// finding: two carriers, one of them silently pointing at the front door.
//
// So the URL is DERIVED from the target here (R31), once, and both carriers read it. A tap from the
// lock screen and a tap on the bell are then the same landing by construction rather than by two
// people remembering the same query string.
//
// The path is typed here because this module is imported by the SERVER and `src/lib/pages.ts` is
// TypeScript, which a Node server cannot import. That crossing is therefore held by the fixture
// against `PAGE_PATHS` by import (R13/R14/R15), not by hoping the two strings stay equal.
export const LIVING_ROOM_PATH = "/living-room";

/** TRAP-1b's marker, declared once and read by the composer AND by the arrival (R28/R29). */
export const TAG_ARRIVAL_PARAM = "tag";

/**
 * Did this member arrive from an @everybody tag? `search` is `window.location.search`.
 *
 * **PRESENCE, NEVER TRUTHINESS (R53/R136/R137).** A query value is always a STRING and
 * `Boolean("false")` is `true` — the coercion that once cast a whole chart at the equator — so a
 * `?tag=0` must not open a sales panel at somebody who explicitly said no.
 */
export function arrivedFromTag(search) {
  const raw = String(search || "");
  if (!raw) return false;
  try {
    return new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw).get(TAG_ARRIVAL_PARAM) === "1";
  } catch { return false; }
}

export function livingRoomUrl(target = {}) {
  const kind = String(target.kind || "");
  const id = String(target.id || "");
  // A THREAD IS NOT A PLACE ON A PAGE, and that is why it needs a URL of its own. The bell opens the
  // inbox panel in place; a push has nothing but a URL to work with, so a DM lands on the room with
  // the thread named and the inbox opens itself on arrival. Without this a message push landed on
  // "/" and the member was handed the front door in answer to a person talking to her.
  if (kind === "thread" && id) {
    const q = `thread=${encodeURIComponent(id)}`;
    return target.messageId ? `${LIVING_ROOM_PATH}?${q}&message=${encodeURIComponent(String(target.messageId))}` : `${LIVING_ROOM_PATH}?${q}`;
  }
  if (kind === "post" && id) {
    const parts = [`post=${encodeURIComponent(id)}`];
    // >>> TRAP-1b — **THE @EVERYBODY TAG MARKS ITS OWN LANDING, AND THAT IS WHAT THE VELVET ROPE
    // TURNS ON.** <<< Her clause splits the landing BY ACCESS, and the split may only fire on a tag
    // arrival: a bare `?post=` is also what a SHARED LINK composes (LR12-2/the shared-card round),
    // so reading the rope off the post id alone would put a sales panel in front of a member a friend
    // sent a link to. The marker is on the URL because a URL is all a push carries — and it is
    // ADDITIVE, so every link already out in the world behaves exactly as it does today (R68's
    // vanished-anchor lesson one control over: this house has already had every unsubscribe link in
    // somebody's inbox depend on a subject spelling never changing).
    if (target.commentId) parts.push(`comment=${encodeURIComponent(String(target.commentId))}`);
    // LR13: the reply rides BESIDE its parent, never instead of it. A `reply` with no `comment` would
    // name a row the page cannot find, because the thread it lives in would still be collapsed.
    if (target.commentId && target.replyId) parts.push(`reply=${encodeURIComponent(String(target.replyId))}`);
    // >>> AND THE MARKER GOES LAST, WHICH R183's CENSUS IS WHAT FOUND. <<< Written first it landed
    // BETWEEN the post and the comment — `?post=1406&tag=1&comment=77` — so a fact about ACCESS was
    // sitting inside the address of the ROW, which is two different things reading as one string.
    // Last, the row's own address is byte-identical to what it has always been and the marker is a
    // suffix on it (R18).
    if (target.tagged === true) parts.push(`${TAG_ARRIVAL_PARAM}=1`);
    return `${LIVING_ROOM_PATH}?${parts.join("&")}`;
  }
  return LIVING_ROOM_PATH;
}

// A member is never notified about her own act. Stated once, here, because it is the check that gets
// forgotten at the fourth call site and turns a bell into noise.
export function shouldNotify({ actorRef, recipientRef, visibility = VISIBILITY.PUBLIC } = {}) {
  if (!recipientRef || String(actorRef) === String(recipientRef)) return false;
  // AND THE SHADOW-HIDE CONSEQUENCE: held content notifies NOBODY. A notification about a post only
  // its author can see would tell a third party the post exists, which breaks the ruling from the
  // outside — the one direction that is easy to miss when the author's own view is what you are
  // thinking about.
  return visibility === VISIBILITY.PUBLIC;
}

// ---------------------------------------------------------------------------
// LR1-8 — GROUP THREADS. A conversation is a SET of members, and the set is its identity.
// ---------------------------------------------------------------------------
export const MAX_THREAD_MEMBERS = 12;

// The stable key for a set of participants: sorted numerically, comma-joined. Sorting NUMERICALLY
// and not lexically matters — "10,9" and "9,10" are the same conversation, and a lexical sort would
// file them as two.
export function participantKey(ids) {
  const clean = [...new Set((ids || []).map((i) => Number(i)).filter((n) => Number.isFinite(n) && n > 0))];
  return clean.sort((a, b) => a - b).join(",");
}

// What a thread is called when nobody named it: the other people in it, by room first name.
// LAUNCH DAY (2026-08-05) — **A THREAD ALWAYS SHOWS THE OTHER PERSON, AND NEVER THE VIEWER.**
//
// HER REPORT: her thread with her dad David showed HER OWN PICTURE and the label "You", with his
// messages inside it. Every older thread rendered correctly.
//
// **THE CAUSE IS NOT THE ONE THE SYMPTOM SUGGESTS, AND THE MEASUREMENT IS WHY THIS IS A LAW.** The
// obvious reading is a fresh signup with no room name — David never named his room — but Tina and
// Eria BOTH have an empty `living_room_name` AND an empty `room_state->>'roomName'` and both render
// correctly. Read against her live rows, the discriminator is `lr_thread_members.left_at`: David's
// membership row carries one while his message is still in the thread. `threadParticipants` filters
// `left_at IS NULL`, so the array came back holding ONLY HER — and then the inbox row's
// `participants.find((p) => p.ref !== me.ref) || participants[0]` fell through its `find` and
// **returned the viewer herself**, which is her face; and this function's `others` was empty, which
// is "Just you".
//
// So the repair is one predicate rather than three call sites: NOTHING may resolve "the other
// person" to the viewer. A fallback that can return the viewer is not a fallback, it is a different
// answer to a different question (R18), and its failure has no error state — a thread wearing your
// own face looks like a design choice.
//
// **A DEPARTED COUNTERPART IS STILL THE COUNTERPART.** His messages are in the thread and she is
// reading them, so the honest face is his. A live participant is preferred over one who left only so
// a group thread names somebody who is actually there.
export function otherParticipant(participants = [], viewerRef = "") {
  const others = (Array.isArray(participants) ? participants : [])
    .filter((p) => p && String(p.ref) !== String(viewerRef));
  return others.find((p) => !p.left) || others[0] || null;
}

// Every other person in the thread, viewer excluded, in the same order — the list form of the law
// above, so a group's participant line and a one-to-one's face can never disagree about who is here.
export function otherParticipants(participants = [], viewerRef = "") {
  return (Array.isArray(participants) ? participants : [])
    .filter((p) => p && String(p.ref) !== String(viewerRef));
}

// LR18 — **WHAT A GROUP IS, DECLARED ONCE.** Three or more people in one thread, and that is the whole
// definition: a one-to-one is exactly two. It is a law rather than a `> 2` at each call site because
// four separate places already ask it — the receipt's own `group` flag (`receiptLabel` takes one and
// every caller computes it by hand), the title's fallback, the inbox pass's silence, and the routes —
// and four spellings of *is this a group* is four answer keys for the one fact her whole LR18 spec is
// about (R28/R29). It counts people who are HERE: a member who left is not in the room any more, which
// is `otherParticipant`'s own ruling one function up.
export const GROUP_MIN_MEMBERS = 3;
// **ITEM 8 (HER RULING) SUPERSEDES THE COUNT THIS USED TO MAKE, AND THE OLD ONE WAS THE DEFECT.**
// It counted who is HERE, so a three-person group dropping to two STOPPED BEING A GROUP: it fell out
// of her circle, lost its Leave control, and started drawing itself in the one-to-one form — which is
// exactly what LR18's clause-6 walk found and left standing for her word. Her word is that **the group
// remains a working group for everyone still in it no matter how few remain.**
//
// So the question is no longer *how many are here*, it is **was this ever a group** — a property of
// the thread rather than of today's membership, which is what makes it stable across every leave. A
// member who left is still counted for that purpose and for that purpose only; every SURFACE that
// lists people filters her out (`currentMembers`), because her other clause is that the member list
// shows only current members.
export function isGroupThread(participants = []) {
  const all = Array.isArray(participants) ? participants.filter(Boolean) : [];
  return all.length >= GROUP_MIN_MEMBERS;
}

/**
 * **A STANDING GROUP IS A GROUP HOWEVER FEW ARE IN IT.**
 *
 * Elite 2026 found this the day it shipped: on its first morning it held her seat and the one elite
 * member who had signed up, so `isGroupThread` — which counts people — correctly said *two*, and her
 * class rendered as a DM with no members dropdown and no leave control. Item 8's ruling was that a
 * group STAYS a group as people leave; this is the same ruling at the other end, as people ARRIVE.
 *
 * A thread with a SLUG is a standing room somebody declared, never a conversation that happened to
 * form, so its groupness is a property of what it IS rather than of how many are in it today. Asked
 * as ONE function so no surface can answer it differently (R28/R29).
 */
export function threadIsGroup({ participants = [], slug = "" } = {}) {
  return Boolean(slug) || isGroupThread(participants);
}

/** Her clause: the list shows only who is still in it. Everything they CONTRIBUTED stays regardless. */
export function currentMembers(participants = []) {
  return (Array.isArray(participants) ? participants : []).filter((p) => p && !p.left);
}

// LR18-1 — **THE GROUP'S NAME, AS A SHAPE LAW AND NEVER AS MODERATION** (`postComposeViolations`'
// own ruling, one surface over). This says what is MISSING or what is too long; whether the words are
// allowed is the community gate's question and is asked separately at the route, and whether somebody
// already has a group by that name is the database's.
//
// **A NAME IS OPTIONAL HERE AND THAT IS THE HALF LR18b OWNS.** Her clause 1 puts the requirement on
// the COMPOSE surface — *"selects MULTIPLE members ... and NAMES the group"* — and no compose sends a
// title yet, so requiring one at the route would refuse every group this site can currently create,
// which is R19's forbidden direction on a write. What this law does hold today is that a name a member
// DID type is a real one.
export const GROUP_NAME_MAX = 60;
export function groupNameViolations({ name = "", raw = undefined } = {}) {
  const hits = [];
  // The distinction R18 keeps everywhere: a member who typed nothing has not named the group, and a
  // member who typed only spaces HAS tried and is owed a sentence rather than a silent drop.
  const typed = raw === undefined ? name : raw;
  if (String(typed || "").length > 0 && !String(name || "").trim()) hits.push("blank");
  const trimmed = String(name || "").trim();
  if (trimmed.length > GROUP_NAME_MAX) hits.push("too-long");
  if (/[\r\n]/.test(trimmed)) hits.push("multi-line");
  return hits;
}

// One sentence per violation, in the room's register: it says what to do rather than what she did.
export function groupNameRefusal(hits = []) {
  if (hits.includes("blank")) return "Give the group a name, or leave it unnamed and it takes the names of the people in it.";
  if (hits.includes("too-long")) return `Group names go up to ${GROUP_NAME_MAX} characters. Shorten it and try again.`;
  if (hits.includes("multi-line")) return "A group name is one line.";
  return "";
}

// LR18-1 — the uniqueness sentence, declared beside the shape law rather than at the route, because a
// member reads it and it is the only place this house says what a duplicate group name means. It is
// PER CREATOR, her word: two different members may each have a group called "Sunday".
export const GROUP_NAME_TAKEN = "You already have a group with that name. Pick another and this one stays where it is.";

// The one sentence for a conversation that cannot hold another person. It was typed at TWO sites in
// `server/living-room-routes.js` (the start door and the add door) and LR18b needs a THIRD reader —
// the compose, which must refuse a twelfth pick before it costs her the whole send (R15). Three
// spellings of one ceiling is three answer keys for a number that is already declared once above
// (R28/R29), so the sentence is DERIVED from `MAX_THREAD_MEMBERS` here and read everywhere.
export function threadFullMessage() {
  return `A conversation holds up to ${MAX_THREAD_MEMBERS} people.`;
}

// LR18b-1 — **THE COMPOSE'S OWN LAW: WHO SHE PICKED, AND WHETHER THIS ONE NEEDS A NAME.**
//
// 8a put the name at the ROUTE and left it OPTIONAL there, with the reason written into
// `groupNameViolations` above: her clause 1 puts the REQUIREMENT on the compose surface, and no
// compose sent a title, so demanding one at the route would have refused every group this site could
// then create (R19's forbidden direction on a write). This is that half. The route stays exactly as
// it is — a DM still needs no name and an unnamed group is still a legal row, because `threadTitle`
// has always fallen back to the people in it and other doors (a member ADDED to a two-person thread)
// make a group without passing through this surface at all.
//
// **WHETHER THIS IS A GROUP IS ASKED OF `isGroupThread` RATHER THAN COUNTED HERE**, which is R31 on
// the one fact her whole LR18 spec is about: a second `>= GROUP_MIN_MEMBERS` on the client is exactly
// the third answer key 8a spent its round abolishing on the server. The viewer is one of the people
// in the thread she is composing, so she is counted in.
export function composePicks(refs = []) {
  const list = Array.isArray(refs) ? refs : [refs];
  // Deduped, because two taps on one row must not spend two of her twelve seats — and because the
  // ceiling below is the one thing between her and a refusal she cannot see coming.
  return [...new Set(list.map((r) => String(r ?? "").trim()).filter(Boolean))];
}

export function composeIsGroup(refs = []) {
  const picked = composePicks(refs);
  if (!picked.length) return false;
  return isGroupThread([{ ref: "viewer" }, ...picked.map((ref) => ({ ref }))]);
}

export function groupComposeViolations({ refs = [], name = "", raw = undefined } = {}) {
  const picked = composePicks(refs);
  const hits = [];
  if (!picked.length) hits.push("nobody");
  if (picked.length + 1 > MAX_THREAD_MEMBERS) hits.push("full");
  const group = composeIsGroup(picked);
  const named = String(name ?? "").trim();
  if (group && !named) hits.push("name-required");
  // The SHAPE law is reached rather than retyped. Its `blank` is R18's distinction for a surface
  // where a name is OPTIONAL — *she typed only spaces, so she tried* — and on a group that fact is
  // already carried by `name-required`, which says the stronger thing. Two sentences for one mistake
  // is how a form comes to argue with itself.
  for (const hit of groupNameViolations({ name, raw })) {
    if (hit === "blank" && group) continue;
    hits.push(hit);
  }
  return hits;
}

// LR18b-2 — **HER CLAUSE 2: THE GROUP IN HER CIRCLE.** The strip across the top of her inbox is the
// follow graph's faces; a group belongs beside them, and it belongs FIRST — not by taste, but because
// a group is the only thing in that strip with no other door. A person is one tap from every @name
// this room prints; a group thread is reachable from the inbox list and from here, and nowhere else.
//
// The order WITHIN the groups is the thread list's own (pinned first, newest first, the server's
// ORDER BY) rather than a second sort here, which would be a second answer to what "recent" means.
export function circleGroups(threads = []) {
  return (Array.isArray(threads) ? threads : []).filter((t) => t && t.group === true);
}

// **WHAT A GROUP WEARS WHERE A PERSON WEARS A FACE.** Two kinds, and the difference is R18's: a title
// somebody TYPED is a name and its initial is a real badge, while a title the house COMPOSED out of
// the people in it is not — an initial taken from "M2, M3" is the first letter of somebody's name
// wearing the group's badge. So an unnamed group takes the house glyph and says nothing it cannot
// stand behind. `[...title]` rather than `title[0]`, because a title may open on an emoji or on any
// character outside the basic plane and half a surrogate pair is not a letter.
export function groupMedallion({ title = "", named = false } = {}) {
  const trimmed = String(title ?? "").trim();
  if (!named || !trimmed) return { kind: "glyph", text: "" };
  const first = [...trimmed][0] || "";
  return first ? { kind: "initial", text: first.toUpperCase() } : { kind: "glyph", text: "" };
}

// LR18b-3 — **HER CLAUSE 4: LEAVING A GROUP, AND WHY THIS CONTROL IS ALLOWED TO EXIST WHEN S6-5
// KILLED ITS TWIN.** Her S6-5 ruling removed the LEAVE control from a thread because *"it destroyed
// the whole conversation and thread while reading like a navigation exit, one confused tap from a
// member losing their history"*, and named BLOCK as the path for a DM. **A GROUP IS THE ONE SHAPE
// THAT RULING DOES NOT COVER:** blocking somebody does not take you out of a room with five other
// people in it, and her own clause 4 asks for this control by hand.
//
// What makes it honest rather than the thing she killed is the ASK. The DM control posted on one tap;
// this one raises a confirm that says what leaving costs, in the room's own words, and the confirm's
// own button DECLARES itself destructive rather than wearing a door.
//
// **THE SENTENCE NAMES THE GROUP, THE LOSS AND WHAT SURVIVES**, because a confirm that
// under-describes what it destroys is R35's defect with better manners (LR13's ruling, one confirm
// over). It is a law rather than a literal at the sheet: it is the only place this house says what
// leaving a group means, and it has to agree with what the route really does — which is to mark her
// membership row and touch nothing else.
export function leaveGroupSentence(title = "") {
  const name = String(title ?? "").trim();
  const which = name ? `“${name}”` : "this group";
  return `Leave ${which}? It goes from your inbox and you stop receiving it. The others keep the conversation.`;
}
export const LEAVE_GROUP_LABEL = "Leave this group";
export const LEAVE_GROUP_CONFIRM = "Leave the group";
export const LEAVE_GROUP_KEEP = "Stay in it";
// R18/R39 — the route answers `{ ok: true, left }`, so `ok` is not the answer: a member who was not
// in that conversation gets a 200 saying so. The sheet reads `left`, and this is the sentence for the
// case where nothing happened — never the success path's silence, which is what would let a refused
// leave close the thread and reload the list exactly as a real one does (D-1/R83's own defect).
export const LEAVE_GROUP_NOT_IN_IT = "You are not in that conversation.";

// One sentence per violation, in the order the law asks them, so the surface answers the first and
// never has to know the precedence (`messageGateRefusal`'s own shape, one door over).
export function groupComposeRefusal(hits = []) {
  const list = Array.isArray(hits) ? hits : [hits];
  if (list.includes("nobody")) return "Pick who you are writing to.";
  if (list.includes("full")) return threadFullMessage();
  if (list.includes("name-required")) return "Give this group a name. Everyone in it sees it.";
  return groupNameRefusal(list);
}

export function threadTitle({ title, participants = [], viewerRef = "" } = {}) {
  if (title) return String(title);
  const others = otherParticipants(participants, viewerRef).map((p) => p.name);
  // "Just you" is now reachable ONLY by a thread that really holds nobody else — her thread 8, where
  // she is the single member row. It is no longer what a departed counterpart looks like.
  if (!others.length) return "Just you";
  if (others.length <= 3) return others.join(", ");
  return `${others.slice(0, 2).join(", ")} and ${others.length - 2} more`;
}

// LR1-8's hard rule: BLOCK STILL ABSOLUTE — a blocked member can NEVER share a thread with the
// blocker, checked when a thread is created AND when anyone is added, because a group is the one
// place a block could otherwise be routed around by a third party doing the inviting.
//
// **THAT LAW IS NOT HERE, AND R181 DELETED THE HELPER THAT SAID IT WAS.** `threadBlockConflict`
// carried the paragraph above and a body that asked `isBlockedBetween(viewer, ref)` — the VIEWER's
// own pairs and nothing else — so the exact case its own comment names (a third party inviting a
// blocker and a blocked member separately) walked straight through it. It also claimed to be checked
// at both doors and had never been called by anything. A client CANNOT hold this law: a viewer
// context carries her own `blocked`/`blockedBy` sets and no third party's, so every pair in a set is
// a question only the database can answer. It is asked as `blockWithinSet` (server/db/living-room.js)
// at BOTH doors, over the WHOLE participant set, and `scripts/r181-group-block.mjs` runs it.

// ---------------------------------------------------------------------------
// LR1-16 — MEMBER OWNERSHIP. EDIT AND DELETE, THE AUTHOR'S, ALWAYS.
//
// Two laws, and they are opposite in temperament on purpose. EDIT is careful: it re-passes the gates
// and cannot replace approved words with unapproved ones. DELETE is blunt: it is immediate, real,
// and needs no permission from anything.
// ---------------------------------------------------------------------------

// Who may edit or delete? THE AUTHOR, and the owner for delete only. Deliberately narrow, and
// deliberately one function, so no route can invent a wider answer. `mine` comes off the server's
// own comparison of user ids; it is never a client claim.
export function canEdit(item, viewer = {}) {
  if (!item || item.deleted || item.pending) return false;
  return Boolean(item.mine) && !viewer.silenced;
}
// TONIGHT-2 — **A ROW THAT HAS NOT LANDED HAS NOTHING TO ACT ON.** An optimistic row wears a
// namespaced local id (`pending-…`) that names no row anywhere, so every control on it — the like,
// the bin, the pencil, Reply, the flag — is a button that posts an id the server resolves to
// `null` and answers "not here" about (R136/R137's coercion, from the client side). That is R25's
// argument inverted: an absent control has no error state, and a control that is PRESENT and does
// nothing is worse, because she presses it and learns to distrust the ones that work.
//
// It is ONE law rather than a clause inside each surface's row, because S1 shipped the pending row
// for DMs and this ships it for comments — the capability living only where it was needed first is
// exactly the disease this house has paid for six times (W12/R26/R35/R38), and here both surfaces
// draw a bin from the same `canDelete`.
export function canActOn(item) {
  return Boolean(item) && !item.pending;
}

export function canDelete(item, viewer = {}) {
  if (!item || item.deleted || item.pending) return false;
  // HER SEAT CAN DELETE ANYTHING (ledgered) — but that is the OWNER path, and it stays distinct from
  // the author's. The author's delete is hers whether or not she is silenced: a strike takes away
  // posting, never the right to take her own words back.
  return Boolean(item.mine) || Boolean(viewer.owner);
}

// WHICH TEXT THIS VIEWER SEES. The whole two-body model, in one function, so every surface that
// renders a body goes through it and no surface can accidentally show a failing draft to the room.
//   • the AUTHOR sees her pending draft if there is one, otherwise the public body;
//   • EVERYONE ELSE sees the public body, always.
export function visibleBody(item, viewer = {}) {
  if (!item) return "";
  const isAuthor = Boolean(item.mine) || String(item.authorRef ?? "") === String(viewer.ref ?? "");
  if (isAuthor && item.draftBody) return item.draftBody;
  return item.body || "";
}

// The marker. House-styled, small, and NO PUBLIC HISTORY — her ruling. It says an edit happened and
// nothing about what changed, which is the whole of what a reader is owed.
export const EDITED_MARK = "edited";
export function editedMarker(item) {
  return item?.editedAt ? EDITED_MARK : "";
}

// What a deleted item leaves behind, per kind. A post and a comment simply go; a DM leaves an HONEST
// tombstone, because the other people in the thread already read it and silently rewriting their
// history would be a lie told by the software.
export const DELETED_MESSAGE_TOMBSTONE = "This message was deleted.";
export function deletionOutcome(kind, { replies = 0 } = {}) {
  if (kind === "post") {
    return {
      // THE STANDARD FORUM TRUTH, and she named it: others' comments go with the post. Their words
      // were replies, not standalone posts, and a thread of answers to a question nobody can read is
      // not something anybody is better off keeping.
      cascadesComments: true,
      tombstone: null,
      sentence: "Delete this post. It goes, and the replies to it go with it. This cannot be undone.",
    };
  }
  if (kind === "comment") {
    // LR13 — THE POST'S FORUM TRUTH, ONE LEVEL DOWN. A comment carrying replies takes them with it,
    // and the confirm SAYS the number: R35's whole finding is that a destroy she cannot read is worse
    // than no confirm at all, and a confirm that under-describes what it destroys is the same defect
    // wearing better manners. A childless comment keeps the sentence it has always had.
    const n = Number(replies) || 0;
    if (n > 0) {
      return {
        cascadesComments: true,
        tombstone: null,
        sentence: `Delete this reply. The ${n} ${n === 1 ? "reply" : "replies"} under it ${n === 1 ? "goes" : "go"} with it. This cannot be undone.`,
      };
    }
    return { cascadesComments: false, tombstone: null, sentence: "Delete this reply. It goes on its own. This cannot be undone." };
  }
  if (kind === "spell") {
    return {
      cascadesComments: true,
      tombstone: null,
      // HER PERSISTENCE LAW, said out loud in the confirm: a save is a save. What other members
      // already took home stays theirs, and the counter stops where it stands.
      sentence: "Delete this spell. It leaves the room, but the copies members already saved stay theirs and the count stops where it is. This cannot be undone.",
    };
  }
  return { cascadesComments: false, tombstone: DELETED_MESSAGE_TOMBSTONE, sentence: "Delete this message. The others will see that you deleted it." };
}

// An edit is still content, so it still has to be sayable. Length only — the GATES judge the words,
// and this judges the shape, which is the same split as the composer's.
export function editViolations(text, kind = "post") {
  const s = String(text ?? "").trim();
  const cap = kind === "comment" ? LIMITS.commentChars : kind === "message" ? LIMITS.messageChars : LIMITS.postChars;
  if (!s) return ["An edit cannot be empty. Delete it instead."];
  if (s.length > cap) return [`That is longer than the room takes. ${cap.toLocaleString("en-US")} characters.`];
  return [];
}

// ---------------------------------------------------------------------------
// REPORTING — on every post AND comment (LR1-2), and on every DM thread (LR1-4).
// ---------------------------------------------------------------------------
// LR1-12 ROUND THREE, HER RULING: **ONE TAP. THE CATEGORY PICKER IS DEAD.**
//
// A member should not have to be a moderator to press a button. She knows something is wrong;
// working out WHICH RULE it breaks is the backend's job, and the backend already owns the only thing
// that can actually do it — the same machine gate that screens every post. So a flag is one tap, the
// member may add words if she wants to, and `machineRead` below turns the item into the sentence her
// queue shows: what the gate thinks might be wrong with it.
export const FLAG_REASON = "flagged";

// The confirmation. Small, warm, and it says NOTHING about the outcome — telling a reporter that an
// item was hidden hands her a probe for whether any given post is shadow-hidden.
export const REPORT_ACK = "Flagged. This is with Lala now.";
export const REPORT_NOTE_PROMPT = "Want to say why? (optional)";
export const REPORT_NOTE_SEND = "Send";

// THE BACKEND'S CLASSIFICATION, which is the half her ruling moved off the member. Given the flagged
// text, this is the sentence her queue renders. It is the SAME `communityViolations` the gate runs,
// so the queue's read of a flagged item and the gate's read of a posted one can never disagree.
export function machineRead(text, { allowances = [] } = {}) {
  const hits = communityViolations(text, { allowances });
  if (!hits.length) {
    return { code: null, read: "The gate finds nothing against the rules in this one." };
  }
  return { code: hits[0].code, read: hits[0].label };
}

// Kept so old rows still render: reports written before this ruling carry a picker code, and the
// queue must not show a blank where a reason used to be.
export const LEGACY_REPORT_LABELS = {
  harassment: "Bullying, harassment, or hate",
  family: "Derogatory about the Conjure, Lala, or her family",
  nsfw: "NSFW or graphic",
  doxxing: "Doxxing or personal details",
  soliciting: "Soliciting or selling",
  shipping: "An order or shipping complaint",
  purpose: "Not what this room is for",
  other: "Something else",
  [FLAG_REASON]: "Flagged by a member",
};
export function reportLabel(code) {
  return LEGACY_REPORT_LABELS[String(code || "")] || "Flagged by a member";
}

// Anything a member sends is accepted: the flag itself carries no judgement to validate. A legacy
// code still passes, so a replayed old client cannot 400.
export function reportReasonValid(code) {
  const c = String(code || FLAG_REASON);
  return c === FLAG_REASON || Object.prototype.hasOwnProperty.call(LEGACY_REPORT_LABELS, c);
}

// ---------------------------------------------------------------------------
// STRIKES — wired to the EXISTING violation escalation (LR1-5). Strikes SILENCE POSTING.
//
// Deliberately the same shape as server/probe-ladder.js: strikes 1-2 are warnings, 3 silences for a
// day, 4 for a week, 5 indefinitely until she lifts it. Reading is never taken away — this room is
// something a member pays Apprentice for, and a strike is a moderation act, not a refund.
// ---------------------------------------------------------------------------
export const STRIKE_LADDER = [
  { strike: 1, action: "warn", silenceHours: 0, label: "Warned" },
  { strike: 2, action: "warn", silenceHours: 0, label: "Warned again" },
  { strike: 3, action: "silence", silenceHours: 24, label: "Silenced for a day" },
  { strike: 4, action: "silence", silenceHours: 24 * 7, label: "Silenced for a week" },
  { strike: 5, action: "silence", silenceHours: null, label: "Silenced until you lift it" },
];
export function strikeStep(count) {
  const n = Math.max(1, Math.floor(Number(count) || 1));
  return STRIKE_LADDER[Math.min(n, STRIKE_LADDER.length) - 1];
}

// What the struck member is told. A WARNING IS TOLD — this is not shadow-hide. Shadow-hide governs
// CONTENT (she is never told a post failed); a strike governs the ACCOUNT, and an account action she
// is not told about is one she cannot correct.
// **S5b MADE THESE THREE SENTENCES TRUE, WHICH THEY WERE NOT.** A silence is a fact about the ACCOUNT
// and closes every write in this room: `handleCreatePost`, `handleComment`, `handleShareSpell`,
// `handleStartThread` and `handleSendMessage` all refuse a silenced member and all five send THIS
// sentence — while it named POSTING alone. So a struck member who tried to write a DM was refused with
// a sentence about posts, and one struck for a DM was told a post of hers had broken the rules.
//
// **THE REPAIR IS TO MAKE THE SENTENCE TRUE RATHER THAN TO PARAMETERISE IT BY DOOR** (R28/R29): five
// call sites passing a `door` is five chances to pass the wrong one, and the fact itself is not
// per-door — it is *this account cannot write in the Living Room* — so one sentence says it everywhere.
// **Reading is still never taken away**, which is the ladder's own standing law and is what the second
// half of each sentence is for.
export function strikeNotice(count) {
  const step = strikeStep(count);
  if (step.action === "warn") {
    return "Something you wrote in the Living Room went against the house rules. Read them once more — the next one costs you posting and messaging for a day.";
  }
  if (step.silenceHours === null) {
    return "Posting and messaging in the Living Room are closed for you. You can still read. Reach support if you want to talk about it.";
  }
  const d = step.silenceHours / 24;
  return `Posting and messaging in the Living Room are closed for you for ${d === 1 ? "a day" : `${d} days`}. You can still read.`;
}

// ---------------------------------------------------------------------------
// THE MODERATION QUEUE — what her seat shows and what one tap does (LR1-5).
// ---------------------------------------------------------------------------
export const QUEUE_TABS = [
  // **ITEM A — HER MERGE, AND IT IS FIRST BECAUSE IT IS WHAT SHE OPENS ON.** Her order: *ONE home
  // merging Living Room holds + Gates review + Probes into a single queue of everything held or
  // flagged.* Held and Reported were two lanes she had to remember to check, and a hold she did not
  // look at is a member's words sitting invisible for a week. The SQL is one predicate
  // (`queueWhere("needs", …)` in `server/db/living-room.js`), and the COUNT on her Overview is taken
  // from that same predicate — a count from one clause beside a list from another is two answers to
  // one question, and its failure has no symptom except an owner who taps "3 waiting" and counts
  // five cards (R28/R29). Held and Reported stay: the merge is a better default, not a removal.
  { key: "needs", label: "Needs you", note: "Everything held or flagged, in one place, newest first." },
  { key: "held", label: "Held", note: "Machine-hidden. The author sees it; nobody else does." },
  { key: "reported", label: "Reported", note: "A member flagged this." },
  { key: "all", label: "Everything", note: "The whole room, newest first." },
  // ITEM 4(c) — **HER PHOTO LANE.** A profile photo cannot be read by the machine gate (it reads
  // TEXT) and this house RETIRED vision judging, with the reason written down: a judge less reliable
  // than the thing it judges. So a photo PUBLISHES on upload — the room's own fail-open — and lands
  // here, newest and unreviewed first, where a real pair of eyes is the gate.
  { key: "photos", label: "Photos", note: "Every profile photo, newest first. Hide puts that member back on the house glyph and tells her nothing." },
  // ITEM 12 — **HER DMs.** A lane on a seat she already has, not a second seat. It carries no post
  // and no comment: the two text queries below are the room's content and this tab is her own
  // conversations, so mixing them would put sixty posts under the messages she came to answer (the
  // same reasoning the Photos lane is built on). The key is exported below because
  // `src/inboxAnswer.js`, the server route and her seat all need to name this lane and this file
  // imports NOTHING — so the roster stays here and the key travels out, rather than this file
  // growing its first import to fetch a string back.
  { key: "inbox", label: "Your DMs", note: "Messages written to you. The simple ones were answered in your voice; everything else is waiting here with a draft." },
];
// ITEM 12 — the lane's key, exported so nothing else has to type "inbox" as a literal (R24's shape:
// a literal at a call site is held by nothing at all unless something goes and finds it).
export const INBOX_TAB_KEY = "inbox";
// **LR19 ADDED A FIFTH AND TWO VIEWS OF THE ONE ROSTER, DERIVED (R31).** Her seat judges from two
// places now — the dashboard queue and the room itself — and they offer different things on purpose:
// the queue is where she reaches for a strike or a delete, and the room is where she is READING, so
// the inline pair is her two words, Approve and Decline, and nothing else. Two hand-typed lists would
// be two answers to *what may her seat do*; `queue` and `feed` are flags on the one roster, so a
// sixth action joins by declaring where it belongs and can never appear in only half the code.
export const MOD_ACTIONS = [
  { key: "approve", label: "Approve", verb: "publish", note: "Makes it public. The author never knew it was held.", queue: true, feed: true },
  { key: "hide", label: "Hide", verb: "hide", note: "Author-only, silently. No message is sent.", queue: true, feed: false },
  { key: "delete", label: "Delete", verb: "remove", note: "Gone for everyone, including the author.", queue: true, feed: false },
  // VEIL-1: `veil` is the THIRD derived view of this one roster (R31). Her clause 5 reaches the
  // EXISTING ladder from a sender's name in the oversight window and names nothing else on this list
  // — deletion and group removal stay on their own owner paths — so exactly one entry carries the
  // flag and `src/veil.js` refuses the rest by name rather than by nobody drawing a button.
  { key: "warn", label: "Warn", verb: "strike", note: "Adds a strike. Strikes silence posting.", queue: true, feed: false, veil: true },
  // **DECLINE CHANGES NO ROW, AND THAT IS THE WHOLE POINT OF IT BEING ITS OWN KEY.** The item is
  // ALREADY held; "keep it held" is not a write, it is a DECISION — so re-using `hide` would fire
  // `setVisibility` with a reason off the request body and overwrite the reason the GATE recorded,
  // which is the exact field the overturn ledger keys its teaching on. It writes one ledger line and
  // touches nothing else.
  { key: "decline", label: "Decline", verb: "uphold", note: "It stays held. Nothing changes, and the author is told nothing.", queue: false, feed: true },
  // >>> **ITEM 4b / SD6 — THE GENTLE REDIRECT, AND IT IS THE ONLY ENTRY ON THIS ROSTER THAT CHANGES
  // NOTHING ABOUT THE POST.** <<< Her clause: *a gentle standing redirect for support-shaped Living
  // Room posts, on existing moderation surfaces.* Somebody posting *my seal will not download* into
  // the community room has not broken a rule — she is in the wrong room — so approving it, hiding it
  // or striking for it are all wrong answers, and before this entry those were the only answers her
  // seats offered. **The post stays exactly as it is and the member is sent one warm line.**
  //
  // **IT IS AN ENTRY ON THIS ROSTER RATHER THAN A CONTROL BESIDE IT (R31), WHICH IS WHAT PUTS IT ON
  // BOTH OF HER EXISTING SURFACES BY EXISTING** — `QUEUE_MOD_ACTIONS` and `FEED_MOD_ACTIONS` are
  // DERIVED, so `queue: true, feed: true` is the whole of *on existing moderation surfaces*.
  //
  // **AND VEIL-1's WARNING BINDS HERE: A KEY WITH NO BRANCH ON THE OWNER-ACTION ROUTE ANSWERS `ok`
  // HAVING DONE NOTHING** — which is why `ticket` and `flag` were deliberately kept OFF this roster.
  // So the route grows a real branch in the same push, and the fixture holds that as a biconditional
  // rather than as a comment.
  { key: "redirect", label: "Point to support", verb: "redirect",
    note: "Sends her one warm line pointing at Technical Support. The post is not touched.",
    queue: true, feed: true },
];
export const MOD_ACTION_KEYS = MOD_ACTIONS.map((a) => a.key);
// The two views. The DASHBOARD's four are exactly what it had before LR19, so her queue did not grow
// a button she did not ask for; the ROOM's two are her ruling, in her words, in her order.
export const QUEUE_MOD_ACTIONS = MOD_ACTIONS.filter((a) => a.queue);
export const FEED_MOD_ACTIONS = MOD_ACTIONS.filter((a) => a.feed);
// VEIL-1's view. It is exported from `src/veil.js` too, DERIVED from here, so the oversight window
// and this roster cannot come to disagree about what a warn is called or what it does.
export function modActionValid(key) {
  return MOD_ACTION_KEYS.includes(String(key || ""));
}

// WHAT HER SEAT CAN ACT ON. Declared rather than left as "whatever `KIND_OF` did not turn into a
// post" — that default is R17's silence exactly: an unrecognised kind became a POST, so an action
// aimed at something else would have run against a post table with a foreign id and either touched
// nothing or, worse, touched the row that happened to carry that number.
//
// **TWO ROSTERS, AND THE SECOND IS DERIVED FROM THE FIRST (R31).** A member edits and deletes her
// own CONTENT — words, which carry a body and a draft and a deleted_at. A photo is none of those
// things, and letting it through the member's edit route would be a 500 on a table with no `body`
// column. So the content kinds are the list, her seat's list is that list plus the photo, and there
// is no second place where either is typed out.
export const CONTENT_ITEM_KINDS = ["post", "comment", "message"];
export const MOD_ITEM_KINDS = [...CONTENT_ITEM_KINDS, "avatar"];
export function contentItemKind(value) {
  const k = String(value || "");
  return CONTENT_ITEM_KINDS.includes(k) ? k : "post";
}
export function modItemKind(value) {
  const k = String(value || "");
  return MOD_ITEM_KINDS.includes(k) ? k : "post";
}
// What each kind is CALLED in a sentence she reads. An avatar is not "an avatar" to her; it is a
// photo, and the confirm has to say the thing that is about to happen in the words of the room.
export const MOD_ITEM_NOUNS = { post: "post", comment: "comment", message: "message", avatar: "photo" };

// The sentence the SERVER composes for a moderation act, so the ledger line and the dashboard's
// confirmation are the same words (the D2 law: a confirm written on the client can describe
// something other than what the server does).
export function modActionSentence({ action, itemKind = "post", authorName = "a member" }) {
  const a = MOD_ACTIONS.find((x) => x.key === action);
  if (!a) return "";
  // The NOUN, never the kind key: her seat says "photo" where the code says "avatar", because the
  // sentence is the one thing in this system a person reads before something irreversible happens.
  const noun = MOD_ITEM_NOUNS[modItemKind(itemKind)] || itemKind;
  if (action === "approve") return `Publish ${authorName}'s ${noun}. It becomes visible to the room.`;
  if (action === "hide") return `Hide ${authorName}'s ${noun}. Only ${authorName} will see it, and ${authorName} is told nothing.`;
  if (action === "delete") return `Delete ${authorName}'s ${noun}. It is gone for everyone, including ${authorName}.`;
  if (action === "decline") return `Leave ${authorName}'s ${noun} held. Nothing changes, and ${authorName} is told nothing.`;
  if (action === "warn") return `Warn ${authorName}. This adds a strike, and strikes silence posting.`;
  // ITEM 4b / SD6. **THE ONLY SENTENCE HERE THAT PROMISES NOTHING WILL HAPPEN TO THE ITEM**, which is
  // the whole act — and it says so, because this is the sentence she reads in the confirm before she
  // taps, and the difference between this and `hide` is everything.
  if (action === "redirect") return `Send ${authorName} one line pointing at Technical Support. The ${noun} is not touched.`;
  // **EVERY KEY IS NAMED AND THE FALL-THROUGH IS EMPTY, WHICH IS A REPAIR LR19 HAD TO MAKE BEFORE IT
  // COULD ADD A KEY.** This line used to BE the warn sentence, so the fifth action would have read
  // *"Warn @Name. This adds a strike"* on a control that adds no strike — a sentence composed by the
  // server, shown in her confirm and written into the append-only ledger, describing an act that did
  // not happen. It is R25's DRAWER_ICONS shape on a moderation record: an unrostered key falling
  // through to a plausible wrong answer rather than a visibly missing one. The roster guard above
  // (`if (!a) return ""`) already refuses a key nobody declared, so nothing can reach here silently.
  return "";
}

// ---------------------------------------------------------------------------
// LR1-14 — SPELL SHARE. Members' OWN workings, credited to them permanently.
//
// THE ATTRIBUTION IS THE FEATURE. "@Name" is not decoration on a card; it is what the member gets in
// exchange for giving her working away, and it is why `spellByline` is one function that every
// surface (the card, the print sheet, the journal save, the cauldron input) calls. A print-out that
// loses the byline would hand a member's spell to somebody else's paper unattributed.
// ---------------------------------------------------------------------------
// LR1-12 ROUND TWO — HER NAMING, AND IT IS THE ONLY NAMING. The section is **SHARED SPELLS** and the
// button is **SHARE A SPELL**. Both live here as constants so the section header, the button, the
// empty state, the notification copy and the captures all read one fact — the sweep her ruling asked
// for is a sweep of ONE definition, and `scripts/lr1-living-room.mjs` holds the whole repo against
// the retired words coming back.
export const SPELLS_SECTION_LABEL = "Shared Spells";
export const SHARE_SPELL_LABEL = "Share a spell";
export const EMPTY_SPELLS = "No spells shared yet. Be the first.";

export const SPELL_KIND = "spell";
export const SPELL_USE_ACTIONS = [
  { key: "print", label: "Print", verb: "printed it" },
  { key: "journal", label: "Save to Journal", verb: "saved it to their journal" },
  { key: "cauldron", label: "Send to my Cauldron", verb: "sent it to their cauldron" },
];
export const SPELL_USE_KEYS = SPELL_USE_ACTIONS.map((a) => a.key);
export function spellUseValid(action) {
  return SPELL_USE_KEYS.includes(String(action || ""));
}

// The byline, everywhere. `@Name`, and nothing else — the same identity law the rest of the room runs
// on, so sharing a spell can never be the thing that reveals a surname.
export function spellByline(author) {
  return `@${roomFirstName(author?.name || author?.roomName || "") || "a member"}`;
}

// THE USE COUNTER'S SENTENCE, composed from real counts. Her trust law in words: the number is
// members, not taps. Zero says so plainly rather than hiding — "nobody yet" is honest and invites
// the first one; a hidden zero is a number waiting to be inflated.
export function spellUseSummary(counts = {}) {
  const total = Number(counts.members || 0);
  if (!total) return "Nobody has taken this one home yet.";
  const parts = SPELL_USE_ACTIONS
    .filter((a) => Number(counts[a.key] || 0) > 0)
    .map((a) => `${counts[a.key]} ${a.label.toLowerCase()}`);
  const who = total === 1 ? "1 member has" : `${total} members have`;
  return parts.length ? `${who} taken this home. ${parts.join(", ")}.` : `${who} taken this home.`;
}

// The whole spell as plain text: what the print sheet renders, what a journal save stores, and what a
// cauldron send carries. ONE composer, so the three doors cannot describe the same working three
// ways, and the byline rides in all of them.
export function spellPlainText(spell = {}, author = null) {
  const lines = [String(spell.title || "A spell").trim()];
  if (author) lines.push(`Shared by ${spellByline(author)}`);
  if (spell.intention) lines.push("", "Intention", String(spell.intention).trim());
  if (spell.materials) lines.push("", "What you need", String(spell.materials).trim());
  if (spell.steps) lines.push("", "How it is done", String(spell.steps).trim());
  if (spell.notes) lines.push("", "Notes", String(spell.notes).trim());
  return lines.join("\n");
}

// A share must actually be a working. Refused at the composer with an honest reason — this is a form
// check, not moderation, and it never shadow-hides.
export function spellShareViolations(spell = {}) {
  const out = [];
  const title = String(spell.title || "").trim();
  const steps = String(spell.steps || "").trim();
  if (!title) out.push("Give it a name.");
  if (title.length > 120) out.push("That name is longer than a name.");
  if (!steps) out.push("Say how it is done, even briefly. That is the part somebody else needs.");
  if (spellPlainText(spell).length > LIMITS.postChars) out.push(`That is longer than the room takes. ${LIMITS.postChars.toLocaleString("en-US")} characters.`);
  return out;
}

// ---------------------------------------------------------------------------
// LR1-12 ROUND TWO — THE TITLES-FIRST FEED, her verdict on the first captures.
//
// The feed lists TITLES (with @author, likes and the reply count); the body opens on tap. Her
// reason is visible in the first capture: a feed of full bodies is a wall, and a room where you have
// to read everything to find anything is a room people stop opening.
// ---------------------------------------------------------------------------

// A title is required on compose. It is NOT required to render, because posts made before this ruling
// exist and are hers: **an old post renders its FIRST LINE as its title until she edits it.** The
// fallback lives here rather than in a backfill, so no guess is ever written into a member's row.
export function postTitle(post = {}) {
  const given = String(post.title ?? "").trim();
  if (given) return given;
  const body = String(post.draftBody || post.body || "").trim();
  if (!body) return "A post";
  const firstLine = body.split(/\r?\n/).find((l) => l.trim()) || body;
  const clean = firstLine.trim();
  return clean.length > LIMITS.titleChars ? `${clean.slice(0, LIMITS.titleChars - 1)}…` : clean;
}

// Did this post ever get a real title, or is the feed showing a borrowed first line? The composer
// needs to know so an edit can prompt for one; nothing member-facing renders the difference.
export const hasRealTitle = (post = {}) => Boolean(String(post.title ?? "").trim());

// The compose check. A title is REQUIRED on a new post (her word), and it is a shape check, never
// moderation: it says what is missing and it never shadow-hides.
export function postComposeViolations({ title = "", body = "", media = 0 } = {}) {
  const out = [];
  const t = String(title ?? "").trim();
  if (!t) out.push("Give it a title. That is what the room sees first.");
  if (t.length > LIMITS.titleChars) out.push(`That title is long. ${LIMITS.titleChars} characters.`);
  if (!String(body ?? "").trim() && !media) out.push("Say something, or add a picture.");
  return out;
}

// ---------------------------------------------------------------------------
// THE FEED. Newest AND active (LR1-1), which are two different orders, so it is one score.
// `activity` is comments+likes; recency dominates, activity lifts. Pinned posts sit on top, always.
// ---------------------------------------------------------------------------
// LR1-12 ROUND TWO — **STRICTLY NEWEST-FIRST, EVERYWHERE** (her ruling, from her live report of new
// posts landing mid-list).
//
// THE DEFECT SHE DIAGNOSED CORRECTLY: the old rank blended activity with age
// (`activity * 2 - ageHours`), so a day-old post with a few replies outranked a post made ten
// seconds ago. A member who posts and does not see her own words at the top assumes it failed.
//
// **AND THE SHELF-ORDER LESSON IS THE REAL FIX (W8-2): ONE FRESHNESS EXPRESSION, SELECTED AND
// ORDERED BY THE SAME RULE.** `freshness` below is that one expression. The SQL orders by
// `created_at DESC` and this sorts by `createdMs`, which is the same field — so the database's order
// and the client's order cannot disagree, which is exactly how something lands mid-list.
//
// CREATED, not last-activity: a comment on an old post must not shove it above a post made since.
export function freshness(item) {
  return Number(item?.createdMs ?? 0);
}
export function feedRank(item) {
  // Pins sit on top, always. Below them it is time, and nothing else.
  return item?.pinned ? Number.POSITIVE_INFINITY : freshness(item);
}
export function sortFeed(items) {
  return [...(items || [])].sort((a, b) => feedRank(b) - feedRank(a));
}

// ---------------------------------------------------------------------------
// LR1-12 ROUND TWO — SEARCHING THE ROOM. Titles, bodies, and shared spell names. Newest-first, by
// the SAME freshness expression as the feed, so a search result set is the feed filtered rather than
// a second ordering nobody reconciled.
// ---------------------------------------------------------------------------
export function matchesSearch(item, term) {
  const q = String(term ?? "").trim().toLowerCase();
  if (!q) return true;
  const hay = [
    item?.title,
    item?.body,
    item?.draftBody,
    item?.spell?.title,
    item?.spell?.intention,
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q);
}
// **LR17-4 RETIRED `searchFeed`, AND `matchesSearch` IS PROMOTED RATHER THAN LEFT BESIDE IT.**
// The room's search stopped being a filter over the page `listFeed` had already fetched — it could
// not reach a match older than fifty rows and could not see a COMMENT at all — so the filtering and
// the ordering both moved into `searchPosts`, and the wrapper had no caller left. An uncalled export
// superseded by its replacement GOES rather than standing beside it (DL-2's `getTodayDaily`,
// DT-1c's `deliveryAudit`, DT-2b's `undelivered`, R181's `threadBlockConflict`).
//
// **WHAT `matchesSearch` IS NOW IS THE ANSWER KEY.** `searchPosts` asks Postgres
// `strpos(lower(x), lower($4)) > 0`, which is this function's own law — lowercase, substring, no LIKE
// metacharacters — and the two are held against each other over one table of terms, because *what
// counts as a match* having two spellings is exactly how a search box and its query come to disagree
// (R13/R14/R15: a cross-boundary law is held by opening BOTH sides).
export const SEARCH_PLACEHOLDER = "Search the room";
// LR17-4: the room's search answers two kinds of thing, so the people it found are named rather than
// left as an unlabelled row of faces. It lives here with the rest of the room's copy, because a
// heading typed inside a TSX file is the one kind this house keeps losing track of (14-B5's bell).
export const MEMBER_RESULTS_LABEL = "People";
export function searchEmpty(term) {
  return `Nothing matches "${String(term || "").trim()}".`;
}

// ---------------------------------------------------------------------------
// LR17-3 — **FINDING A MEMBER FROM MY ROOM: WHAT THE TERM MEANS, WHAT THE BOX IS DOING, AND WHAT IT
// SAYS WHEN IT FOUND NOBODY.**
//
// Her spec: house-styled, accepts an `@tag` or a plain name WITH or WITHOUT the `@`, matches room
// names AND first names, results as rows carrying the follow button — **finding and following only,
// no doors to anyone's room or chart.**
// ---------------------------------------------------------------------------

// **THE NORMALISATION IS ONE FUNCTION BECAUSE IT IS ASKED IN THREE PLACES**, and typed three times it
// is three answer keys (R28/R29). The store's query strips the `@` before it matches; the ROUTE has
// to know whether there is anything worth asking; and the CLIENT has to know whether the box is idle.
// The one that drifts is invisible in the direction that reads as a finding: a client calling `"@"` a
// real term draws *no one matches* over a store that, having stripped it, was never asked anything —
// R18's distinction (nothing found / nothing asked) arriving as a sentence about the house.
export function memberSearchTerm(term) {
  return String(term ?? "").trim().replace(/^@+/, "").trim();
}

// **THE CAP IS DECLARED RATHER THAN LEFT IN THE QUERY, AND IT IS SAID OUT LOUD** (R17/R160: no silent
// caps). A search that quietly hands back the first twenty of forty reads exactly like a search that
// found twenty, and the repair — type another letter — is one she can only take if she is told.
export const MEMBER_SEARCH_LIMIT = 20;
export function memberSearchCapNote(shown) {
  const n = Number(shown);
  if (!Number.isFinite(n) || n < MEMBER_SEARCH_LIMIT) return "";
  return `Showing the first ${MEMBER_SEARCH_LIMIT}. Type a little more to narrow it.`;
}

// **FIVE STATES, NOT FOUR, AND THE FIFTH IS THE ONE A LIST DOES NOT HAVE: IDLE.** `CircleList` opens
// on a list that was asked for; a search box is asked for nothing until she types, and drawing *no
// one matches* over an empty box is a statement about the house made in answer to no question
// (R64's ruling: the failure state is not a missing sentence but a wrong one). The order is the law —
// the read that did not land sits ABOVE the empty one (R52/R65), so a failed search can never be
// painted as *nobody is here*.
export const MEMBER_SEARCH_STATES = ["idle", "loading", "failed", "none", "rows"];
export function memberSearchState({ term, people, loading } = {}) {
  if (!memberSearchTerm(term)) return "idle";
  if (loading && people === null) return "loading";
  if (people === null) return "failed";
  if (!people.length) return "none";
  return "rows";
}

export const MEMBER_SEARCH_PLACEHOLDER = "Find someone by name or @tag";
export const MEMBER_SEARCH_LABEL = "Find people";
export const MEMBER_SEARCH_LOADING = "Looking...";
// A different sentence from `searchEmpty` on purpose: that one answers a search of the ROOM, which
// looks at posts and comments as well, and this one answers a search for a PERSON. Collapsing them
// would tell her nothing matched her words when what she asked about was a name.
export function memberSearchEmpty(term) {
  return `No one here matches "${memberSearchTerm(term)}".`;
}

// ---------------------------------------------------------------------------
// THE ROOM'S OWN COPY. One place, so the card, the page and the empty state agree.
// ---------------------------------------------------------------------------
export const ROOM_TITLE = "The Living Room";
// LR1-12 ROUND TWO: **"Where the house talks." IS REMOVED ENTIRELY** (her ruling). The constant is
// gone rather than blanked, so nothing can render an empty eyebrow where it used to sit, and the
// fixture holds the whole repo against the string coming back.
export const ROOM_DESCRIPTION =
  "Sit with the rest of the house. Ask what you have been sitting on, show the work you have done, and read what everybody else is finding.";
export const EMPTY_FEED = "Nothing here yet. Say the first thing.";
// L2.7 — **AN EMPTY PANEL IS STILL AN ANSWER, AND IT IS WARM.** Her ruling: an empty inbox opens
// saying no messages yet, an empty bell opens saying no notifications. Both sentences say what will
// land here, because a door that opens onto four words reads as broken and one that tells her what
// this place is for reads as new. The bell's was a bare `Nothing yet.` typed inside a TSX file — the
// one kind of copy this house keeps losing track of — and it lives here with the rest of the room's
// words now, so `scripts/lr1-living-room.mjs` can ask about it.
export const EMPTY_INBOX = "No messages yet. When somebody writes to you, it lands here.";
export const EMPTY_NOTIFICATIONS = "Nothing yet. When somebody replies to you, tags you or writes to you, you will hear about it here.";

// ---------------------------------------------------------------------------
// ITEM 5 — THE INBOX, ITS OWN SCREEN. The words on it live here, with the law, so the placeholder a
// member reads and the search the server actually runs are one declaration. **ONE INBOX, NO TABS**
// is her ruling and it is enforced by there being no tab roster to add to: the only narrowing is a
// search string and one unread filter, both of which are the SAME list read differently.
// ---------------------------------------------------------------------------
export const INBOX_TITLE = "Inbox";
export const INBOX_COMPOSE_LABEL = "New message";
// S-1 put a back chevron left of the centred title. **ITEM 1 RETIRED IT AND THE REASON IS HER OWN
// SENTENCE:** it was labelled "Back" and wired to a hard-typed `goTo("living-room")`, so it did not
// go back — it went somewhere. A member who opened her inbox from My Room tapped the only thing in
// that corner that looked like a way out and landed in the Living Room, which is why she reported
// the screen as having *no way to close*. The slot now holds a CLOSE, and the destination is the
// screen she really came from (`src/lib/inbox-return.ts`).
//
// The glyph carries no text, so this is its whole accessible name — the one kind of copy nobody
// looking at the screen can see, and the kind this house has twice shipped in English by accident
// (14-B5's bell and mail). The retired export is DELETED rather than left standing beside its
// replacement (DL-2's `getTodayDaily`, DT-2b's `undelivered`, R181's `threadBlockConflict`).
export const INBOX_CLOSE_LABEL = "Close";
// ITEM 5: the one control that opens a group thread member list. Its whole text is what she reads,
// so it is declared here rather than typed at the render site (14-B5).
export const GROUP_MEMBERS_LABEL = "Group members";

// ---------------------------------------------------------------------------
// ITEM 5 (her `4`) — **THE UNBLOCK PATH, AND WHY IT NEEDED A SCREEN OF ITS OWN.**
//
// Her order: *"Blocked means no new DMs and no adding to groups by the blocker. Warm refusal wording.
// UNBLOCK PATH IN THE SAME PLACE."* The block half has been built, wired and rendered since ITEM 3;
// **the unblock half had a ROUTE and no door.** The client's `block()` hard-coded `blocked: true` and
// nothing anywhere ever sent `false`.
//
// **AND IT COULD NOT BE A TOGGLE ON THE BUTTON SHE ALREADY HAS, WHICH IS THE WHOLE OF THIS ROUND.**
// The Block control lives in a thread's participant list, and blocking CLOSES that thread and takes
// the other member out of her room entirely (LR1 ruling 2, absolute and symmetric) — **so the control
// she would need to un-press leaves with the person it points at.** *"The same place"* therefore
// resolves to the INBOX, which is the screen the block is performed from and the only surface that
// survives it (DECISIONS ITEM5-BLOCK-2).
//
// **FOUR STATES, NOT TWO (R18), AND THE MIDDLE TWO ARE THE REASON THIS IS A FUNCTION.** A read that
// has not answered must HOLD STILL; a read that FAILED says so in its own sentence; `[]` is an ANSWER
// and earns her warm line. Collapsing waiting into empty tells a member who has blocked somebody that
// she has blocked nobody — which on a SAFETY control reads as the block having come undone, and is
// the one wrong thing to say on this screen (R64's cauldron ruling, one drawer over).
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 2026-08-14 — **THE FOUR STATES ARE ONE DECLARATION NOW, BECAUSE A SECOND SURFACE NEEDED THEM AND
// TWO SPELLINGS OF ONE IDEA IS TWO ANSWERS (R28/R29).**
//
// `listReadState` is the whole question — *still looking / could not read / genuinely empty / here
// they are* — and `blockedListState` is DERIVED from it rather than kept as a second body, so it
// answers exactly what it always answered and cannot drift from the law it is an instance of.
// **The comment fold reads it too**, which is what this lift is for: `InlineComments` had no failed
// state at all and rendered *"Loading…"* forever on a read that never landed.
//
// **PRESENCE, NEVER TRUTHINESS (R53/R136/R137):** anything that is not a real array is *not a list*,
// so a 200 carrying no collection, a `null`, a string and an object all HOLD rather than being read
// as an empty answer — the direction that puts *"there is nothing here"* over a surface whose read
// simply did not arrive.
export function listReadState({ items = null, failed = false } = {}) {
  if (failed === true) return "failed";
  if (!Array.isArray(items)) return "waiting";
  if (items.length === 0) return "empty";
  return "list";
}

export function blockedListState({ items = null, failed = false } = {}) {
  return listReadState({ items, failed });
}

// ---------------------------------------------------------------------------
// **THE COMMENT FOLD'S OWN READ, AND WHY IT NEEDED ITS OWN SENTENCE (2026-08-14).**
//
// `InlineComments` held `comments: Comment[] | null` and rendered *"Loading…"* whenever it was null.
// Its loader set that null on a failure and never cleared it, so **a 500 and a slow answer were ONE
// STATE on her screen, forever** — and the sentence it did compose could be the empty string, since
// a thrown request answers `null` and a 500 from R72's catch need carry no message the client reads.
// So a member whose fold failed sat under *"Loading…"* with nothing else on the screen at all.
//
// That is R18/R64's four-state ruling one component over, and it is a defect in its own right rather
// than a symptom of anything upstream: whatever makes the read fail — a pool that is busy, a
// container restarting mid-deploy (ITEM 13b exists because this site is redeployed under a live
// crowd), a phone that lost its signal — she is entitled to be told, and told once rather than
// promised an arrival that is not coming.
//
// **THE SENTENCE IS `ROOM_READ_FAILED`, WHICH THIS ROOM ALREADY OWNS**, and the SERVER'S own words
// come first through `roomNote` (R83: it is the only side that knows whether this was a refusal or a
// fault). No new copy is minted for a fact the room already has a sentence for (R28/R29).
export const COMMENTS_LOADING = "Loading...";
export function commentsReadState(d) {
  // `roomItems` is the house's own presence law: an answer that is not `ok`, or one carrying no real
  // array, is NOT a list. A failure is therefore *the read did not land*, which is exactly what the
  // four-state law is about — and a REFUSAL (the server deliberately said no) and TRANSPORT NOISE (a
  // thrown request) take the same sentence here on purpose, because a member reading a fold does not
  // act differently on the two and the server's own words override both when it sent any.
  const items = roomItems(d, "comments");
  return { state: listReadState({ items, failed: items === null }), items, note: roomNote(d, ROOM_READ_FAILED) };
}

export const BLOCKED_LIST_LABEL = "Blocked members";
// The empty line says what this place is FOR rather than reporting a count, which is `EMPTY_INBOX`'s
// own ruling (L2.7: a door that opens onto four words reads as broken).
export const EMPTY_BLOCKED_LIST =
  "You have not blocked anyone. If you ever do, they will be listed here so you can undo it.";
export const UNBLOCK_LABEL = "Unblock";

// **THE TWO SENTENCES OF ONE ACT, DECLARED TOGETHER.** The block's wording has always been composed
// in `server/living-room-routes.js` as a literal; the unblock's was a bare `"Unblocked."`. They are
// the same fact read in two directions, so they are one declaration here — the room's copy lives with
// the room's law (14-B5, the kind of copy this house keeps losing track of), and the SERVER imports
// them rather than typing a third spelling (R28/R29).
//
// **THE BLOCK SENTENCE IS BYTE-IDENTICAL TO THE ONE THAT SHIPPED.** This round moved it; it did not
// re-word it. The unblock sentence is the exact inverse of it and claims nothing beyond the block
// being lifted — it deliberately does NOT promise that they can message her, because the follow gate
// is a separate wall this act does not touch and a sentence that over-promises is a defect on a
// safety screen.
export const BLOCK_DONE =
  "Blocked. They cannot message you, you will never share a conversation, and neither of you sees the other in this room.";
export const UNBLOCK_DONE =
  "Unblocked. The block is lifted, and you will both see each other in this room again.";
// ITEM 6: the whole vocabulary of the name-tap menu. Exactly two actions, her order, declared here
// so the one component reads them rather than three surfaces typing them (14-B5).
export const MEMBER_MENU_FOLLOW = "Follow";
export const MEMBER_MENU_UNFOLLOW = "Unfollow";
export const MEMBER_MENU_MESSAGE = "Message";
// I3 (her answer H4-3, 2026-10-19): Block on a member's name, in the Living Room and in a live. The same block
// the message thread has always carried (one route, both ways), asked once before it lands.
export const MEMBER_MENU_BLOCK = "Block";
export const MEMBER_MENU_BLOCK_ASK = "You will not see each other in the Living Room or in a live, and they cannot message you.";
export const MEMBER_MENU_BLOCK_YES = "Block";
export const MEMBER_MENU_BLOCK_NO = "Never mind";
// The signal a surface hears to re-read itself, so a blocked member leaves the feed the moment it lands.
export const BLOCK_EVENT = "member-block:changed";
export const SHARE_LABEL = "Share";
export const SHARE_PICK_HINT = "Type a name to send this to.";
// ITEM 7 — WHAT A SHARED POST SAYS WHEN IT LANDS IN AN INBOX. Room names only and nothing about the
// author beyond what the room already shows everybody: a title and the link back. Composed HERE so
// the route and any later surface cannot drift about what a share reads like (14-B5/R28).
export function sharedPostMessage(post = {}) {
  const title = String(post.title || "").trim();
  const url = livingRoomUrl({ kind: "post", id: post.id });
  return title
    ? `Shared a post: ${title}\n${url}`
    : `Shared a post from the Living Room\n${url}`;
}
// ---------------------------------------------------------------------------
// HER QC (2026-08-07): **A SHARED POST ARRIVES AS A CARD, NEVER AS A RAW PATH.**
// ---------------------------------------------------------------------------
//
// *"Sharing a post delivers a bubble reading `Shared a post: TITLE /living-room?post=1406`, a raw
// path, nothing clickable, nothing shown. Unacceptable."*
//
// **HER CLAUSE 3 DECIDES THE WHOLE DESIGN: IT IS A *RENDER* LAW, NOT A SEND LAW.** `sharedPostMessage`
// above composes a STRING and that string IS the message body — there is no `shared_post_id` column,
// no attachment row, no kind flag. So every share already sent, including her own ADD THE APP message,
// carries nothing but text. **A fix that stamped a new column on new sends would leave every existing
// share exactly as broken as she reported it**, which is the one thing her sweep clause forbids. The
// id is therefore PARSED out of the body at render time, for every message, old and new.
//
// A column could be added later as an optimisation — it must never become the only path, or the
// sweep clause is silently unmet again.

// What a shared-post body points at, or `null` for an ordinary message. Pure, so a gate RUNS it over
// bodies this database does not contain.
export function sharedPostRef(body = "") {
  const text = String(body ?? "");
  // Anchored on the ROOM'S OWN PATH rather than on the English sentence in front of it: the sentence
  // is copy and copy gets edited (it already has two forms, with and without a title), while the path
  // is what `livingRoomUrl` composes and what the tap has to resolve. A body that merely mentions the
  // room in prose carries no `post=`, so it is not a share.
  // **THE PATH MUST STAND ON ITS OWN, NOT SIT INSIDE SOMEBODY ELSE'S URL.** `https://example.com
  // /living-room?post=4` contains this path, and matching it would render one of THIS room's posts as
  // a card for a link that points somewhere else entirely. Anchored to the start of the string or to
  // whitespace, which is exactly how `livingRoomUrl` composes it (`sharedPostMessage` puts it on its
  // own line). The first draft of this parser matched anywhere and the fixture HEDGED that case
  // rather than testing it — the mutation run reported the check vacuous and it was right.
  const m = new RegExp(`(^|\\s)${LIVING_ROOM_PATH}\\?post=(\\d+)`).exec(text);
  if (!m) return null;
  return { kind: "post", id: m[2] };
}

// **THE SAME WARM SENTENCE COVERS BOTH "DELETED" AND "YOU MAY NOT SEE IT", AND THAT IS DELIBERATE.**
// Her clause 2 names the deleted case. The other case is LR1 ruling 1: the post may have been
// shadow-HELD since, or its author may have blocked this reader. Those are different facts about the
// row (R18) — but telling a reader a post was DELETED when it was really HELD would tell her the hold
// exists, which is the whole thing shadow-hide withholds. One sentence, no leak, and it is true in
// both cases: it is not in the room for her.
export const SHARED_POST_GONE = "This post is no longer in the room.";

// ---------------------------------------------------------------------------
// HER QC ROUND TWO (2026-08-07) — **WHAT THE CARD'S PICTURE AREA IS, DECIDED ONCE.**
//
// Round one drew a broken-image ghost on her video share, because the store took the post's first
// attachment REGARDLESS OF KIND and handed video bytes to an `<img>`. Her three clauses about the
// picture area are one decision, and this is it:
//
//   • an image post shows a crop of that image;
//   • a video post shows the video's cover frame with a play badge;
//   • a text post has **no picture area at all** — *"if there is no valid thumbnail the image area
//     simply does not exist."*
//
// So the answer is `null` or a KIND, and the kind is the attachment table's own `kind` column rather
// than anything sniffed off a URL — this site has no video sniff anywhere (LR16's own note) and a
// guess off a file extension is how the ghost got there.
//
// **AN UNROSTERED KIND ANSWERS `null`**, which is this house's standing ruling on an unknown key
// (R17/R25's `DRAWER_ICONS`): a third kind added to that column next year draws NO picture rather
// than a plausible broken one. That is the direction whose failure she can see.
//
// **IT LIVES HERE RATHER THAN IN THE STORE SO A GATE RUNS IT.** `server/db/living-room.js` reaches
// the connection pool, and the FAST tier may not import anything that does (R146/R152).
// ---------------------------------------------------------------------------
// **THE ROOM'S ONE ATTACHMENT URL** (R8's law, one room over). It was composed inline in
// `attachmentWire` and this round would have been its second copy — and R8's own note says why that
// matters: a 404 on a media source is SILENT, so the two spellings drift with no error anywhere.
export function roomAttachmentUrl(id) { return `/api/living-room/attachment/${id}`; }

export const SHARED_THUMB_KINDS = ["image", "video"];
export function sharedThumb(attachmentId, kind) {
  const id = attachmentId === 0 || attachmentId ? String(attachmentId) : "";
  const k = String(kind || "");
  if (!id || !SHARED_THUMB_KINDS.includes(k)) return null;
  return { url: roomAttachmentUrl(id), kind: k };
}

export const INBOX_SEARCH_COPY = "Search your messages";
export const INBOX_UNREAD_FILTER = "Unread";
export const INBOX_PIN_LABEL = "Pin to the top";
// COMPOSE'S EMPTY STATE, and it exists because the room's people route DELIBERATELY refuses a blank
// query: the picker is a shortcut, never a directory (LR1-9's ruling, and the reason there is no
// member list on this site to leak). An empty box with no list under it reads as broken; this says
// what to do instead.
export const INBOX_COMPOSE_HINT = "Type a name to find them.";
// LR18b — THE COMPOSE'S OWN WORDS, once her clause 1 made it a multi-select. They live here with the
// law rather than as literals in the sheet for 14-B5's reason: every one of them is member-facing
// copy, and the two the house has previously shipped in English by accident were both accessible
// names nobody looking at the screen can see.
//
// The multi hint is a TEACHING in `INBOX_FOLLOW_NOTE`'s register — it says what she CAN do before
// she meets a refusal, and it is the only place this surface says that picking more than one person
// is even possible, because the control for it is a second tap on a row that already looks tapped.
export const COMPOSE_MULTI_HINT = "Pick one person, or pick a few to start a group.";
export const COMPOSE_GROUP_NAME_LABEL = "Name this group";
export const COMPOSE_SELECTED_LABEL = "Who you are writing to";
export const COMPOSE_REMOVE_LABEL = "Take out";
export const COMPOSE_START_LABEL = "Start the conversation";
export const COMPOSE_STARTING_LABEL = "Starting...";
// LR18b(b) — the strip stops being only people the moment a group is in it, so its accessible name
// stops being only people too. It is the one word on that rail and nothing on screen carries it.
export const CIRCLE_STRIP_LABEL = "Your groups and the people you follow";
export const CIRCLE_GROUP_LABEL = "Group";
export const INBOX_UNPIN_LABEL = "Unpin";
// L6(4) — **THE ONE LINE THAT TELLS HER THE RULE BEFORE SHE MEETS IT AS A REFUSAL.**
// Her ruling: *"a small elegant line under the inbox header or empty state: you can only message
// people you follow, in register, teaching without nagging."*
//
// **IT IS A TEACHING AND NOT A WARNING, WHICH IS WHY IT IS NOT `messageGateRefusal`'s SENTENCE.**
// ITEM 9's gate refuses at the DOOR — *you can only message people you follow* arrives after she has
// picked somebody and pressed a button, which is the moment it is least useful and most like being
// told off. This is the same fact stated before it costs her anything, so it opens with what she CAN
// do rather than with what she cannot: it is the compose rule read forwards.
//
// It says FOLLOW rather than naming the refusal, and it does not mention blocking or reachability —
// LR1 ruling 2 is that a blocked member and a member who is not here answer the SAME sentence, so a
// line that started enumerating the reasons a message might not go would be the one place on this
// site that hands a blocked member the difference.
export const INBOX_FOLLOW_NOTE = "You can message anyone you follow.";
// What she reads when a search matches nothing. Deliberately different from EMPTY_INBOX: an inbox
// with no messages and an inbox whose search found none are two different facts, and telling her the
// first when the second is true reads as messages having gone missing.
export function inboxSearchEmpty(term) {
  return `Nothing in your messages matches "${String(term || "").trim()}".`;
}
// ---------------------------------------------------------------------------
// >>> HER INBOX SEARCH IS PEOPLE-FIRST (her order, 2026-08-19). <<<
//
// HER CLAUSE: *"inbox search becomes PEOPLE-FIRST (People / Messages split)."*
//
// **MEASURED FIRST (R25/R180): THE INBOX SEARCH COULD NOT FIND A PERSON AT ALL.** `listThreads`
// matches a thread's TITLE, the WORDS in it, and the people IN it — every one of which requires a
// thread to already exist. So the member she has never written to is unfindable from the one screen
// she goes to to write to somebody, which is her @rickena report from the other end: the door was
// repaired in `6aa2468a` and there was still no way to REACH it by typing a name.
//
// **THE PEOPLE LIST IS NARROWED BY `pickerRefuses`, WHICH IS R15 AND NOT A TIDY-UP.** ITEM 9's gate
// decides who she may write to; a list offering somebody the draft door would then refuse costs her
// the action and teaches her the screen is unreliable. The HELD COUNT survives with it
// (`ComposeMessage`'s own ruling): an empty list under a name that plainly exists reads as broken
// rather than as a rule, so the two empty states say different things.
//
// **PEOPLE BEHIND A TERM, MESSAGES ALWAYS**, which is the members route's own recorded ruling one
// door over: *"a member has a handful of groups and knows them by sight… people are different —
// there are thousands, so they stay behind a term."* An inbox that opened onto a directory would be
// that ruling broken on the surface it was written for.
//
// **AND THE TWO READS FAIL SEPARATELY (R18).** A people lookup that did not land may not take her
// conversations off the screen, and a threads read that did not land may not be reported as *nobody
// matched*. Four states per section, never two.
export const INBOX_PEOPLE_HEADING = "People";
export const INBOX_MESSAGES_HEADING = "Messages";
// What she reads when a NAME matched nobody she can write to. Deliberately not `inboxSearchEmpty`:
// that one is about her conversations, and telling her a person does not exist when what is true is
// that her messages do not mention that word is the same collapse one section down.
export function inboxPeopleEmpty(term) {
  return `Nobody you follow matches "${String(term || "").trim()}".`;
}
// The held sentence, when the room found somebody and ITEM 9's gate is what is holding her back.
export function inboxPeopleHeld(n) {
  // PRESENCE, NEVER COERCION (R53/R136/R137). `Number(true)` is 1, so a coerced read would put
  // "One more member matched" on her screen off a flag — and **this round's own fixture is what
  // caught it**, which is the whole argument for DRIVING a law rather than reading it.
  const count = typeof n === "number" && Number.isFinite(n) ? n : 0;
  if (count <= 0) return "";
  return count === 1
    ? "One more member matched. You can message anyone you follow."
    : `${count} more members matched. You can message anyone you follow.`;
}
// >>> THE SPLIT, AND THE ORDER IS THE WHOLE CLAUSE. <<<
//
// `sections` is what DRAWS, in the order it draws. People first whenever there is a term, so a
// build that appended them under her conversations is RED rather than merely different — which is
// what her sentence asks for and what a presence check could never see.
//
// PEOPLE STATES (R18/R84, four and never two): `hidden` (no term — this is not a directory),
// `wait` (asked, nothing back), `failed` (asked, could not be read), `none` (asked, nobody she
// may write to), `list`. Only `hidden` removes the section; the other three all say something,
// because a heading that vanishes on a slow read is a screen she cannot trust.
export const INBOX_PEOPLE_STATES = ["hidden", "wait", "failed", "none", "list"];
export function inboxPeopleState(term, people) {
  if (!String(term || "").trim()) return "hidden";
  if (people === null || people === undefined) return "wait";
  if (people === false) return "failed";
  if (!Array.isArray(people)) return "failed";
  return people.length ? "list" : "none";
}
export function inboxSearchSections(term, people) {
  const state = inboxPeopleState(term, people);
  return state === "hidden" ? ["messages"] : ["people", "messages"];
}

// ---------------------------------------------------------------------------
// ITEM 7 — THE FOLLOW GRAPH. **ONE LAW, AND IT ANSWERS THE ONLY THREE QUESTIONS A SURFACE HAS:**
// what does the tag say, what does tapping it do, and is there a tag here at all.
//
// **A FOLLOW IS A PER-VIEWER FACT**, exactly like shadow-hide's visibility and like blocking, so it
// is carried ON the identity every surface already renders (`publicIdentity`) rather than fetched
// per surface. That is what makes "a Follow tag beside every @name everywhere" one component and not
// a sweep — the same argument ITEM 4(c) proved with the photo.
//
// **THE THIRD STATE IS THE ONE THAT MATTERS: `self`.** Following yourself is not a thing that can be
// refused politely — it is a tag that must not be drawn — and a two-state boolean cannot say so.
// `followState` returns one of three, and `null` for "nobody is asking", which is her seat and the
// notification list, where a follow control would be noise.
// ---------------------------------------------------------------------------
export const FOLLOW_LABEL = "Follow";
export const FOLLOWING_LABEL = "Followed";
// What the control ANNOUNCES to a screen reader, which is not what it says on its face: the face
// says what IS, the label says what tapping does. A button reading "Followed" whose accessible name
// is also "Followed" tells somebody who cannot see the colour nothing at all.
export const FOLLOW_ACTION = (name) => `Follow @${String(name || "").trim()}`;
export const UNFOLLOW_ACTION = (name) => `Unfollow @${String(name || "").trim()}`;
export const FOLLOW_STATES = ["self", "following", "not-following"];
export const EMPTY_FOLLOW_STRIP = "The people you follow show up here.";

// WHO IS ASKING decides everything here, which is why the viewer is the first argument and not an
// option: there is no viewer-free answer to "is this followed".
export function followState(who, viewerRef) {
  if (!who || viewerRef === null || viewerRef === undefined || viewerRef === "") return null;
  if (String(who.ref ?? "") === String(viewerRef)) return "self";
  return who.following ? "following" : "not-following";
}

// The face of the control, per state. `self` has no face — the caller renders nothing — and asking
// for one is a caller bug rather than a fallback, so it answers null rather than a plausible word.
export function followLabel(state) {
  if (state === "following") return FOLLOWING_LABEL;
  if (state === "not-following") return FOLLOW_LABEL;
  return null;
}

// What a tap MEANS, which is the opposite of what the control currently says. Written as a law
// because the client used to be the only place this inversion lived, and an inversion typed at a
// call site is the kind of thing that survives a refactor backwards.
export function followOnTap(state) {
  if (state === "following") return false;
  if (state === "not-following") return true;
  return null;
}

// May this follow be WRITTEN? The write side of the same three questions, with the two absolute
// rules on top of it. **A blocked pair holds no follow in either direction** — blocking is symmetric
// and sits above everything (LR1 ruling 2) — and the refusal is the room's ONE sentence for a member
// who is not reachable, because "you are blocked" hands a blocked member exactly what a block exists
// to withhold.
export function followViolations({ viewer, targetRef } = {}) {
  const out = [];
  const me = String(viewer?.ref ?? "");
  const them = String(targetRef ?? "");
  if (!me) out.push("no-viewer");
  if (!them) out.push("no-target");
  if (me && them && me === them) out.push("self");
  if (viewer?.blocked?.has?.(them) || viewer?.blockedBy?.has?.(them)) out.push("blocked");
  return out;
}

// ---------------------------------------------------------------------------
// ITEM 9 — FOLLOW-GATED MESSAGING. **YOU MUST FOLLOW SOMEBODY TO OPEN A CONVERSATION WITH THEM**, and
// the enforcement is the SERVER's, never the picker's.
//
// **THE GATE IS ON THE DOOR, NOT ON EVERY SENTENCE, AND THAT IS THE WHOLE DESIGN.** Opening a thread
// asks "may I start this at all"; sending into one that is already open asks nothing, because
// PARTICIPATION IS THE PROOF THE DOOR WAS PASSED. That is what makes her grandfather rule fall out
// rather than be carved out: a conversation that existed before this shipped is a conversation whose
// participants are participants, so it keeps working in BOTH directions forever, and so does a reply
// from somebody who never followed back. A per-message follow check would mean a member handed a
// message she is structurally unable to answer.
//
// **`threadExists` IS THEREFORE THE GRANDFATHER RULE ITSELF**, written as an argument rather than
// implied by where the call sites are. `ensureThread` is idempotent on the participant set, so
// "start a conversation" and "open the one we already have" are the same request from the client's
// side — without this flag the gate would refuse to REOPEN an old thread, which is the exact thing
// her rule protects.
//
// **THE ORDER OF THE TWO GUARDS IS THE ITEM'S REAL RISK.** "You can only message people you follow"
// is warm and correct for a stranger and is a DISCLOSURE for a blocked member: two different
// sentences at the same door tell her which of the two things happened, and that a block exists is
// precisely what a block exists to withhold (LR1 ruling 2). So reachability is asked FIRST, through
// the law that already answers it (`canMessage`), and any code other than `not-following` collapses
// to the room's one sentence.
// ---------------------------------------------------------------------------

// The room's ONE sentence for a member who cannot be reached — absent, blocked, or yourself. It is a
// declaration rather than a literal at each call site because its whole job is to be IDENTICAL
// everywhere: a copy edit at one of them is what turns absolute blocking into a probe.
export const MEMBER_NOT_HERE = "That member is not here.";
// Her verbiage, and it is deliberately NOT an apology or an explanation of the graph. It says the
// rule and what to do about it, which is one tap away on every @name in the room.
export const MESSAGE_FOLLOW_REFUSAL = "You can only message people you follow. Follow them first.";
// COMPOSE'S OWN COPY, for the case that reads as broken otherwise: she typed a real member's name,
// the room found her, and the picker has nothing to show because she does not follow her. An empty
// list under a name that exists is a bug wearing a rule.
export const COMPOSE_FOLLOW_HINT = "You can only message people you follow. Follow them in the room and they show up here.";

// May this viewer OPEN a conversation with these people? Returns the violations in the order they
// are asked, so the caller answers the first one and never has to know the precedence.
// CRYSTAL — **`enforceBlocks` DEFAULTS TO TRUE, SO EVERY EXISTING CALLER IS BYTE-IDENTICAL.**
//
// The `unreachable` clause below is the SECOND site that closed Elite 2026 to Crystal: the send door
// passes every other participant as a target, so in a 270-member group one block raised `unreachable`
// and the room answered *"No such conversation."* — the same defect as the door above it, one clause
// down, which is why fixing only the first would have looked like a fix and changed nothing.
//
// **IT IS A PARAMETER RATHER THAN A REWRITE BECAUSE THIS LAW IS RIGHT EVERYWHERE ELSE.** On a DM, on
// a draft, and on ADDING somebody to a group, `unreachable` is load-bearing and absolute (ruling 4).
// The one case where a block must NOT refuse is sending into an EXISTING group, where the room-level
// decision has already been made by `conversationClosedToViewer` per LR1 ruling 2. Defaulting to TRUE
// means the failure mode of anybody forgetting this argument is today's behaviour and never a hole
// (FREE-1's `action` default, same shape).
export function messageGateViolations({ viewer, targetRefs = [], threadExists = false, enforceBlocks = true } = {}) {
  const out = [];
  const me = String(viewer?.ref ?? "");
  const refs = (Array.isArray(targetRefs) ? targetRefs : [targetRefs]).map((r) => String(r ?? "")).filter(Boolean);
  if (!me) out.push("no-viewer");
  if (!refs.length) out.push("no-target");
  if (refs.some((r) => r === me)) out.push("self");
  // REACHABILITY FIRST, AND THROUGH LR1'S OWN LAW rather than a second copy of it: blocking is
  // absolute and symmetric and the strike ladder's silence lands here too.
  if (enforceBlocks && me && refs.some((r) => !canMessage(viewer, r))) out.push("unreachable");
  // **THE SILENCE STILL LANDS EVEN WHEN BLOCKS ARE NOT ENFORCED.** `canMessage` folds two different
  // facts together (ruling 4's block, and the strike ladder's silence), and only the FIRST of them is
  // the one a group must not enforce room-wide. Dropping both would hand a silenced member a group
  // she can shout in — the strike ladder undone by a fix for blocking (R18: two facts, one spelling).
  if (!enforceBlocks && me && viewer?.silenced) out.push("unreachable");
  // AND ONLY THEN THE FOLLOW. A group is checked on every person in it, because a group is where
  // this would otherwise be routed around — the same argument LR1 ruling 2 makes about blocking.
  //
  // **ITEM 3 — HER SEAT IS EXEMPT FROM THE FOLLOW AND FROM NOTHING ELSE, AND THE POSITION OF THIS
  // CLAUSE IS THE WHOLE OF THAT SENTENCE.** The follow gate exists so a stranger cannot arrive in a
  // member's inbox uninvited; the house answering a support message is not a stranger arriving, and
  // requiring her to follow first means the only account that can help somebody has to publicly
  // follow her before it can say a word. **`unreachable` is asked ABOVE and is untouched by this**, so
  // a silenced owner still does not write and `self` is still refused: the exemption is one clause
  // wide, and moving it up would hand her seat a hole rather than a reach.
  //
  // It reads `owner` and never `crowned` (see `canBlock`'s note): the two rosters are different sets
  // on purpose, and a crowned member follows before she messages like anybody else.
  //
  // **T141-1 EXTENDS THE EXEMPTION TO THE HOUSE SEAT AND TO NOTHING ELSE.** The desk answering a
  // support message is the same act ITEM 3 exempted her own seat for; requiring the desk to follow
  // a member before it may answer her means the only account that can help somebody has to
  // publicly follow her first. `unreachable` is still asked ABOVE and is still untouched.
  if (!houseReach(viewer) && !threadExists && refs.some((r) => !viewer?.following?.has?.(r))) out.push("not-following");
  return out;
}

// What she is TOLD. Anything that is not the follow rule collapses to the room's one sentence, which
// is the whole point of asking reachability first: a blocked member and an absent one and a member
// she simply has not followed must not be distinguishable by their refusals.
export function messageGateRefusal(codes = []) {
  const list = Array.isArray(codes) ? codes : [codes];
  if (!list.length) return null;
  if (list.some((c) => c !== "not-following")) return MEMBER_NOT_HERE;
  return MESSAGE_FOLLOW_REFUSAL;
}

// ---------------------------------------------------------------------------
// ITEM 10 — READ RECEIPTS. **A SENDER SEES WHEN HER MESSAGE WAS READ, AND NOBODY EVER SEES WHEN A
// MEMBER WAS LAST HERE.** Those are two halves of one order and the second is the harder one.
//
// **NO NEW COLUMN, AND THAT IS THE MEASUREMENT RATHER THAN A PREFERENCE.**
// `lr_thread_members.last_read_at` has been written on every thread open since LR1 and is already
// what ITEM 5's unread badge and ITEM 7's strip dot both read. A second stamp would be a second
// answer to "has she read this", which is R28/R29's disease with a timestamp in it.
//
// **THE RECEIPT IS DERIVED ON THE SERVER AND ONLY THE MARK CROSSES THE WIRE.** Sending each
// participant's raw `last_read_at` to the client would be a per-thread LAST-ONLINE — her order
// forbids that outright, and "the client only renders the ones it needs" is not a boundary. So a
// message carries a list of who has read it and nothing else, and only for messages the viewer sent.
//
// **HER READS NEVER PRODUCE RECEIPTS, AND THE EXCLUSION IS ON THE READING SIDE ON PURPOSE.** Not
// stamping her `last_read_at` would keep her own inbox permanently unread — her seat opens threads
// all day. So the stamp still moves and no receipt is ever DERIVED from an owner's, which makes her
// read indistinguishable from no read at all. That is exactly what her rule asks for.
//
// **AND A HELD MESSAGE NEVER CARRIES ONE (LR1 ruling 1), WHICH IS THE INVERSE LEAK.** A held message
// is invisible to everyone but its author, so nobody reads it — but the other participants' read
// stamps still advance past it in TIME. Without this a shadow-hidden message would wear "Seen" and
// tell its author it landed, which is the hold failing at the one thing it exists to do.
// ---------------------------------------------------------------------------
export const SEEN_LABEL = "Seen";

// WHO HAS READ THIS, and it answers for the SENDER only — there is no reason to tell a member
// whether she has read a message she is looking at, and every extra ref on the wire is a fact about
// somebody else's attention.
// **`Date.parse` IS WRONG HERE AND IT IS A QUIET WRONG.** Postgres hands `timestamptz` back as a JS
// DATE, and `Date.parse(aDate)` coerces it through `toString()` — which has no milliseconds in it.
// Two events 40ms apart become the same instant, and this comparison is `>=`, so the loser is the
// message: it reads as seen by somebody who had not opened the thread yet. `new Date(v)` takes both
// a Date and an ISO string and keeps the milliseconds; a missing stamp stays NaN rather than
// becoming the epoch, which `new Date(null)` would quietly make it.
const instant = (v) => (v === null || v === undefined || v === "" ? NaN : new Date(v).getTime());

export function messageSeenBy({ message, participants = [], readStamps = {}, viewerRef = "" } = {}) {
  if (!message) return [];
  const authorRef = String(message.authorRef ?? message.author?.ref ?? "");
  if (!authorRef || authorRef !== String(viewerRef ?? "")) return [];
  if (message.visibility === VISIBILITY.HIDDEN) return [];
  const at = instant(message.createdAt);
  if (!Number.isFinite(at)) return [];
  return participants
    .filter((p) => String(p.ref) !== authorRef)
    // **THE OWNER IS DROPPED HERE AND NOWHERE ELSE.** One filter, on the only path that builds a
    // receipt, so a new surface cannot forget it.
    .filter((p) => !p.owner)
    .filter((p) => {
      const read = instant(readStamps?.[String(p.ref)]);
      return Number.isFinite(read) && read >= at;
    })
    .map((p) => String(p.ref));
}

// What the mark SAYS. A group needs a number or it claims everybody when one person looked; a
// one-to-one does not, and "Seen by 1" there is machinery talking.
export function receiptLabel(count, { group = false } = {}) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  return group ? `${SEEN_LABEL} by ${n}` : SEEN_LABEL;
}

// **WHICH MESSAGE WEARS IT — ONE, AND IT IS THE LAST OF HERS THAT HAS BEEN READ.** Her word is "a
// subtle seen mark"; a mark under every message of hers is a column of them. Written as a law rather
// than as a loop at the call site because "the last one" is the kind of thing that survives a
// refactor backwards into "the first one" with nothing to notice.
export function receiptMessageId(messages = []) {
  for (let i = (messages?.length || 0) - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.mine && (m.seen?.length || 0) > 0) return String(m.id);
  }
  return null;
}

// >>> **HER RULING, 2026-09-13: THE BOX WAS ASKING THE WRONG QUESTION.** <<<
//
// It said *"What are you working on?"* and her members answered it literally, one line each, naming
// a project and giving the room nothing to reply to. **A prompt is an instruction, and that one
// instructed them to file a status update.** Her words: *"people are actually putting what they're
// working on instead of actually giving context to the community, so we want to put something in
// there that makes people want to comment."*
//
// So it asks for the thing she actually wants: something shared WITH somebody, rather than a fact
// about the poster. The room's own nature is said once under the banner above rather than crammed
// in here, because a placeholder long enough to explain a room is a placeholder nobody reads.
export const COMPOSER_PLACEHOLDER = "Share something with the room";

// >>> **THE BANNER, HER WORDS EXACTLY, AND IT SITS BETWEEN THE LIVE CIRCLES AND THE SECTIONS.** <<<
// Three words because three words get read. The line under it is the part that tells a newcomer what
// this page IS, which is the half her members were missing.
export const ROOM_BANNER = "Greet. Meet. Grow.";
export const ROOM_BANNER_SUB = "This is the community page. Comments, tips, and each other.";

// LR18b-4 — **HER CLAUSE 4's SECOND HALF: THE CREATOR ADDS AND REMOVES.** Her words are exact —
// *"The CREATOR can add (follow-gated) and remove members"* — and the first half of that is a
// NARROWING of a live route: `handleAddToThread` has let any participant add since LR1-8 shipped.
// It is her house and her ruling, and the consequence is named rather than discovered.
//
// **IT IS ONE PREDICATE READ BY BOTH SIDES**, because the surface that draws the controls and the
// route that refuses them must not come to two views of who is in charge of a group (R13/R28/R29):
// a picker that offers what the gate refuses costs her the whole action before she is told no (R15),
// and a control the server would honour but the client hides is a capability she cannot reach.
//
// **A GROUP WITH NO CREATOR ON FILE IS UNMANAGED, AND THAT IS DECLARED RATHER THAN INVENTED AROUND.**
// `lr_threads.created_by` is `ON DELETE SET NULL`, so a group whose creator's account is gone has
// nobody to be its creator — and inventing a succession rule (the oldest member? the first to speak?)
// is a product decision and hers. Everything else in such a group keeps working: sending, leaving,
// blocking, reading. Only add and remove close.
// **IT IS TOTAL RATHER THAN DESTRUCTURED**, which its own fixture caught on the first run: a thread
// that has not arrived yet is `null` at every surface in this room (R53's third state), and a
// destructured parameter with a default THROWS on one rather than answering. A law that dies on the
// state its own surfaces spend most of their time in is a law nothing can ask (R19).
export function canManageGroup(thread) {
  return Boolean(thread && thread.group) && thread.iCreated === true;
}
// The refusal, in the room's register: it says who may, rather than what she did wrong.
export const GROUP_MANAGE_REFUSAL = "Only whoever started this group can add or remove people.";
// A remove is somebody else losing a room they are standing in, so the confirm names the person and
// says what survives — LR13/R35's law, and the same shape as the leave's own sentence one control up.
export function removeFromGroupSentence(name = "") {
  const who = String(name ?? "").trim();
  return `Take ${who ? `@${who}` : "this member"} out of the group? They stop receiving it and the conversation carries on without them.`;
}
export const REMOVE_FROM_GROUP_LABEL = "Take out of the group";
export const REMOVE_FROM_GROUP_CONFIRM = "Take them out";
export const REMOVE_FROM_GROUP_KEEP = "Leave them in";
// R18/R39 — the remove route answers `{ ok: true, removed }`, exactly as the leave does, so a member
// who was not in that group any more is a 200 that changed nothing. This is that sentence.
export const REMOVE_NOT_IN_GROUP = "They are not in that conversation.";
// The creator's own way out is the LEAVE, behind its confirm — this door refuses her, so she is never
// standing outside her own group with no sentence about how she got there.
export const REMOVE_SELF_REFUSAL = "Use Leave this group to take yourself out.";
export const REMOVED_FROM_GROUP = "They are out of the group.";

// ---------------------------------------------------------------------------
// GROUP IMAGES (her order, 2026-08-06) — **THE SAME LAW AS A MEMBER'S PHOTO, ONE ENTITY OVER.**
//
// Her words: the creator can name their group and set an IMAGE for it, at creation and after; only
// the creator may change either; no image is the current default look; uploads ride the same
// handling as a picture already does; and the image is visible only to that group's members.
//
// Every line below is `avatarPhotoUrl` / `avatarRefusal` with `thread` where `user` was — deliberately,
// because "invent nothing new" is a property you get by DERIVING rather than by promising (R31).
// ---------------------------------------------------------------------------
export const THREAD_IMAGE_ROUTE = "/api/living-room/group-image";
export function threadImageUrl(threadId, version) {
  const id = encodeURIComponent(String(threadId ?? ""));
  const v = encodeURIComponent(String(version ?? ""));
  return `${THREAD_IMAGE_ROUTE}/${id}?v=${v}`;
}

// Square by construction, cropped before upload by `prepareAvatarForUpload` — the SAME function a
// profile photo uses, so there is one crop in this house rather than two that drift.
export const THREAD_IMAGE_EDGE = AVATAR_PHOTO_EDGE;
export const THREAD_IMAGE_TYPES = AVATAR_PHOTO_TYPES;

// **HER RULE: THE SAME LIMITS AS POST PICTURES**, so the number is READ from the post limit rather
// than typed again (R28/R29) — and it is not the profile-photo cap, which is a different promise
// about a different picture. At 2MB it refused a routine iPhone photo before anything else could go
// wrong: the file her camera writes is commonly just over it, and being told a photo she took this
// morning is too big for a group is the refusal-of-correct-input class this round is about (R19).
export const THREAD_IMAGE_MAX_BYTES = LIMITS.imageBytes;

/**
 * Why this upload is refused, or "" — DERIVED from the photo refusal so *what a picture may be* has
 * one answer, with only the noun changed. A second copy of that law is a second answer key
 * (R28/R29).
 *
 * **THE CAP TRAVELS AS AN ARGUMENT RATHER THAN AS A SECOND CLAUSE HERE.** A group picture rides the
 * post-picture limit by her ruling and a profile photo does not; writing that as its own test above
 * the delegation left the profile cap live underneath it, so a 2.04MB iPhone photo passed the
 * group's 8MB question and was refused by the profile's 2MB one — with a sentence that named a
 * limit this door does not enforce, which sends her hunting for a smaller file that would not have
 * helped. One question, one number, supplied by whoever is asking.
 */
export function threadImageRefusal(input = {}) {
  const said = avatarRefusal({ ...input, maxBytes: THREAD_IMAGE_MAX_BYTES });
  if (!said) return "";
  return said
    .replace("Choose a picture first.", "Choose a picture for the group first.")
    .replace("A profile photo goes up to", "A group picture goes up to")
    .replace("A profile photo is square.", "A group picture is square.");
}

/**
 * **WHO MAY CHANGE A GROUP'S NAME OR ITS PICTURE — HER RULE, IN ONE PLACE.**
 *
 * Only the CREATOR. It is one function because the name and the image are the same permission, and
 * two spellings of it would be two places for a non-creator to get through (R28/R29).
 *
 * **THREE OUTCOMES RATHER THAN TWO (R18):** *not a group*, *not the creator* and *yes* are different
 * facts, and a surface that collapses them tells a member in a DM that she is not the owner of
 * something that has no owner.
 *
 * A thread with NO creator on file is unmanaged rather than everybody's — LR18b(d)'s standing ruling,
 * inherited here rather than re-decided.
 */
export function mayEditGroup({ isGroup = false, createdBy = null, viewerRef = null } = {}) {
  if (!isGroup) return { allowed: false, reason: "not-a-group" };
  if (createdBy === null || createdBy === undefined || String(createdBy) === "") {
    return { allowed: false, reason: "no-creator" };
  }
  if (String(createdBy) !== String(viewerRef ?? "")) return { allowed: false, reason: "not-the-creator" };
  return { allowed: true, reason: "creator" };
}

/** The one sentence a non-creator is told. Never names who the creator IS (LR1 ruling 3). */
export const GROUP_EDIT_REFUSED = "Only whoever started this group can change its name or its picture.";

/** SHARE TO GROUPS: what marks a group in the recipient list. One word, declared once. */
export const GROUP_RECIPIENT_LABEL = "Group";

/** DEFECT 2 — the creator's edit sheet. One label each, declared once (14-B5). */
export const GROUP_EDIT_LABEL = "Group name and picture";
export const GROUP_NAME_LABEL = "Group name";
export const GROUP_PICTURE_LABEL = "Group picture";
export const GROUP_PICTURE_PICK = "Choose a picture";

/**
 * NEW RULING (owner, 2026-08-06) — **A CREATOR MAY DELETE HER OWN GROUP, AND DESTRUCTION NEVER
 * DRESSES AS EXIT.** Her words: the confirm says plainly that the group and its thread end FOR
 * EVERYONE. R35's law is that the sentence is DERIVED from what is destroyed, never typed at the
 * call site, so this is the sentence and there is one of it.
 */
export const GROUP_DELETE_LABEL = "Delete group";
export function groupDeleteConfirm(title = "") {
  const named = String(title || "").trim();
  return `Delete ${named ? `"${named}"` : "this group"}? The group and every message in it end for everyone in it, not just for you. This cannot be undone.`;
}

// ---------------------------------------------------------------------------
// THE EVERYONE TAG (her order, 2026-08-06) — **THE HOUSE-WIDE CALL, AND ONLY SHE MAY MAKE IT.**
//
// When an OWNER writes @everyone in a Living Room post, every member on the site is told about that
// post. Anybody else typing it gets plain text: no tag, no notifications, and **no error** — her
// rule, and the right one, because an error would teach a member that a capability exists which is
// not hers to have.
// ---------------------------------------------------------------------------
export const EVERYONE_TAG = "everyone";

/**
 * Does this body carry the tag? Case-insensitive (`@everyone`, `@Everyone`, `@EVERYONE`) and bounded
 * on BOTH sides, so `@everyoneelse` is not the tag and `hey @everyone!` is.
 *
 * **THIS ANSWERS ONLY THE TEXT QUESTION.** Whether it ACTIVATES is `everyoneFanoutVerdict` below,
 * which is the one that knows about the owner — a single function answering both would be a place
 * for the permission to get lost (R18: two facts).
 */
export function hasEveryoneTag(text = "") {
  return new RegExp(`(^|[^\\w@])@${EVERYONE_TAG}\\b`, "i").test(String(text || ""));
}

/**
 * **WHETHER THE HOUSE-WIDE CALL FIRES, AND WHY NOT WHEN IT DOES NOT.**
 *
 * Four outcomes rather than two (R18), because a session reading a log needs to tell *she did not
 * type it* from *he is not the owner* from *this post already rang*:
 *
 *   - `no-tag`        the body does not carry it
 *   - `not-owner`     somebody else typed it — PLAIN TEXT, silently, her rule
 *   - `already-rung`  this post has already told the house, so an edit does not ring it again
 *   - `fire`          send it
 *
 * **THE `already-rung` CLAUSE IS WHAT MAKES THE EDIT CASE SAFE**, and it is her explicit requirement:
 * she is adding the tag to a post that already exists, and editing that post again later must not
 * ring the house a second time. The memory is the notifications themselves — a post that has rung
 * has rows — so there is no flag to keep in step (R28/R29).
 */
export function everyoneFanoutVerdict({ text = "", isOwner = false, alreadyRung = false } = {}) {
  if (!hasEveryoneTag(text)) return { fire: false, reason: "no-tag" };
  if (!isOwner) return { fire: false, reason: "not-owner" };
  if (alreadyRung) return { fire: false, reason: "already-rung" };
  return { fire: true, reason: "fire" };
}

/**
 * **THE THREE-LINE LAW, ON THE HOUSE-WIDE CALL.** P5: app name / what this is / the content. The
 * middle line is the plain event and carries NO legal name — the room-name is the only name any
 * surface renders (LR1 ruling 3), and this one does not name her at all: it says the house.
 */
export const EVERYONE_NOTICE_LINE = "Lala posted to the house";
// HER NEW RULING: the tag works in a COMMENT too, and the middle line says which it was — a
// member tapping a bell should know whether she is being sent to a post or into a conversation
// under one. Same law, same absence of any legal name.
export const EVERYONE_COMMENT_LINE = "Lala commented in the house";
export function everyoneNotice(excerpt = "", { kind = "post" } = {}) {
  const said = String(excerpt || "").replace(new RegExp(`@${EVERYONE_TAG}`, "ig"), "").replace(/\s+/g, " ").trim();
  return { line: kind === "comment" ? EVERYONE_COMMENT_LINE : EVERYONE_NOTICE_LINE, content: said.slice(0, 140) };
}

/** How many members one send reaches. Batched, because this fires to thousands after 8/8. */
export const EVERYONE_BATCH = 500;


/**
 * **THE PICKER ENTRY — AND THIS IS WHAT WAS MISSING.** She typed `@`, the picker offered members,
 * `Everyone` was not among them, so the words sat as plain text with nothing popping and the whole
 * feature read as dead. The tag WORKED on save the entire time; there was simply no way to reach it
 * the way this room teaches you to reach a name.
 */
export const EVERYONE_PICKER_LABEL = "Everyone";
export const EVERYONE_PICKER_HINT = "Notifies the whole house";

/**
 * Does this member's picker offer the house-wide call, and does what she has typed so far match it?
 *
 * **OWNER ONLY** — her rule 1, and it is asked HERE so the client never draws a row a member could
 * tap and be refused for (R15: the surface refuses exactly what the route would). A regular member's
 * picker has no Everyone in it at all, so there is nothing to discover.
 */
export function everyonePickerRow({ isOwner = false, query = "" } = {}) {
  if (!isOwner) return null;
  const q = String(query || "").toLowerCase();
  if (q && !EVERYONE_TAG.startsWith(q)) return null;
  return { ref: "everyone", name: EVERYONE_PICKER_LABEL, hint: EVERYONE_PICKER_HINT, everyone: true };
}

/**
 * **WHAT SHE IS TOLD AFTER SHE RINGS THE HOUSE.** Her ask: she rings it and sees nothing, because
 * the author is excluded from her own fan-out, so the only confirmation was the ledger.
 *
 * It is a COUNT rather than a promise — *"rang 118 members"* — because the number is the thing she
 * cannot otherwise see, and a bare "sent" would be the kind of sentence this house refuses (DT-1b:
 * SENT is not a success state). Singular and plural, because a house of one reads as broken.
 */
export function rangHouseNotice(n) {
  const count = Math.max(0, Number(n) || 0);
  if (!count) return "";
  return `Rang ${count} ${count === 1 ? "member" : "members"}.`;
}
